(() => {
  const STORAGE_KEY = 'jd-greta-kitchen-builder-v1';
  const SHARE_BACKUP_KEY = 'jd-greta-kitchen-builder-before-share-v1';
  const PROJECTS_KEY = 'jd-greta-kitchen-builder-projects-v1';
  const SHARE_PARAM = 'kitchen';
  const HISTORY_LIMIT = 60;
  const PROJECT_LIMIT = 20;

  const FAMILY_RULES = [
    { re: /^ALBC(\d{3,4})/, category: 'corner', label: 'Adjustable L-corner', depth: n => n, height: () => 870, cornerArmDepth: () => 578 },
    { re: /^ALDR(\d{3,4})/, category: 'drawer', label: 'Adjustable drawer', depth: () => 578, height: () => 870 },
    { re: /^ALS(\d{3,4})/, category: 'sink', label: 'Adjustable sink', depth: () => 578, height: () => 870 },
    { re: /^ALO(\d{3,4})/, category: 'oven', label: 'Adjustable oven', depth: () => 578, height: () => 870 },
    { re: /^ALT(\d{3,4})/, category: 'tall', label: 'Adjustable tall', depth: () => 578, height: () => 1950 },
    { re: /^ALB(\d{3,4})/, category: 'base', label: 'Adjustable base', depth: () => 578, height: () => 870 },
    { re: /^BC(\d{3,4})/, category: 'corner', label: 'L-shaped base corner', depth: n => n, height: () => 820, cornerArmDepth: () => 463 },
    { re: /^DR(\d{3,4})/, category: 'drawer', label: 'Drawer unit', depth: () => 463, height: () => 820 },
    { re: /^WEX(\d{3,4})/, category: 'wall', label: 'Bridging wall unit', depth: () => 313, height: () => 400 },
    { re: /^EX(\d{3,4})/, category: 'wall', label: 'Bridging wall unit', depth: () => 313, height: () => 400 },
    { re: /^WC(\d{3,4})/, category: 'wall', label: 'Wall L-corner', depth: n => Math.min(n, 650), height: () => 720, cornerArmDepth: () => 313, cornerShape: true },
    { re: /^B(\d{3,4})/, category: 'base', label: 'Base unit', depth: () => 463, height: () => 820 },
    { re: /^TS(\d{3,4})/, category: 'sink', label: 'Utility sink unit', depth: () => 586, height: () => 870 },
    { re: /^S(\d{3,4})/, category: 'sink', label: 'Sink unit', depth: () => 463, height: () => 820 },
    { re: /^O(\d{3,4})/, category: 'oven', label: 'Oven unit', depth: () => 463, height: () => 820 },
    { re: /^T(\d{3,4})/, category: 'tall', label: 'Tall unit', depth: () => 578, height: () => 1950 },
    { re: /^W(\d{3,4})/, category: 'wall', label: 'Wall unit', depth: () => 313, height: () => 720 }
  ];

  class KitchenBuilder {
    constructor(root) {
      this.root = root;
      const isShopifyPreview = /(^|\\.)shopifypreview\\.com$/i.test(window.location.hostname) ||
        Boolean(window.Shopify && window.Shopify.designMode);
      this.root.classList.toggle('is-shopify-preview', isShopifyPreview);
      this.catalogRaw = this.readCatalog();
      const catalogCandidates = this.catalogRaw
        .map(item => this.enrichProduct(item))
        .filter(Boolean);

      // Shopify currently contains a few duplicate SKUs from older multi-variant
      // listings as well as the active single-product listings. The planner must
      // have one canonical product per SKU or saved designs can bind to an old
      // variant ID and later look falsely out of stock.
      const catalogBySku = new Map();
      catalogCandidates.forEach(item => {
        const skuKey = String(item.sku || '').trim().toUpperCase();
        if (!skuKey) return;

        const existing = catalogBySku.get(skuKey);
        if (!existing) {
          catalogBySku.set(skuKey, item);
          return;
        }

        const score = product => {
          const variantTitle = String(product.variantTitle || '').trim().toLowerCase();
          const singleVariantListing = !variantTitle || variantTitle === 'default title';
          const inventory = Number(product.inventoryQuantity);
          return (singleVariantListing ? 100 : 0) +
            (product.available ? 10 : 0) +
            (Number.isFinite(inventory) && inventory > 0 ? 1 : 0);
        };

        if (score(item) > score(existing)) catalogBySku.set(skuKey, item);
      });

      this.catalog = [...catalogBySku.values()];
      this.catalogProductIdAliases = new Map();
      catalogCandidates.forEach(item => {
        const skuKey = String(item.sku || '').trim().toUpperCase();
        const canonical = skuKey ? catalogBySku.get(skuKey) : null;
        if (!canonical) return;
        const oldId = String(item.id || '');
        const canonicalId = String(canonical.id || '');
        if (oldId && canonicalId && oldId !== canonicalId) {
          this.catalogProductIdAliases.set(oldId, canonicalId);
        }
      });
      this.productById = new Map(this.catalog.map(item => [String(item.id), item]));
      this.customProducts = new Map();
      this.roomWidth = Number(root.dataset.roomWidth) || 4000;
      this.roomDepth = Number(root.dataset.roomDepth) || 3000;
      this.roomShape = { type: 'rectangle', corner: 'top-right', cutX: 500, cutY: 500 };
      this.roomShapeDraft = null;
      this.grid = Number(root.dataset.grid) || 50;
      this.units = [];
      this.extras = {};
      this.endPanelAssignments = [];
      this.plinthApplications = [];
      this.activeEndPanelProductId = null;
      this.selectedId = null;
      this.justAddedIds = new Set();
      this.lastAddedId = null;
      this.scale = 0.12;
      this.precisionStep = 50;
      this.drag3dOrigin = null;
      this.drag = null;
      this.dragGestureLock = null;
      this.nudgeHoldTimer = null;
      this.nudgeRepeatTimer = null;
      this.lastNudgePointerAt = 0;
      this.searchDrag = null;
      this.catalogDrag = null;
      this.suppressCatalogClick = false;
      this.activeLayer = 'all';
      this.mobileSearchLayer = 'all';
      this.activeColour = 'all';
      this.activeWorktop = 'all';
      this.activeHeight = 'all';
      this.activeWidth = 'all';
      this.viewMode = '2d';
      this.wallColour = 'white';
      this.heightWarningSignature = '';
      this.historyPast = [];
      this.historyFuture = [];
      this.historyReady = false;
      this.historyApplying = false;
      this.viewer3d = null;
      this.viewer3dInit = null;
      this.designId = this.createDesignId();
      this.projectName = 'My kitchen';

      this.ensureWallLinkControls();
      this.ensureCustomAppliancePresets();
      this.ensureWidthFilterControls();
      this.cache();
      this.updateResponsiveMode();
      this.bind();
      this.renderColourFilters();
      this.renderWorktopFilters();
      this.renderHeightFilters();
      this.renderWidthFilters();
      const openedSharedDesign = this.restoreSharedDesign();
      if (!openedSharedDesign) this.restore();
      this.resetHistory();
      this.renderCatalog();
      this.renderAll();
      this.observeResize();
      if (openedSharedDesign) {
        this.updateShareNotice(true);
        this.setStatus('Shared kitchen project opened. Changes will save as your own copy on this device.');
      }
    }

    ensureWallLinkControls() {
      const addControl = (containerSelector, attribute, rotateSelector) => {
        const container = this.root.querySelector(containerSelector);
        if (!container || container.querySelector(`[${attribute}]`)) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute(attribute, '');
        button.setAttribute('aria-pressed', 'true');
        button.textContent = 'Wall: ON';

        const rotate = container.querySelector(rotateSelector);
        if (rotate && rotate.nextSibling) container.insertBefore(button, rotate.nextSibling);
        else container.appendChild(button);
      };

      addControl('.jkb__precision-buttons', 'data-wall-link', '[data-snap-wall]');
      addControl('.jkb__3d-selected-actions', 'data-3d-wall-link', '[data-3d-rotate]');

      const addServiceGapControl = (containerSelector, attribute, referenceAttribute) => {
        const container = this.root.querySelector(containerSelector);
        if (!container || container.querySelector(`[${attribute}]`)) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute(attribute, '');
        button.hidden = true;
        button.textContent = 'Gap 115';

        const reference = container.querySelector(`[${referenceAttribute}]`);
        if (reference && reference.nextSibling) container.insertBefore(button, reference.nextSibling);
        else container.appendChild(button);
      };

      addServiceGapControl('.jkb__precision-buttons', 'data-service-gap', 'data-wall-link');
      addServiceGapControl('.jkb__3d-selected-actions', 'data-3d-service-gap', 'data-3d-wall-link');

      const selectionControls = this.root.querySelector('.jkb__selection-controls');
      if (selectionControls && !selectionControls.querySelector('[data-service-gap-help]')) {
        const helper = document.createElement('div');
        helper.className = 'jkb__service-gap-help';
        helper.setAttribute('data-service-gap-help', '');
        helper.hidden = true;
        helper.innerHTML = '<b>Why 115 mm?</b><span>This cabinet carcass is 463 mm deep. Leave 115 mm behind it so the cabinet front sits 578 mm from the wall. A standard 600 mm worktop will then project about 22 mm past the front. AL cabinets are already 578 mm deep, so this control is never shown for AL units.</span>';
        selectionControls.appendChild(helper);
      }

      const actions3d = this.root.querySelector('.jkb__3d-selected-actions');
      if (actions3d && !actions3d.querySelector('[data-3d-move-step-toggle]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'jkb__3d-move-step-inline';
        button.setAttribute('data-3d-move-step-toggle', '');
        button.setAttribute('aria-label', 'Change movement step');
        button.title = 'Switch between 50 mm and 10 mm';
        button.textContent = '50 mm';
        actions3d.insertBefore(button, actions3d.firstChild);
      }
    }

    ensureCustomAppliancePresets() {
      const typeSelect = this.root.querySelector('[data-custom-type]');
      if (typeSelect && !typeSelect.querySelector('option[value="dishwasher"]')) {
        const group = document.createElement('optgroup');
        group.label = 'Appliances';
        [
          ['dishwasher', 'Dishwasher'],
          ['oven-appliance', 'Oven / cooker'],
          ['hood', 'Cooker hood']
        ].forEach(([value, label]) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          group.appendChild(option);
        });
        typeSelect.appendChild(group);
      }

      const form = this.root.querySelector('.jkb__custom-form');
      if (!form || form.querySelector('[data-custom-presets]')) return;

      const presets = document.createElement('div');
      presets.className = 'jkb__custom-presets';
      presets.setAttribute('data-custom-presets', '');
      presets.innerHTML = `
        <span>Quick objects</span>
        <div>
          <button type="button" data-custom-preset="fridge">Fridge</button>
          <button type="button" data-custom-preset="dishwasher">Dishwasher</button>
          <button type="button" data-custom-preset="oven-appliance">Oven</button>
          <button type="button" data-custom-preset="hood">Cooker hood</button>
          <button type="button" data-custom-preset="obstacle">Other</button>
        </div>
      `;
      form.insertBefore(presets, form.firstChild);
    }

    ensureWidthFilterControls() {
      const desktopHeights = this.root.querySelector('[data-heights]');
      const desktopBlock = desktopHeights?.closest('.jkb__filter-block');
      if (desktopBlock && !this.root.querySelector('[data-widths]')) {
        const block = document.createElement('div');
        block.className = 'jkb__filter-block jkb__width-filter-block';
        block.innerHTML = '<span class="jkb__filter-label">Width</span><div class="jkb__width-filter" data-widths></div>';
        desktopBlock.insertAdjacentElement('afterend', block);
      }

      const mobileHeights = this.root.querySelector('[data-mobile-heights]');
      const mobileRow = mobileHeights?.closest('.jkb__mobile-filter-row');
      if (mobileRow && !this.root.querySelector('[data-mobile-widths]')) {
        const row = document.createElement('div');
        row.className = 'jkb__mobile-filter-row';
        row.innerHTML = '<span>Width</span><div class="jkb__mobile-filter-chips" data-mobile-widths></div>';
        mobileRow.insertAdjacentElement('afterend', row);
      }
    }

    cache() {
      const q = selector => this.root.querySelector(selector);
      this.el = {
        widthInput: q('[data-room-width-input]'),
        depthInput: q('[data-room-depth-input]'),
        applyRoom: q('[data-apply-room]'),
        roomShapeOpen: q('[data-room-shape-open]'),
        roomShapeModal: q('[data-room-shape-modal]'),
        roomShapeClose: [...this.root.querySelectorAll('[data-room-shape-close]')],
        roomShapeTypes: q('[data-room-shape-types]'),
        roomShapeCorners: q('[data-room-shape-corners]'),
        roomShapeChamferField: q('[data-room-shape-chamfer-field]'),
        roomShapeNotchFields: q('[data-room-shape-notch-fields]'),
        roomShapeCut: q('[data-room-shape-cut]'),
        roomShapeCutX: q('[data-room-shape-cut-x]'),
        roomShapeCutY: q('[data-room-shape-cut-y]'),
        roomShapePreview: q('[data-room-shape-preview]'),
        roomShapeSummary: q('[data-room-shape-summary]'),
        roomShapeApply: q('[data-room-shape-apply]'),
        roomShapeReset: q('[data-room-shape-reset]'),
        search: q('[data-search]'),
        quickSearch: q('[data-quick-search]'),
        quickResults: q('[data-quick-results]'),
        quickSearchWrap: q('[data-quick-search-wrap]'),
        searchDragHandle: q('[data-search-drag-handle]'),
        mobileSearchLaunch: q('[data-mobile-search-launch]'),
        mobileSearch: q('[data-mobile-search]'),
        mobileSearchClose: q('[data-mobile-search-close]'),
        mobileSearchBack: q('[data-mobile-search-back]'),
        mobileSearchInput: q('[data-mobile-search-input]'),
        mobileSearchResults: q('[data-mobile-search-results]'),
        mobileSearchCount: q('[data-mobile-search-count]'),
        mobileSearchLayers: q('[data-mobile-search-layers]'),
        mobileColours: q('[data-mobile-colours]'),
        mobileWorktops: q('[data-mobile-worktops]'),
        mobileHeights: q('[data-mobile-heights]'),
        mobileWidths: q('[data-mobile-widths]'),
        mobileAddedBar: q('[data-mobile-added-bar]'),
        mobileAddedText: q('[data-mobile-added-text]'),
        mobileViewPlan: q('[data-mobile-view-plan]'),
        summaryCount: q('[data-summary-count]'),
        summaryToggle: q('[data-summary-toggle]'),
        panelUnplacedTray: q('[data-panel-unplaced-tray]'),
        category: q('[data-category]'),
        colours: q('[data-colours]'),
        worktops: q('[data-worktops]'),
        heights: q('[data-heights]'),
        widths: q('[data-widths]'),
        products: q('[data-products]'),
        productCount: q('[data-product-count]'),
        stage: q('[data-stage]'),
        stageScroll: q('[data-stage-scroll]'),
        room: q('[data-room]'),
        empty: q('[data-empty]'),
        widthLabel: q('[data-width-label]'),
        depthLabel: q('[data-depth-label]'),
        selection: q('[data-selection]'),
        selectionTitle: q('[data-selection-title]'),
        selectionSize: q('[data-selection-size]'),
        endPanelPlacement: q('[data-end-panel-placement]'),
        plinthPlacement: q('[data-plinth-placement]'),
        moveStep: q('[data-move-step-toggle]'),
        moveStep3d: q('[data-3d-move-step-toggle]'),
        rotate: q('[data-rotate]'),
        snapWall: q('[data-snap-wall]'),
        wallLink: q('[data-wall-link]'),
        serviceGap: q('[data-service-gap]'),
        duplicate: q('[data-duplicate]'),
        lock: q('[data-lock]'),
        delete: q('[data-delete]'),
        undo: q('[data-undo]'),
        redo: q('[data-redo]'),
        shareDesign: q('[data-share-design]'),
        shareNotice: q('[data-share-notice]'),
        restoreLocal: q('[data-restore-local]'),
        projectTitle: q('[data-project-title]'),
        projectsOpen: q('[data-projects-open]'),
        projectsModal: q('[data-projects-modal]'),
        projectsClose: [...this.root.querySelectorAll('[data-projects-close]')],
        projectsList: q('[data-projects-list]'),
        projectsName: q('[data-project-name]'),
        projectsSave: q('[data-project-save]'),
        projectsNew: q('[data-project-new]'),
        customOpen: [...this.root.querySelectorAll('[data-custom-open]')],
        customModal: q('[data-custom-modal]'),
        customClose: [...this.root.querySelectorAll('[data-custom-close]')],
        customName: q('[data-custom-name]'),
        customType: q('[data-custom-type]'),
        customWidth: q('[data-custom-width]'),
        customDepth: q('[data-custom-depth]'),
        customHeight: q('[data-custom-height]'),
        customElevation: q('[data-custom-elevation]'),
        customElevationRow: q('[data-custom-elevation-row]'),
        customColour: q('[data-custom-colour]'),
        customColourRow: q('[data-custom-colour-row]'),
        customPresets: q('[data-custom-presets]'),
        customAdd: q('[data-custom-add]'),
        summary: q('[data-summary]'),
        summaryScrollHint: q('[data-summary-scroll-hint]'),
        fitAssistant: q('[data-fit-assistant]'),
        fitAssistantBody: q('[data-fit-assistant-body]'),
        fitAssistantCount: q('[data-fit-assistant-count]'),
        heightAlert: q('[data-height-alert]'),
        heightAlertList: q('[data-height-alert-list]'),
        heightConfirm: q('[data-height-confirm]'),
        total: q('[data-total]'),
        designId: q('[data-design-id]'),
        addCart: q('[data-add-cart]'),
        save: q('[data-save]'),
        clear: q('[data-clear]'),
        layerSwitch: q('[data-layer-switch]'),
        viewSwitch: q('[data-view-switch]'),
        viewTitle: q('[data-view-title]'),
        viewHelp: q('[data-view-help]'),
        view3d: q('[data-3d-view]'),
        canvas3d: q('[data-3d-canvas]'),
        loading3d: q('[data-3d-loading]'),
        error3d: q('[data-3d-error]'),
        camera3d: q('[data-3d-camera]'),
        layerSwitch3d: q('[data-3d-layer-switch]'),
        add3d: q('[data-3d-add]'),
        wallPicker3d: q('[data-3d-wall-picker]'),
        selected3d: q('[data-3d-selected]'),
        selected3dTitle: q('[data-3d-selected-title]'),
        selected3dMeta: q('[data-3d-selected-meta]'),
        endPanelPlacement3d: q('[data-3d-end-panel-placement]'),
        plinthPlacement3d: q('[data-3d-plinth-placement]'),
        front3d: q('[data-3d-front]'),
        open3d: q('[data-3d-open]'),
        hinge3d: q('[data-3d-hinge]'),
        rotate3d: q('[data-3d-rotate]'),
        snapWall3d: q('[data-3d-snap-wall]'),
        wallLink3d: q('[data-3d-wall-link]'),
        serviceGap3d: q('[data-3d-service-gap]'),
        duplicate3d: q('[data-3d-duplicate]'),
        lock3d: q('[data-3d-lock]'),
        edit3d: q('[data-3d-edit]'),
        remove3d: q('[data-3d-remove]'),
        status: q('[data-status]')
      };
      if (this.isMobile()) {
        this.el.quickSearch.readOnly = true;
      }
    }

    isMobile() {
      return window.matchMedia('(max-width: 700px)').matches;
    }

    viewportMetrics() {
      const viewport = window.visualViewport;
      return {
        width: Math.round(viewport?.width || window.innerWidth || document.documentElement.clientWidth || 0),
        height: Math.round(viewport?.height || window.innerHeight || document.documentElement.clientHeight || 0)
      };
    }

    isCompactUi() {
      const { width, height } = this.viewportMetrics();
      return width <= 1450 || height <= 850;
    }

    isExtraCompactUi() {
      const { width, height } = this.viewportMetrics();
      return width <= 1180 || height <= 720;
    }

    sync2DSelectionDock() {
      const panel = this.el?.selection;
      const stage = this.el?.stage;
      if (!panel || !stage) return;

      if (!this.selectionHome) {
        this.selectionHome = {
          parent: panel.parentElement,
          next: panel.nextSibling
        };
      }

      if (!panel.querySelector('.jkb__selection-empty')) {
        const empty = document.createElement('div');
        empty.className = 'jkb__selection-empty';
        empty.innerHTML = '<strong>Cabinet controls</strong><span>Select a cabinet in the 2D plan to move, rotate, lock, duplicate or remove it.</span>';
        panel.prepend(empty);
      }

      const useDesktopDock = window.matchMedia('(min-width: 1181px)').matches;

      if (useDesktopDock) {
        if (panel.parentElement !== stage) stage.appendChild(panel);
        panel.classList.add('is-desktop-dock');
        const hasSelection = Boolean(this.selectedId && this.units?.some(unit => unit.uid === this.selectedId));
        panel.classList.toggle('is-empty', !hasSelection);
        panel.hidden = false;
        return;
      }

      panel.classList.remove('is-desktop-dock', 'is-empty');
      const home = this.selectionHome;
      if (!home?.parent || panel.parentElement === home.parent) return;

      if (home.next && home.next.parentElement === home.parent) {
        home.parent.insertBefore(panel, home.next);
      } else {
        home.parent.appendChild(panel);
      }
    }

    syncRoomControlsPlacement() {
      const controls = this.root.querySelector('.jkb__room-controls');
      const toolbarRight = this.root.querySelector('.jkb__toolbar-right');
      if (!controls || !toolbarRight) return;

      if (!this.roomControlsHome) {
        this.roomControlsHome = {
          parent: controls.parentElement,
          next: controls.nextSibling
        };
      }

      if (!this.isMobile()) {
        if (controls.parentElement !== toolbarRight) toolbarRight.appendChild(controls);
        controls.classList.add('is-toolbar-room-controls');
        return;
      }

      controls.classList.remove('is-toolbar-room-controls');
      const home = this.roomControlsHome;
      if (!home?.parent || controls.parentElement === home.parent) return;

      if (home.next && home.next.parentElement === home.parent) {
        home.parent.insertBefore(controls, home.next);
      } else {
        home.parent.appendChild(controls);
      }
    }

    updateResponsiveMode() {
      const { width, height } = this.viewportMetrics();
      const compact = width <= 1450 || height <= 850;
      const extraCompact = width <= 1180 || height <= 720;
      const touch = window.matchMedia('(pointer: coarse)').matches ||
        window.matchMedia('(hover: none)').matches;

      this.root.classList.toggle('is-compact-ui', compact);
      this.root.classList.toggle('is-extra-compact-ui', extraCompact);
      this.root.classList.toggle('is-touch-ui', touch);
      this.root.dataset.viewport = `${width}x${height}`;
      this.sync2DSelectionDock();
      this.syncRoomControlsPlacement();

      if (this.el?.quickSearch) {
        this.el.quickSearch.readOnly = this.isMobile();
      }

      if (!compact && !this.isMobile() && this.el?.mobileSearch && !this.el.mobileSearch.hidden) {
        this.closeMobileSearch();
      }

      return { width, height, compact, extraCompact, touch };
    }

    bind() {
      this.el.applyRoom.addEventListener('click', () => this.applyRoomDimensions());
      this.el.roomShapeOpen?.addEventListener('click', () => this.openRoomShapeEditor());
      this.el.roomShapeClose?.forEach(button => button.addEventListener('click', () => this.closeRoomShapeEditor()));
      this.el.roomShapeModal?.addEventListener('pointerdown', e => {
        if (e.target === this.el.roomShapeModal) this.closeRoomShapeEditor();
      });
      this.el.roomShapeTypes?.addEventListener('click', e => {
        const button = e.target.closest('[data-room-shape-type]');
        if (!button || !this.roomShapeDraft) return;
        this.roomShapeDraft.type = String(button.dataset.roomShapeType || 'rectangle');
        this.renderRoomShapeEditor();
      });
      this.el.roomShapeCorners?.addEventListener('click', e => {
        const button = e.target.closest('[data-room-shape-corner]');
        if (!button || !this.roomShapeDraft) return;
        this.roomShapeDraft.corner = String(button.dataset.roomShapeCorner || 'top-right');
        this.renderRoomShapeEditor();
      });
      const updateShapeDraft = () => {
        if (!this.roomShapeDraft) return;
        const type = this.roomShapeDraft.type;
        if (type === 'chamfer') {
          const size = Number(this.el.roomShapeCut?.value) || 0;
          this.roomShapeDraft.cutX = size;
          this.roomShapeDraft.cutY = size;
        } else if (type === 'notch') {
          this.roomShapeDraft.cutX = Number(this.el.roomShapeCutX?.value) || 0;
          this.roomShapeDraft.cutY = Number(this.el.roomShapeCutY?.value) || 0;
        }
        this.updateRoomShapePreview();
      };
      this.el.roomShapeCut?.addEventListener('input', updateShapeDraft);
      this.el.roomShapeCutX?.addEventListener('input', updateShapeDraft);
      this.el.roomShapeCutY?.addEventListener('input', updateShapeDraft);
      this.el.roomShapeReset?.addEventListener('click', () => {
        this.roomShapeDraft = { type: 'rectangle', corner: 'top-right', cutX: 500, cutY: 500 };
        this.renderRoomShapeEditor();
      });
      this.el.roomShapeApply?.addEventListener('click', () => this.applyRoomShapeEditor());
      this.el.widthInput.addEventListener('keydown', e => { if (e.key === 'Enter') this.applyRoomDimensions(); });
      this.el.depthInput.addEventListener('keydown', e => { if (e.key === 'Enter') this.applyRoomDimensions(); });
      this.el.search.addEventListener('input', () => {
        if (this.el.quickSearch) this.el.quickSearch.value = this.el.search.value;
        this.renderCatalog();
        this.renderQuickResults();
      });

      this.el.quickSearch?.addEventListener('input', () => {
        this.el.search.value = this.el.quickSearch.value;
        this.renderCatalog();
        this.renderQuickResults();
      });

      this.el.quickSearch?.addEventListener('focus', () => {
        if (this.isMobile()) return;
        this.renderQuickResults(true);
      });

      this.el.mobileSearchLaunch?.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.openMobileSearch();
      });

      this.el.mobileSearchClose?.addEventListener('click', () => this.closeMobileSearch());
      this.el.mobileSearchBack?.addEventListener('click', () => this.closeMobileSearch());

      this.el.mobileSearch?.addEventListener('pointerdown', e => {
        if (e.target === this.el.mobileSearch) this.closeMobileSearch();
      });

      this.el.mobileSearchInput?.addEventListener('input', () => {
        this.renderMobileSearchResults();
      });

      this.el.mobileSearchInput?.addEventListener('keydown', e => {
        if (e.key === 'Escape') this.closeMobileSearch();
      });

      this.el.mobileSearchLayers?.addEventListener('click', e => {
        const button = e.target.closest('[data-mobile-layer]');
        if (!button) return;
        const layer = String(button.dataset.mobileLayer || 'all');
        const nextLayer = ['all', 'floor', 'wall'].includes(layer) ? layer : 'all';
        if (nextLayer !== this.mobileSearchLayer) {
          this.resetFacetFilters({ renderResults: false });
        }
        this.mobileSearchLayer = nextLayer;
        this.el.mobileSearchLayers.querySelectorAll('[data-mobile-layer]').forEach(item => {
          item.classList.toggle('is-active', item.dataset.mobileLayer === this.mobileSearchLayer);
        });
        this.renderMobileSearchResults();
      });

      [this.el.mobileColours, this.el.mobileWorktops, this.el.mobileHeights, this.el.mobileWidths]
        .filter(Boolean)
        .forEach(container => {
          container.addEventListener('scroll', () => {
            this.updateMobileFilterOverflow(container);
          }, { passive: true });
        });
      window.addEventListener('resize', () => this.scheduleMobileFilterOverflow(), { passive: true });

      this.el.mobileViewPlan?.addEventListener('click', () => {
        const uid = this.lastAddedId;
        this.closeMobileSearch();
        if (this.viewMode === '3d') {
          this.selectedId = uid || this.selectedId;
          this.sync3D();
        } else {
          requestAnimationFrame(() => this.revealJustAdded(uid));
        }
      });

      this.el.mobileSearchResults?.addEventListener('click', e => {
        const button = e.target.closest('[data-mobile-add]');
        if (!button) return;
        const product = this.productById.get(String(button.dataset.mobileAdd));
        if (!product || (!product.available && !product.placeable)) return;

        if (product.placeable) {
          const unit = this.addUnit(product.id);
          if (!unit) return;
          if (this.el.mobileAddedText) this.el.mobileAddedText.textContent = `${product.sku} added ✓`;
          if (this.el.mobileAddedBar) this.el.mobileAddedBar.hidden = false;
        } else {
          this.addExtra(product.id);
          if (this.el.mobileAddedText) this.el.mobileAddedText.textContent = `${product.sku} added to basket ✓`;
          if (this.el.mobileAddedBar) this.el.mobileAddedBar.hidden = false;
        }

        const oldText = button.textContent;
        button.textContent = 'Added ✓';
        button.classList.add('is-added');
        window.setTimeout(() => {
          if (!button.isConnected) return;
          button.textContent = oldText;
          button.classList.remove('is-added');
        }, 900);
      });

      this.el.quickResults?.addEventListener('click', e => {
        const addButton = e.target.closest('[data-quick-add]');
        if (!addButton) return;
        e.preventDefault();
        e.stopPropagation();
        const product = this.productById.get(String(addButton.dataset.quickAdd));
        if (!product || (!product.available && !product.placeable)) return;
        if (product.placeable) this.addUnit(product.id);
        else this.addExtra(product.id);
      });

      this.el.quickResults?.addEventListener('pointerdown', e => {
        if (e.target.closest('[data-quick-add]')) return;
        const row = e.target.closest('[data-quick-product-id]');
        if (!row) return;
        const product = this.productById.get(String(row.dataset.quickProductId));
        if (!product || !product.placeable) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        row.dataset.dragging = 'false';
        this.catalogDrag = {
          productId: String(product.id),
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          dragging: false,
          ghost: null,
          sourceRow: row,
          fromQuickSearch: true
        };
      });

      this.el.searchDragHandle?.addEventListener('pointerdown', e => {
        e.preventDefault();
        e.stopPropagation();
        const searchSurface = this.viewMode === '3d' ? this.el.view3d : this.el.stage;
        const stageRect = searchSurface.getBoundingClientRect();
        const trayRect = this.el.quickSearchWrap.getBoundingClientRect();
        this.searchDrag = {
          pointerId: e.pointerId,
          offsetX: e.clientX - trayRect.left,
          offsetY: e.clientY - trayRect.top,
          width: trayRect.width,
          height: trayRect.height,
          stageRect
        };
        this.el.searchDragHandle.setPointerCapture?.(e.pointerId);
        this.el.quickSearchWrap.classList.add('is-moving');
      });

      document.addEventListener('pointerdown', e => {
        if (!this.el.quickSearchWrap?.contains(e.target) && this.el.quickResults) {
          this.el.quickResults.hidden = true;
        }
      });

      this.el.panelUnplacedTray?.addEventListener('click', e => {
        const placeButton = e.target.closest('[data-panel-start-place]');
        if (placeButton) {
          e.preventDefault();
          this.startEndPanelPlacement(String(placeButton.dataset.panelStartPlace || ''));
          return;
        }

        const cancelButton = e.target.closest('[data-panel-cancel-place]');
        if (cancelButton) {
          e.preventDefault();
          this.cancelEndPanelPlacement();
        }
      });

      this.el.summary?.addEventListener('click', e => {
        const placeButton = e.target.closest('[data-panel-start-place]');
        if (placeButton) {
          e.preventDefault();
          this.startEndPanelPlacement(String(placeButton.dataset.panelStartPlace || ''));
          return;
        }

        const cancelPlaceButton = e.target.closest('[data-panel-cancel-place]');
        if (cancelPlaceButton) {
          e.preventDefault();
          this.cancelEndPanelPlacement();
          return;
        }

        const qtyButton = e.target.closest('[data-summary-qty]');
        if (qtyButton) {
          e.preventDefault();
          const productId = String(qtyButton.dataset.productId || '');
          const delta = Number(qtyButton.dataset.summaryQty) || 0;
          if (productId && delta) this.changeSummaryQuantity(productId, delta);
          return;
        }

        const removeButton = e.target.closest('[data-summary-remove-line]');
        if (removeButton) {
          e.preventDefault();
          this.removeSummaryLine(String(removeButton.dataset.summaryRemoveLine || ''));
        }
      });

      this.el.fitAssistantBody?.addEventListener('click', e => {
        const plinthButton = e.target.closest('[data-fit-add-plinth]');
        if (plinthButton) {
          e.preventDefault();
          this.addRecommendedPlinths();
          return;
        }

        const addButton = e.target.closest('[data-fit-add-panels]');
        if (addButton) {
          e.preventDefault();
          this.addSuggestedEndPanels();
          return;
        }

        const wallFixButton = e.target.closest('[data-fit-wall]');
        if (wallFixButton) {
          e.preventDefault();
          const uid = String(wallFixButton.dataset.fitWall || '');
          const unit = this.units.find(item => String(item.uid) === uid);
          if (unit) this.snapRoomFeatureToNearestWall(unit);
          return;
        }

        const cabinetWallFixButton = e.target.closest('[data-fit-cabinet-wall]');
        if (cabinetWallFixButton) {
          e.preventDefault();
          const uid = String(cabinetWallFixButton.dataset.fitCabinetWall || '');
          const unit = this.units.find(item => String(item.uid) === uid);
          if (!unit) return;
          this.selectedId = unit.uid;
          this.snapSelectedToWall();
          this.focusFitIssue(unit.uid);
          return;
        }

        const gapFixButton = e.target.closest('[data-fit-close-gap]');
        if (gapFixButton) {
          e.preventDefault();
          this.closeSmallRunGap(String(gapFixButton.dataset.fitCloseGap || ''));
          return;
        }

        const focusButton = e.target.closest('[data-fit-focus]');
        if (focusButton) {
          e.preventDefault();
          this.focusFitIssue(String(focusButton.dataset.fitFocus || ''));
        }
      });

      this.el.summary?.addEventListener('scroll', () => this.updateSummaryScrollHint(), { passive: true });

      this.el.summaryToggle?.addEventListener('click', () => {
        const expanded = this.el.summary.classList.toggle('is-expanded');
        this.el.summaryToggle.textContent = expanded ? 'Collapse' : 'View all';
        this.updateSummaryScrollHint();
      });

      this.el.category.addEventListener('change', () => {
        this.resetFacetFilters({ renderResults: false });
        this.renderCatalog();
        this.renderMobileSearchResults();
        if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
      });

      this.el.products.addEventListener('click', e => {
        if (this.suppressCatalogClick) return;
        const addButton = e.target.closest('[data-catalog-add]');
        if (!addButton) return;

        e.preventDefault();
        e.stopPropagation();

        const productId = String(addButton.dataset.catalogAdd || '');
        const product = this.productById.get(productId);
        if (!product || addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') return;

        if (product.placeable) this.addUnit(productId);
        else this.addExtra(productId);
      });

      this.el.products.addEventListener('pointerdown', e => {
        if (e.target.closest('[data-catalog-add]')) return;
        const card = e.target.closest('[data-product-id]');
        if (!card || card.getAttribute('aria-disabled') === 'true') return;
        const product = this.productById.get(String(card.dataset.productId));
        if (!product || !product.placeable) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.catalogDrag = {
          productId: card.dataset.productId,
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          dragging: false,
          ghost: null
        };
      });

      // Do not intercept desktop wheel/trackpad input over the 2D plan.
      // The desktop stage is not vertically scrollable, so native browser
      // propagation already scrolls the page correctly. Forwarding deltaY to
      // window.scrollBy caused tiny residual trackpad/mouse deltas to produce
      // the slow page-creep seen while the pointer was over the planner.

      this.el.room.addEventListener('pointerdown', e => {
        const panelTarget = e.target.closest('[data-panel-drop-side]');
        if (panelTarget) {
          e.preventDefault();
          e.stopPropagation();
          this.placeActiveEndPanel(
            String(panelTarget.dataset.panelDropUnit || ''),
            String(panelTarget.dataset.panelDropSide || '')
          );
          return;
        }

        const deleteButton = e.target.closest('[data-unit-delete]');
        if (deleteButton) {
          e.preventDefault();
          e.stopPropagation();
          const deletedUid = deleteButton.dataset.unitDelete;
          const targetUnit = this.units.find(unit => unit.uid === deletedUid);
          const targetProduct = targetUnit ? this.product(targetUnit) : null;
          const label = targetProduct?.sku || 'this item';
          if (!window.confirm(`Remove ${label} from your kitchen plan?`)) return;
          this.units = this.units.filter(unit => unit.uid !== deletedUid);
          this.releaseEndPanelsForUnit(deletedUid);
          this.justAddedIds.delete(deletedUid);
          if (this.lastAddedId === deletedUid) this.lastAddedId = null;
          if (this.selectedId === deletedUid) this.selectedId = null;
          this.renderAll();
          this.save();
          return;
        }

        const rotateButton = e.target.closest('[data-unit-rotate]');
        if (rotateButton) {
          e.preventDefault();
          e.stopPropagation();
          this.selectedId = rotateButton.dataset.unitRotate;
          this.rotateSelected();
          return;
        }

        const node = e.target.closest('[data-unit-id]');
        if (!node) {
          this.select(null);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const unit = this.units.find(u => u.uid === node.dataset.unitId);
        if (!unit) return;
        const point = this.roomPoint(e);
        const wasSelected = this.selectedId === unit.uid;

        // Do not call select() here: select() re-renders all unit DOM nodes,
        // which destroys pointer capture on iOS during the drag gesture.
        this.selectedId = unit.uid;
        this.el.room.querySelectorAll('[data-unit-id]').forEach(otherNode => {
          otherNode.classList.toggle('is-selected', otherNode === node);
        });
        this.renderSelection();

        const product = this.product(unit);
        if (!wasSelected && product?.category === 'wall') {
          const below = this.floorUnitsBelowWall(unit);
          if (below.length) {
            const names = below.map(item => this.product(item)?.sku).filter(Boolean).join(', ');
            this.setStatus(`Wall unit selected. Tap it again without dragging to select ${names || 'the cabinet'} underneath.`);
          }
        }

        if (unit.locked) {
          this.setStatus('Cabinet is locked. Tap Unlock to move or rotate it.');
          return;
        }

        node.setPointerCapture?.(e.pointerId);
        node.classList.add('is-dragging');
        this.drag = {
          uid: unit.uid,
          pointerId: e.pointerId,
          node,
          grabX: point.x - unit.x,
          grabY: point.y - unit.y,
          originX: unit.x,
          originY: unit.y,
          startClientX: e.clientX,
          startClientY: e.clientY,
          startedAt: Date.now(),
          wasSelected
        };
        this.lockCabinetDragGesture();
      });

      window.addEventListener('pointermove', e => {
        this.onSearchDragMove(e);
        this.onCatalogPointerMove(e);
        this.onPointerMove(e);
      }, { passive: false });

      // iOS Safari can still promote an in-progress pointer gesture into page
      // scrolling. While a cabinet is actively captured, keep that one gesture
      // owned by the planner. Empty room space remains normally scrollable.
      window.addEventListener('touchmove', e => {
        if (!this.drag) return;
        if (e.cancelable) e.preventDefault();
      }, { passive: false, capture: true });
      window.addEventListener('pointerup', e => {
        this.stopNudgeRepeat();
        this.onSearchDragEnd(e);
        this.onCatalogPointerUp(e, false);
        this.onPointerUp(e, false);
      });
      window.addEventListener('pointercancel', e => {
        this.stopNudgeRepeat();
        this.onSearchDragEnd(e);
        this.onCatalogPointerUp(e, true);
        this.onPointerUp(e, true);
      });

      // Move immediately on pointerdown and repeat after a short hold.
      // The following synthetic click is suppressed, preventing iOS double-tap
      // zoom while keeping mouse, touch and pen controls equally responsive.
      this.el.selection?.addEventListener('pointerdown', e => {
        const nudge = e.target.closest('[data-nudge]');
        if (!nudge) return;
        e.preventDefault();
        e.stopPropagation();
        this.lastNudgePointerAt = Date.now();
        this.startNudgeRepeat(nudge.dataset.nudge);
      });

      this.el.selection?.addEventListener('click', e => {
        const nudge = e.target.closest('[data-nudge]');
        if (nudge) {
          if (Date.now() - (this.lastNudgePointerAt || 0) < 600) return;
          this.nudgeSelected(nudge.dataset.nudge);
          return;
        }
        if (e.target.closest('[data-snap-wall]')) {
          this.snapSelectedToWall();
          return;
        }
        if (e.target.closest('[data-wall-link]')) {
          this.toggleSelectedWallLink();
          return;
        }
        if (e.target.closest('[data-service-gap]')) {
          this.toggleSelectedServiceGap();
          return;
        }

        const applyPlinth = e.target.closest('[data-plinth-apply]');
        if (applyPlinth) {
          this.applySelectedPlinth(this.el.plinthPlacement);
          return;
        }

        const removePlinth = e.target.closest('[data-plinth-remove]');
        if (removePlinth) {
          this.removeSelectedPlinth();
          return;
        }

        const addPanel = e.target.closest('[data-panel-add-side]');
        if (addPanel) {
          this.addAndPlaceSelectedEndPanel(String(addPanel.dataset.panelAddSide || ''));
          return;
        }

        const placePanel = e.target.closest('[data-panel-place-side]');
        if (placePanel) {
          this.placeSelectedEndPanel(String(placePanel.dataset.panelPlaceSide || ''));
          return;
        }

        const removePanel = e.target.closest('[data-panel-remove-side]');
        if (removePanel) {
          this.removeSelectedEndPanel(String(removePanel.dataset.panelRemoveSide || ''));
        }
      });
      [this.el.moveStep, this.el.moveStep3d].filter(Boolean).forEach(button => {
        button.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          this.precisionStep = this.precisionStep === 10 ? 50 : 10;
          this.renderSelection();
          this.setStatus(`Movement step changed to ${this.precisionStep} mm in 2D and 3D.`);
        });
      });
      this.el.rotate.addEventListener('click', () => this.rotateSelected());
      this.el.duplicate?.addEventListener('click', () => this.duplicateSelected());
      this.el.lock?.addEventListener('click', () => this.toggleSelectedLock());
      this.el.delete.addEventListener('click', () => this.deleteSelected());
      this.el.undo?.addEventListener('click', () => this.undo());
      this.el.redo?.addEventListener('click', () => this.redo());
      this.el.shareDesign?.addEventListener('click', () => this.shareDesign());
      this.el.restoreLocal?.addEventListener('click', () => this.restoreLocalAfterShare());
      this.el.projectsOpen?.addEventListener('click', () => this.openProjects());
      this.el.projectsClose?.forEach(button => button.addEventListener('click', () => this.closeProjects()));
      this.el.projectsSave?.addEventListener('click', () => this.saveCurrentProject());
      this.el.projectsNew?.addEventListener('click', () => this.newProject());
      this.el.projectsList?.addEventListener('click', e => {
        const open = e.target.closest('[data-project-open]');
        if (open) {
          this.openProject(open.dataset.projectOpen);
          return;
        }
        const copy = e.target.closest('[data-project-copy]');
        if (copy) {
          this.duplicateProject(copy.dataset.projectCopy);
          return;
        }
        const remove = e.target.closest('[data-project-delete]');
        if (remove) this.deleteProject(remove.dataset.projectDelete);
      });
      this.el.customOpen?.forEach(button => button.addEventListener('click', () => this.openCustomModal()));
      this.el.customClose?.forEach(button => button.addEventListener('click', () => this.closeCustomModal()));
      this.el.customAdd?.addEventListener('click', () => this.addCustomItem());
      this.el.customType?.addEventListener('change', () => this.applyCustomTypeDefaults());
      this.el.customPresets?.addEventListener('click', event => {
        const button = event.target.closest('[data-custom-preset]');
        if (!button || !this.el.customType) return;
        this.el.customType.value = button.dataset.customPreset;
        this.applyCustomTypeDefaults();
        this.el.customWidth?.focus();
        this.el.customWidth?.select?.();
      });
      this.el.save.addEventListener('click', () => this.save(true));
      this.el.clear.addEventListener('click', () => this.clear());
      this.el.heightConfirm?.addEventListener('change', () => this.updateCheckoutState());
      this.el.addCart.addEventListener('click', () => this.addToCart());

      window.addEventListener('keydown', e => {
        const target = e.target;
        const typing = target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target?.isContentEditable;
        if (typing) return;

        const command = e.metaKey || e.ctrlKey;
        if (command && e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) this.redo();
          else this.undo();
          return;
        }
        if (command && e.key.toLowerCase() === 'y') {
          e.preventDefault();
          this.redo();
          return;
        }

        if (!this.selectedId) return;
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          this.deleteSelected();
        } else if (e.key.toLowerCase() === 'r') {
          e.preventDefault();
          this.rotateSelected();
        } else if (e.key.toLowerCase() === 'd' && command) {
          e.preventDefault();
          this.duplicateSelected();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.select(null);
        }
      });

      this.el.viewSwitch?.addEventListener('click', e => {
        const button = e.target.closest('[data-view]');
        if (!button) return;
        this.setView(button.dataset.view);
      });

      this.el.view3d?.addEventListener('pointerdown', e => {
        const nudge = e.target.closest('[data-3d-nudge]');
        if (!nudge) return;
        e.preventDefault();
        e.stopPropagation();
        this.lastNudgePointerAt = Date.now();
        const screenDirection = this.viewer3d?.screenNudgeDirection?.(nudge.dataset['3dNudge']) || nudge.dataset['3dNudge'];
        this.startNudgeRepeat(screenDirection);
      });

      this.el.view3d?.addEventListener('click', e => {
        const nudge = e.target.closest('[data-3d-nudge]');
        if (nudge) {
          e.preventDefault();
          e.stopPropagation();
          if (Date.now() - (this.lastNudgePointerAt || 0) >= 600) {
            const screenDirection = this.viewer3d?.screenNudgeDirection?.(nudge.dataset['3dNudge']) || nudge.dataset['3dNudge'];
            this.nudgeSelected(screenDirection);
          }
          return;
        }

        const wallButton = e.target.closest('[data-wall-colour]');
        if (wallButton) {
          this.wallColour = wallButton.dataset.wallColour || 'white';
          this.el.wallPicker3d?.querySelectorAll('[data-wall-colour]').forEach(button => {
            button.classList.toggle('is-active', button === wallButton);
          });
          this.save();
          this.sync3D();
          return;
        }

        const layerButton3d = e.target.closest('[data-3d-layer]');
        if (layerButton3d) {
          this.setLayer(layerButton3d.dataset['3dLayer'], { resetFilters: true });
          return;
        }

        const cameraButton = e.target.closest('[data-3d-camera]');
        if (cameraButton) {
          const mode = cameraButton.dataset['3dCamera'];
          this.el.view3d.querySelectorAll('[data-3d-camera]').forEach(button => {
            button.classList.toggle('is-active', button === cameraButton);
          });
          if (mode === 'fit') this.viewer3d?.fitView();
          else this.viewer3d?.cameraPreset(mode);
          return;
        }

        if (e.target.closest('[data-3d-add]')) {
          if (this.isMobile() || this.isCompactUi()) this.openMobileSearch();
          else {
            this.el.quickSearch?.focus();
            this.renderQuickResults(true);
          }
          return;
        }

        if (e.target.closest('[data-3d-open]')) {
          this.toggleSelectedDoor();
          return;
        }

        if (e.target.closest('[data-3d-hinge]')) {
          this.toggleSelectedHinge();
          return;
        }

        if (e.target.closest('[data-3d-rotate]')) {
          this.rotateSelected();
          return;
        }

        if (e.target.closest('[data-3d-snap-wall]')) {
          this.snapSelectedToWall();
          return;
        }

        if (e.target.closest('[data-3d-wall-link]')) {
          this.toggleSelectedWallLink();
          return;
        }

        if (e.target.closest('[data-3d-service-gap]')) {
          this.toggleSelectedServiceGap();
          return;
        }

        const applyPlinth3d = e.target.closest('[data-plinth-apply]');
        if (applyPlinth3d) {
          this.applySelectedPlinth(this.el.plinthPlacement3d);
          return;
        }

        const removePlinth3d = e.target.closest('[data-plinth-remove]');
        if (removePlinth3d) {
          this.removeSelectedPlinth();
          return;
        }

        const addPanel3d = e.target.closest('[data-panel-add-side]');
        if (addPanel3d) {
          this.addAndPlaceSelectedEndPanel(String(addPanel3d.dataset.panelAddSide || ''));
          return;
        }

        const placePanel3d = e.target.closest('[data-panel-place-side]');
        if (placePanel3d) {
          this.placeSelectedEndPanel(String(placePanel3d.dataset.panelPlaceSide || ''));
          return;
        }

        const removePanel3d = e.target.closest('[data-panel-remove-side]');
        if (removePanel3d) {
          this.removeSelectedEndPanel(String(removePanel3d.dataset.panelRemoveSide || ''));
          return;
        }

        if (e.target.closest('[data-3d-duplicate]')) {
          this.duplicateSelected();
          return;
        }

        if (e.target.closest('[data-3d-lock]')) {
          this.toggleSelectedLock();
          return;
        }

        if (e.target.closest('[data-3d-edit]')) {
          const uid = this.selectedId;
          this.setView('2d');
          requestAnimationFrame(() => this.revealJustAdded(uid));
          return;
        }

        if (e.target.closest('[data-3d-remove]')) {
          this.deleteSelected();
        }
      });

      this.el.layerSwitch?.addEventListener('click', e => {
        const button = e.target.closest('[data-layer]');
        if (!button) return;
        this.setLayer(button.dataset.layer, { resetFilters: true });
      });

      [this.el.colours, this.el.mobileColours].filter(Boolean).forEach(container => {
        container.addEventListener('click', e => {
          const chip = e.target.closest('[data-colour]');
          if (!chip) return;
          this.activeColour = chip.dataset.colour;
          this.renderColourFilters();
          this.renderCatalog();
          this.renderMobileSearchResults();
          if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
        });
      });

      [this.el.worktops, this.el.mobileWorktops].filter(Boolean).forEach(container => {
        container.addEventListener('click', e => {
          const chip = e.target.closest('[data-worktop-filter]');
          if (!chip) return;
          this.activeWorktop = chip.dataset.worktopFilter;
          this.renderWorktopFilters();
          this.renderCatalog();
          this.renderMobileSearchResults();
          if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
        });
      });

      [this.el.heights, this.el.mobileHeights].filter(Boolean).forEach(container => {
        container.addEventListener('click', e => {
          const chip = e.target.closest('[data-height-filter]');
          if (!chip) return;
          this.activeHeight = chip.dataset.heightFilter;
          this.renderHeightFilters();
          this.renderCatalog();
          this.renderMobileSearchResults();
          if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
        });
      });

      [this.el.widths, this.el.mobileWidths].filter(Boolean).forEach(container => {
        container.addEventListener('click', e => {
          const chip = e.target.closest('[data-width-filter]');
          if (!chip) return;
          this.activeWidth = chip.dataset.widthFilter;
          this.renderWidthFilters();
          this.renderCatalog();
          this.renderMobileSearchResults();
          if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
        });
      });

      this.root.querySelectorAll('[data-reset-filters]').forEach(button => {
        button.addEventListener('click', () => this.resetProductFilters());
      });

      window.addEventListener('scroll', () => {
        if (this.el.quickResults && !this.el.quickResults.hidden) this.positionQuickResults();
      }, { passive: true });

      window.visualViewport?.addEventListener('resize', () => {
        if (this.el.quickSearchWrap?.classList.contains('is-searching')) this.positionSearchForTyping();
        if (this.el.quickResults && !this.el.quickResults.hidden) this.positionQuickResults();
      });

      window.visualViewport?.addEventListener('scroll', () => {
        if (this.el.quickSearchWrap?.classList.contains('is-searching')) this.positionSearchForTyping();
        if (this.el.quickResults && !this.el.quickResults.hidden) this.positionQuickResults();
      });

      window.addEventListener('keydown', e => {
        if (!this.root.isConnected) return;
        const modifier = e.ctrlKey || e.metaKey;

        if (modifier && !this.isTyping(e.target) && e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) this.redo();
          else this.undo();
          return;
        }

        if (modifier && !this.isTyping(e.target) && e.key.toLowerCase() === 'y') {
          e.preventDefault();
          this.redo();
          return;
        }

        if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedId && !this.isTyping(e.target)) {
          e.preventDefault();
          this.deleteSelected();
        }
        if (e.key.toLowerCase() === 'r' && this.selectedId && !this.isTyping(e.target)) {
          e.preventDefault();
          this.rotateSelected();
        }
        if (e.key === 'Escape' && this.el.roomShapeModal && !this.el.roomShapeModal.hidden) {
          this.closeRoomShapeEditor();
          return;
        }
        if (e.key === 'Escape' && this.el.customModal && !this.el.customModal.hidden) {
          this.closeCustomModal();
        }
      });
    }

    async setView(mode) {
      if (!['2d', '3d'].includes(mode)) return;
      this.viewMode = mode;
      const is3d = mode === '3d';

      this.root.classList.toggle('is-3d', is3d);
      this.el.stage.hidden = is3d;
      this.el.view3d.hidden = !is3d;

      if (is3d && !this.isMobile()) {
        requestAnimationFrame(() => {
          const toolbar = this.el.viewSwitch?.closest('.jkb__workspace-toolbar');
          toolbar?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      if (this.el.quickSearchWrap) {
        const target = is3d ? this.el.view3d : this.el.stage;
        if (target && this.el.quickSearchWrap.parentElement !== target) {
          target.appendChild(this.el.quickSearchWrap);
          Object.assign(this.el.quickSearchWrap.style, {
            left: '',
            right: '',
            top: '',
            bottom: '',
            width: '',
            transform: ''
          });
        }
      }
      this.el.viewSwitch?.querySelectorAll('[data-view]').forEach(button => {
        button.classList.toggle('is-active', button.dataset.view === mode);
      });

      if (this.el.viewTitle) this.el.viewTitle.textContent = is3d ? '3D view' : '2D plan';
      if (this.el.viewHelp) {
        this.el.viewHelp.textContent = is3d
          ? 'Drag to rotate, pinch or scroll to zoom, and tap a cabinet to select it.'
          : 'Drag cabinets into the room. Measurements are in millimetres.';
      }

      if (!is3d) {
        this.viewer3d?.hide();
        return;
      }

      await this.ensure3D();
      this.viewer3d?.show();
      this.sync3D();
    }

    async ensure3D() {
      if (this.viewer3d?.ready) return this.viewer3d;
      if (this.viewer3dInit) return this.viewer3dInit;

      if (!window.JDKitchen3D || !this.el.canvas3d) {
        this.el.error3d && (this.el.error3d.hidden = false);
        return null;
      }

      this.el.loading3d && (this.el.loading3d.hidden = false);
      this.el.error3d && (this.el.error3d.hidden = true);

      this.viewer3d = new window.JDKitchen3D(this.el.canvas3d, {
        onSelect: uid => {
          this.select(uid);
          this.viewer3d?.setSelected(uid);
        },
        onMove: (uid, x, y, phase) => this.moveUnitFrom3D(uid, x, y, phase),
        onReady: () => {
          if (this.el.loading3d) this.el.loading3d.hidden = true;
          if (this.el.error3d) this.el.error3d.hidden = true;
        },
        onError: () => {
          if (this.el.loading3d) this.el.loading3d.hidden = true;
          if (this.el.error3d) this.el.error3d.hidden = false;
        }
      });

      this.viewer3dInit = this.viewer3d.init()
        .then(() => {
          this.sync3D();
          return this.viewer3d;
        })
        .catch(error => {
          console.warn('JD Kitchen Builder: 3D unavailable', error);
          return null;
        })
        .finally(() => {
          this.viewer3dInit = null;
        });

      return this.viewer3dInit;
    }

    threeData() {
      return {
        roomWidth: this.roomWidth,
        roomDepth: this.roomDepth,
        roomShape: { ...this.normaliseRoomShape(this.roomShape) },
        units: this.units,
        selectedId: this.selectedId,
        justAddedIds: this.justAddedIds,
        wallColour: this.wallColour,
        activeLayer: this.activeLayer,
        endPanels: this.endPanelPlacements(),
        plinthApplications: (this.plinthApplications || []).map(item => ({ ...item })),
        plinthRuns: this.appliedPlinthRuns(),
        extras: { ...this.extras },
        productById: this.productById,
        catalog: this.catalog,
        precisionStep: this.precisionStep
      };
    }

    moveUnitFrom3D(uid, rawX, rawY, phase) {
      const unit = this.units.find(item => String(item.uid) === String(uid));
      if (!unit || unit.locked) return { allowed: false };

      if (phase === 'start') {
        this.drag3dOrigin = { uid: unit.uid, x: unit.x, y: unit.y };
        return { allowed: true, x: unit.x, y: unit.y };
      }

      if (!this.drag3dOrigin || String(this.drag3dOrigin.uid) !== String(unit.uid)) {
        return { allowed: false, x: unit.x, y: unit.y };
      }

      if (phase === 'cancel') {
        unit.x = this.drag3dOrigin.x;
        unit.y = this.drag3dOrigin.y;
        this.drag3dOrigin = null;
        this.renderAll();
        this.save();
        return { allowed: true, x: unit.x, y: unit.y };
      }

      const position = this.snapPosition(unit, rawX, rawY, this.precisionStep);
      unit.x = position.x;
      unit.y = position.y;

      if (phase === 'move') {
        return { allowed: true, x: unit.x, y: unit.y };
      }

      const origin = this.drag3dOrigin;
      if (!this.unitFitsRoomShape(unit)) {
        unit.x = origin.x;
        unit.y = origin.y;
        this.setStatus('That position is outside the edited room shape. The item was returned to its previous position.');
      } else if (this.hasCollision(unit)) {
        unit.x = origin.x;
        unit.y = origin.y;
        this.setStatus('That position overlaps another item. The unit was returned to its previous position.');
      } else {
        this.justAddedIds.delete(unit.uid);
        if (this.lastAddedId === unit.uid) this.lastAddedId = null;
        const product = this.product(unit);
        if (product?.planningKind && product.planningKind !== 'obstacle') {
          this.snapRoomFeatureToNearestWall(unit, { silent: true, render: false, save: false });
        }
        this.setStatus(`${product?.productTitle || 'Item'} moved in 3D.`);
      }

      this.drag3dOrigin = null;
      this.renderAll();
      this.save();
      return { allowed: true, x: unit.x, y: unit.y };
    }

    sync3D() {
      if (!this.viewer3d) return;
      this.viewer3d.update(this.threeData());
      this.render3DSelection();
    }

    render3DSelection() {
      if (!this.el.selected3d) return;
      const unit = this.selected();
      const product = unit ? this.product(unit) : null;
      this.el.selected3d.hidden = !product;
      if (!product) {
        if (this.el.endPanelPlacement3d) {
          this.el.endPanelPlacement3d.hidden = true;
          this.el.endPanelPlacement3d.innerHTML = '';
        }
        if (this.el.plinthPlacement3d) {
          this.el.plinthPlacement3d.hidden = true;
          this.el.plinthPlacement3d.innerHTML = '';
        }
        return;
      }

      const fitInfo = this.fittedRunInfo(unit);
      const appliedPanels = this.appliedEndPanelsForUnit(unit);
      // End-panel state has its own compact control next to the cabinet
      // details in 3D. Repeating SKU/side information in the meta line makes
      // the compact inspector hard to scan, especially on Surface/tablet.
      const panelText = '';
      const belowUnits = product.category === 'wall' ? this.floorUnitsBelowWall(unit) : [];
      const stackText = belowUnits.length
        ? ` · above ${belowUnits.map(item => this.product(item)?.sku || 'base').join(', ')}`
        : '';
      const depthText = fitInfo
        ? `D${fitInfo.fittedDepth} fitted · carcass D${fitInfo.carcassDepth} · rear gap ${fitInfo.serviceGap} mm · front ${fitInfo.frontAllowance} mm ${fitInfo.includedWorktop ? 'overhang' : 'allowance'}`
        : `D${product.depth}`;
      this.el.selected3dTitle.textContent = `${product.sku} · ${product.colour}`;
      this.el.selected3dMeta.textContent = `W${product.width} × ${depthText} × H${product.height} mm · ${product.familyLabel}${panelText}${product.planningKind && Number(product.elevation) ? ` · bottom +${Math.round(product.elevation)} mm` : ''}${!product.custom && !product.available ? ' · OUT OF STOCK – planning only' : ''}`;
      this.renderEndPanelPlacementControls(this.el.endPanelPlacement3d, unit);
      this.renderPlinthPlacementControls(this.el.plinthPlacement3d, unit);

      if (this.el.front3d) {
        const frontText = product.planningKind ? '' : this.frontDirectionText(unit);
        this.el.front3d.hidden = !frontText;
        this.el.front3d.textContent = frontText ? `FRONT ${frontText}` : '';
      }

      if (this.el.rotate3d) {
        this.el.rotate3d.textContent = 'Rotate';
      }
      if (this.el.edit3d) {
        this.el.edit3d.textContent = '2D';
      }
      if (this.el.snapWall3d) {
        const isCorner = Boolean(product.cornerShape || product.category === 'corner');
        this.el.snapWall3d.textContent = isCorner ? 'Snap corner' : 'Snap to wall';
        this.el.snapWall3d.setAttribute(
          'aria-label',
          isCorner ? 'Move cabinet to nearest clear corner now' : 'Move cabinet to nearest clear wall now'
        );
      }

      if (this.el.moveStep3d) {
        this.el.moveStep3d.textContent = `${this.precisionStep} mm`;
        this.el.moveStep3d.setAttribute('aria-pressed', this.precisionStep === 10 ? 'true' : 'false');
        this.el.moveStep3d.title = this.precisionStep === 10
          ? 'Fine movement enabled. Tap for 50 mm.'
          : 'Normal movement enabled. Tap for 10 mm fine adjustment.';
      }

      if (this.el.wallLink3d) {
        const supportsFreeFacing = !product.planningKind && !product.cornerShape && product.category !== 'corner';
        this.el.wallLink3d.hidden = !supportsFreeFacing;
        this.el.wallLink3d.textContent = this.isCompactUi()
          ? (unit.wallLinked === false ? 'Wall: OFF' : 'Wall: ON')
          : (unit.wallLinked === false ? 'Wall: OFF' : 'Wall: ON');
        this.el.wallLink3d.setAttribute('aria-label', unit.wallLinked === false ? 'Turn automatic wall alignment on' : 'Turn automatic wall alignment off for free positioning');
        this.el.wallLink3d.setAttribute('aria-pressed', unit.wallLinked === false ? 'false' : 'true');
        this.el.wallLink3d.classList.toggle('is-active', unit.wallLinked !== false);
      }
      if (this.el.serviceGap3d) {
        const canToggleGap = this.supportsServiceGapToggle(unit);
        this.el.serviceGap3d.classList.toggle('is-gap-applicable', canToggleGap);
        this.el.serviceGap3d.hidden = !canToggleGap;
        if (canToggleGap) {
          const gap = this.serviceGapForUnit(unit);
          const standard = this.defaultServiceGap(product);
          this.el.serviceGap3d.textContent = `Rear gap ${Math.round(gap)} mm`;
          this.el.serviceGap3d.title = gap > 0
            ? `Required position for this 463 mm carcass: leave ${Math.round(standard)} mm behind it so the front sits at 578 mm and a 600 mm worktop leaves about 22 mm overhang.`
            : `Rear gap is off. Restore ${Math.round(standard)} mm for the normal 600 mm worktop position.`;
          this.el.serviceGap3d.setAttribute('aria-pressed', gap > 0 ? 'true' : 'false');
        }
      }
      if (this.el.lock3d) {
        this.el.lock3d.textContent = unit.locked ? 'Unlock' : 'Lock';
        this.el.lock3d.setAttribute('aria-pressed', unit.locked ? 'true' : 'false');
        this.el.lock3d.classList.toggle('is-active', Boolean(unit.locked));
      }

      const canOpen = this.supportsDoorOpen(product);
      if (this.el.open3d) {
        this.el.open3d.hidden = !canOpen;
        this.el.open3d.textContent = unit.doorOpen ? 'Close' : 'Open';
      }

      const canHinge = canOpen && this.supportsHingeChange(product);
      if (this.el.hinge3d) {
        this.el.hinge3d.hidden = !canHinge;
        const side = unit.hingeSide === 'right' ? 'Right' : 'Left';
        this.el.hinge3d.textContent = `Hinge: ${side}`;
      }

      this.viewer3d?.setSelected(unit.uid);
    }

    supportsDoorOpen(product) {
      return Boolean(product &&
        !product.planningKind &&
        !product.cornerShape &&
        ['base', 'wall', 'sink', 'tall'].includes(product.category));
    }

    supportsHingeChange(product) {
      if (!this.supportsDoorOpen(product)) return false;
      if (Boolean(product.cornerShape || product.category === 'corner')) return false;
      if (product.category === 'tall') return true;
      // Extractor/bridging fronts hinge along the top edge, so a left/right
      // hinge choice is not applicable.
      if (
        product.familyLabel === 'Bridging wall unit' ||
        /^(?:W)?EX\d/.test(String(product.sku || '').toUpperCase())
      ) return false;
      return Number(product.width || 0) < 595;
    }

    toggleSelectedDoor() {
      const unit = this.selected();
      const product = unit ? this.product(unit) : null;
      if (!unit || !this.supportsDoorOpen(product)) return;
      unit.doorOpen = !unit.doorOpen;
      this.save();
      this.sync3D();
    }

    toggleSelectedHinge() {
      const unit = this.selected();
      const product = unit ? this.product(unit) : null;
      if (!unit || !this.supportsHingeChange(product)) return;
      unit.hingeSide = unit.hingeSide === 'right' ? 'left' : 'right';
      this.save();
      this.sync3D();
    }

    setLayer(layer, options = {}) {
      if (!['all', 'floor', 'wall'].includes(layer)) return;
      const changed = layer !== this.activeLayer;
      if (changed && options.resetFilters) {
        this.resetFacetFilters({ renderResults: false });
        if (this.el.category) this.el.category.value = 'all';
      }
      this.activeLayer = layer;
      this.mobileSearchLayer = layer;
      this.el.layerSwitch?.querySelectorAll('[data-layer]').forEach(button => {
        button.classList.toggle('is-active', button.dataset.layer === layer);
      });
      this.el.layerSwitch3d?.querySelectorAll('[data-3d-layer]').forEach(button => {
        button.classList.toggle('is-active', button.dataset['3dLayer'] === layer);
      });
      const selected = this.selected();
      if (selected && !this.unitVisibleInLayer(selected)) this.selectedId = null;
      this.renderCatalog();
      if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
      if (this.el.mobileSearch && !this.el.mobileSearch.hidden) this.renderMobileSearchResults();
      this.renderUnits();
      this.renderSelection();
      this.sync3D();
    }

    unitVisibleInLayer(unit) {
      const product = this.product(unit);
      if (!product || this.activeLayer === 'all') return true;

      // Doors, windows, radiators and obstacles are room constraints rather
      // than cabinet layers, so they should remain visible in every view.
      if (product.planningKind) return true;

      const isWall = product.category === 'wall';
      return this.activeLayer === 'wall' ? isWall : !isWall;
    }

    productVisibleInLayerFilter(product, layer = this.activeLayer) {
      if (!product || layer === 'all') return true;

      if (product.category === 'panel') {
        const family = this.endPanelFamily(product.sku);
        if (layer === 'wall') return family === 'ALWEND';
        if (layer === 'floor') return family === 'ALBEND' || family === 'GBEND' || family === 'ALDEC';
      }

      if (layer === 'wall') return product.category === 'wall';
      if (layer === 'floor') {
        if (product.category === 'plinth') return true;
        return product.placeable && product.category !== 'wall';
      }
      return true;
    }

    isTyping(target) {
      return target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
    }

    openCustomModal() {
      if (!this.el.customModal) return;
      if (this.el.mobileSearch && !this.el.mobileSearch.hidden) this.closeMobileSearch();
      this.el.customModal.hidden = false;
      this.el.customModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('jkb-custom-open');
      requestAnimationFrame(() => {
        try { this.el.customName?.focus({ preventScroll: true }); }
        catch (_) { this.el.customName?.focus(); }
      });
    }

    closeCustomModal() {
      if (!this.el.customModal) return;
      this.el.customModal.hidden = true;
      this.el.customModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('jkb-custom-open');
    }

    applyCustomTypeDefaults() {
      const type = this.el.customType?.value || 'base';
      const defaults = {
        base: { name: 'Custom cabinet', width: 600, depth: 560, height: 870, elevation: 0 },
        wall: { name: 'Custom wall cabinet', width: 600, depth: 320, height: 720, elevation: 1450 },
        tall: { name: 'Custom tall cabinet', width: 500, depth: 580, height: 1950, elevation: 0 },
        door: { name: 'Door opening', width: 900, depth: 900, height: 2100, elevation: 0 },
        window: { name: 'Window', width: 1000, depth: 100, height: 1200, elevation: 900 },
        radiator: { name: 'Radiator', width: 1000, depth: 150, height: 600, elevation: 150 },
        fridge: { name: 'Fridge / freezer', width: 600, depth: 650, height: 1850, elevation: 0 },
        dishwasher: { name: 'Dishwasher', width: 600, depth: 600, height: 820, elevation: 0 },
        'oven-appliance': { name: 'Oven / cooker', width: 600, depth: 600, height: 900, elevation: 0 },
        hood: { name: 'Cooker hood', width: 600, depth: 500, height: 700, elevation: 1450 },
        obstacle: { name: 'Room obstacle', width: 600, depth: 600, height: 1000, elevation: 0 }
      }[type] || { name: 'Custom cabinet', width: 600, depth: 560, height: 870, elevation: 0 };

      const roomFeature = ['door', 'window', 'radiator', 'fridge', 'dishwasher', 'oven-appliance', 'hood', 'obstacle'].includes(type);

      if (this.el.customName) this.el.customName.value = defaults.name;
      if (this.el.customWidth) this.el.customWidth.value = defaults.width;
      if (this.el.customDepth) this.el.customDepth.value = defaults.depth;
      if (this.el.customHeight) this.el.customHeight.value = defaults.height;
      if (this.el.customElevation) this.el.customElevation.value = defaults.elevation;
      if (this.el.customElevationRow) this.el.customElevationRow.hidden = !roomFeature;
      if (this.el.customColourRow) this.el.customColourRow.hidden = roomFeature;
    }

    addCustomItem() {
      const name = String(this.el.customName?.value || 'Custom cabinet').trim().slice(0, 50) || 'Custom cabinet';
      const requestedType = String(this.el.customType?.value || 'base');
      const type = ['base', 'wall', 'tall', 'door', 'window', 'radiator', 'fridge', 'dishwasher', 'oven-appliance', 'hood', 'obstacle'].includes(requestedType)
        ? requestedType
        : 'base';
      const width = this.clamp(Number(this.el.customWidth?.value), 100, 3000);
      const depth = this.clamp(Number(this.el.customDepth?.value), 50, 1500);
      const height = this.clamp(Number(this.el.customHeight?.value), 100, 3000);
      const elevation = this.clamp(Number(this.el.customElevation?.value) || 0, 0, 3000);
      const colour = String(this.el.customColour?.value || 'White');

      if (!width || !depth || !height) {
        this.setStatus('Enter width, depth and height for the planning item.');
        return;
      }

      const roomFeature = ['door', 'window', 'radiator', 'fridge', 'dishwasher', 'oven-appliance', 'hood', 'obstacle'].includes(type);
      const category = (type === 'window' || type === 'hood')
        ? 'wall'
        : roomFeature
          ? 'base'
          : type;

      const labels = {
        base: 'Custom floor unit',
        wall: 'Custom wall unit',
        tall: 'Custom tall unit',
        door: 'Door opening / keep clear',
        window: 'Window opening',
        radiator: 'Radiator / keep clear',
        fridge: 'Fridge / freezer',
        dishwasher: 'Dishwasher',
        'oven-appliance': 'Oven / cooker',
        hood: 'Cooker hood',
        obstacle: 'Room obstacle / appliance'
      };

      const skuPrefixes = {
        door: 'DOOR',
        window: 'WINDOW',
        radiator: 'RAD',
        fridge: 'FRIDGE',
        dishwasher: 'DISHWASHER',
        'oven-appliance': 'OVEN',
        hood: 'HOOD',
        obstacle: 'OBST'
      };

      const id = `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const number = this.customProducts.size + 1;
      const product = {
        id,
        sku: roomFeature ? `${skuPrefixes[type]}-${number}` : `CUSTOM-${number}`,
        productTitle: name,
        variantTitle: '',
        price: 0,
        available: true,
        url: '',
        image: null,
        category,
        familyLabel: labels[type] || 'Custom planning item',
        width,
        depth,
        height,
        placeable: true,
        standalone: false,
        colour,
        custom: true,
        planningKind: roomFeature ? type : '',
        elevation: roomFeature ? elevation : (type === 'wall' ? 1450 : 0)
      };

      this.customProducts.set(String(id), product);
      this.productById.set(String(id), product);

      const unit = this.addUnit(id);
      if (!unit) {
        this.customProducts.delete(String(id));
        this.productById.delete(String(id));
        return;
      }

      if (roomFeature && type !== 'obstacle') {
        this.snapRoomFeatureToNearestWall(unit, { silent: true });
      }

      this.closeCustomModal();
      this.setStatus(roomFeature
        ? `${name} added to the room plan.`
        : `${name} added as a custom planning item.`);
    }

    readCatalog() {
      const script = this.root.querySelector('[data-catalog]');
      try {
        return JSON.parse(script?.textContent || '[]');
      } catch (error) {
        console.error('JD Kitchen Builder: invalid catalog JSON', error);
        return [];
      }
    }

    enrichProduct(item) {
      const sku = String(item.sku || '').trim().toUpperCase();
      const title = String(item.productTitle || '').trim();
      const variantTitle = String(item.variantTitle || '').trim();
      if (!sku) return null;

      const text = `${title} ${variantTitle} ${sku}`.toLowerCase();

      if (/^WCORNER/.test(sku)) {
        return {
          ...item,
          sku,
          category: 'wall',
          familyLabel: 'Wall L-corner',
          width: 600,
          depth: 600,
          height: 720,
          placeable: true,
          cornerShape: true,
          cornerArmDepth: 313,
          colour: this.detectColour(item, 'WCORNER'),
          doorColour: this.detectDoorColour(item, 'WCORNER')
        };
      }

      for (const rule of FAMILY_RULES) {
        const match = sku.match(rule.re);
        if (!match) continue;
        const width = Number(match[1]);
        if (!width || width < 100 || width > 1600) continue;
        return {
          ...item,
          sku,
          category: rule.category,
          familyLabel: rule.label,
          width,
          depth: rule.depth(width),
          height: rule.height(width),
          placeable: true,
          standalone: /^TS/.test(sku),
          cornerArmDepth: rule.cornerArmDepth ? rule.cornerArmDepth(width) : null,
          cornerShape: Boolean(rule.cornerShape || rule.category === 'corner'),
          colour: this.detectColour(item, match[0]),
          doorColour: this.detectDoorColour(item, match[0])
        };
      }

      const mmMatches = [...text.matchAll(/(\d{3,4})\s*mm/g)].map(m => Number(m[1]));
      const firstMm = mmMatches.find(n => n >= 100 && n <= 3000) || null;

      let category = 'accessory';
      let familyLabel = 'Accessory';

      if (/work\s*top|worktop/.test(text) || /WW$/.test(sku)) {
        category = 'worktop';
        familyLabel = 'Worktop';
      } else if (/end\s*panel|endpanel|decor\s*panel|ALBEND|ALWEND|GBEND|ALDEC/i.test(text)) {
        category = 'panel';
        familyLabel = /ALDEC|decor\s*panel/i.test(text) ? 'Tall decor panel' : 'End panel';
      } else if (/^F-ALB27(?:WGL|DG|[WGSNC])$/i.test(sku) || /plinth|kickboard|kick board/.test(text)) {
        category = 'plinth';
        familyLabel = 'Plinth / kickboard';
      } else if (/^F[-_]/.test(sku) || /door|drawer front|front panel/.test(text)) {
        category = 'front';
        familyLabel = 'Door / front';
      } else if (/handle|hinge|shelf|seal|strip|leg|feet|fixing|bracket/.test(text)) {
        category = 'accessory';
        familyLabel = 'Accessory';
      }

      const panelSpec = category === 'panel' ? this.endPanelSpec(sku) : null;

      return {
        ...item,
        sku,
        category,
        familyLabel,
        width: firstMm,
        depth: null,
        height: null,
        placeable: false,
        panelSpec,
        colour: this.detectColour(item, sku),
        doorColour: this.detectColour(item, sku)
      };
    }

    endPanelInfo(sku) {
      const raw = String(sku || '').trim().toUpperCase();
      if (!raw) return null;

      const compact = raw.replace(/[\s_-]+/g, '');
      const families = ['ALWEND', 'ALBEND', 'ALDEC', 'GBEND'];
      const family = families.find(prefix => compact.startsWith(prefix));
      if (!family) return null;

      let suffix = compact.slice(family.length);
      if (suffix.startsWith('PANEL')) suffix = suffix.slice(5);
      if (suffix.endsWith('PANEL')) suffix = suffix.slice(0, -5);

      const colourCodes = ['WGL', 'DG', 'G', 'W', 'S', 'N', 'C'];
      const colourCode = colourCodes.includes(suffix) ? suffix : '';
      if (suffix && !colourCode) return null;

      const colourByCode = {
        WGL: 'White Gloss',
        DG: 'Dark Grey',
        G: 'Grey',
        W: 'White',
        S: 'Sage Green',
        N: 'Navy Blue',
        C: 'Cashmere'
      };

      return {
        raw,
        family,
        colourCode,
        colour: colourByCode[colourCode] || null,
        canonical: family + colourCode
      };
    }

    normalizeEndPanelSku(sku) {
      return this.endPanelInfo(sku)?.canonical || String(sku || '').trim().toUpperCase();
    }

    endPanelFamily(sku) {
      return this.endPanelInfo(sku)?.family || null;
    }

    endPanelSpec(sku) {
      const family = this.endPanelFamily(sku);
      const specs = {
        GBEND: {
          family: 'GBEND',
          label: 'Standard base end panel',
          height: 820,
          depth: 578,
          thickness: 16
        },
        ALBEND: {
          family: 'ALBEND',
          label: 'Adjustable base end panel',
          height: 865,
          depth: 578,
          thickness: 16
        },
        ALWEND: {
          family: 'ALWEND',
          label: 'Wall end panel',
          height: 720,
          depth: 313,
          thickness: 16
        },
        ALDEC: {
          family: 'ALDEC',
          label: 'Tall decor panel',
          height: 2095,
          depth: 578,
          thickness: 16
        }
      };
      return family ? specs[family] || null : null;
    }

    endPanelCssColour(name) {
      const value = String(name || '').toLowerCase();
      if (value.includes('navy')) return '#24364f';
      if (value.includes('sage')) return '#a9b7a0';
      if (value.includes('cashmere')) return '#c8bdad';
      if (value.includes('dark grey')) return '#4a4d4e';
      if (value.includes('grey')) return '#92928f';
      if (value.includes('white')) return '#f7f6f2';
      return '#f7f6f2';
    }

    detectColour(item, familyCode) {
      const text = `${item.productTitle || ''} ${item.variantTitle || ''}`.toLowerCase();
      if (text.includes('white gloss')) return 'White Gloss';
      if (text.includes('dark grey') || text.includes('dark gray')) return 'Dark Grey';
      if (text.includes('sage')) return 'Sage Green';
      if (text.includes('navy')) return 'Navy Blue';
      if (text.includes('cashmere')) return 'Cashmere';
      if (text.includes('grey') || text.includes('gray')) return 'Grey';
      if (text.includes('white')) return 'White';

      const panelInfo = this.endPanelInfo(item?.sku || familyCode);
      if (panelInfo) return panelInfo.colour || 'White';

      let suffix = String(item.sku || '').toUpperCase().slice(String(familyCode || '').length);
      // Included-worktop SKUs finish with W; keep the preceding frontage
      // code for colour detection (DGW -> DG, WGLW -> WGL, WW -> W).
      if (/^(?:WGL|DG|G|W|S|N|C)W$/.test(suffix)) suffix = suffix.slice(0, -1);
      if (suffix.startsWith('WGL')) return 'White Gloss';
      if (suffix.startsWith('DG')) return 'Dark Grey';
      if (suffix.startsWith('S')) return 'Sage Green';
      if (suffix.startsWith('N')) return 'Navy Blue';
      if (suffix.startsWith('C')) return 'Cashmere';
      if (suffix.startsWith('G')) return 'Grey';
      if (suffix.startsWith('W')) return 'White';
      return 'Other';
    }

    detectDoorColour(item, familyCode) {
      const sku = String(item?.sku || '').trim().toUpperCase();
      const family = String(familyCode || '').trim().toUpperCase();
      let suffix = sku.startsWith(family) ? sku.slice(family.length) : '';
      suffix = suffix.replace(/[\s_-]+/g, '');

      // Included-worktop cabinet SKUs add a final W after the door colour,
      // e.g. WW, DGW and WGLW. Strip only that worktop marker.
      if (/^(?:WGL|DG|G|W|S|N|C)W$/.test(suffix)) {
        suffix = suffix.slice(0, -1);
      }

      if (suffix.startsWith('WGL')) return 'White Gloss';
      if (suffix.startsWith('DG')) return 'Dark Grey';
      if (suffix.startsWith('G')) return 'Grey';
      if (suffix.startsWith('W')) return 'White';
      if (suffix.startsWith('S')) return 'Sage Green';
      if (suffix.startsWith('N')) return 'Navy Blue';
      if (suffix.startsWith('C')) return 'Cashmere';

      // A placeable cabinet without a recognised front suffix is a carcass-only
      // choice. Its carcass can still be white, but it must not appear under
      // the White *door* filter.
      return 'No Doors';
    }

    productDoorColour(product) {
      return String(product?.doorColour || product?.colour || 'Other');
    }

    scheduleMobileFilterOverflow() {
      window.requestAnimationFrame(() => this.updateMobileFilterOverflow());
    }

    updateMobileFilterOverflow(target = null) {
      const containers = target
        ? [target]
        : [this.el.mobileColours, this.el.mobileWorktops, this.el.mobileHeights, this.el.mobileWidths].filter(Boolean);
      containers.forEach(container => {
        const row = container.closest('.jkb__mobile-filter-row');
        if (!row) return;
        const hasMoreRight = container.scrollWidth - container.scrollLeft - container.clientWidth > 3;
        row.classList.toggle('has-more-right', hasMoreRight);
      });
    }

    renderColourFilters() {
      const preferred = ['White', 'White Gloss', 'Grey', 'Dark Grey', 'Sage Green', 'Navy Blue', 'Cashmere', 'No Doors', 'Other'];
      const present = new Set(this.catalog.map(product => this.productDoorColour(product)));
      const colours = preferred.filter(colour => present.has(colour));
      const markup = [
        this.colourChip('all', 'All door colours'),
        ...colours.map(colour => this.colourChip(colour, colour))
      ].join('');
      if (this.el.colours) this.el.colours.innerHTML = markup;
      if (this.el.mobileColours) this.el.mobileColours.innerHTML = markup;
      this.scheduleMobileFilterOverflow();
    }

    colourChip(value, label) {
      const active = this.activeColour === value ? ' is-active' : '';
      return `<button type="button" class="jkb__filter-chip jkb__colour-chip${active}" data-colour="${this.escape(value)}">${this.escape(label)}</button>`;
    }

    renderWorktopFilters() {
      const choices = [
        ['all', 'All'],
        ['with', 'With worktop'],
        ['without', 'No worktop']
      ];
      const markup = choices.map(([value, label]) => this.worktopChip(value, label)).join('');
      if (this.el.worktops) this.el.worktops.innerHTML = markup;
      if (this.el.mobileWorktops) this.el.mobileWorktops.innerHTML = markup;
      this.scheduleMobileFilterOverflow();
    }

    worktopChip(value, label) {
      const active = this.activeWorktop === value ? ' is-active' : '';
      return `<button type="button" class="jkb__filter-chip jkb__worktop-chip${active}" data-worktop-filter="${this.escape(value)}">${this.escape(label)}</button>`;
    }

    productWorktopGroup(product) {
      if (!this.isWorktopUnit(product)) return 'not-applicable';
      return this.hasIncludedWorktop(product) ? 'with' : 'without';
    }

    productWorktopLabel(product) {
      const group = this.productWorktopGroup(product);
      if (group === 'with') return 'Worktop included';
      if (group === 'without') return 'No worktop';
      return '';
    }

    renderHeightFilters() {
      const choices = [
        ['all', 'All heights'],
        ['fixed-820', 'Fixed · 820'],
        ['adjustable-870', 'Adjustable · 870'],
        ['wall-720', 'Wall · 720'],
        ['tall-1950', 'Tall · 1950']
      ];
      const markup = choices.map(([value, label]) => this.heightChip(value, label)).join('');
      if (this.el.heights) this.el.heights.innerHTML = markup;
      if (this.el.mobileHeights) this.el.mobileHeights.innerHTML = markup;
      this.scheduleMobileFilterOverflow();
    }

    heightChip(value, label) {
      const active = this.activeHeight === value ? ' is-active' : '';
      return `<button type="button" class="jkb__filter-chip jkb__height-chip${active}" data-height-filter="${this.escape(value)}">${this.escape(label)}</button>`;
    }

    renderWidthFilters() {
      const choices = [
        ['all', 'All'],
        ['300', '300'],
        ['400', '400'],
        ['500', '500'],
        ['600', '600'],
        ['800', '800'],
        ['1000', '1000']
      ];
      const markup = choices.map(([value, label]) => this.widthChip(value, label)).join('');
      if (this.el.widths) this.el.widths.innerHTML = markup;
      if (this.el.mobileWidths) this.el.mobileWidths.innerHTML = markup;
      this.scheduleMobileFilterOverflow();
    }

    widthChip(value, label) {
      const active = this.activeWidth === value ? ' is-active' : '';
      const accessible = value === 'all' ? 'All widths' : `${label} mm wide`;
      return `<button type="button" class="jkb__filter-chip jkb__width-chip${active}" data-width-filter="${this.escape(value)}" aria-label="${this.escape(accessible)}">${this.escape(label)}${value === 'all' ? '' : '<span> mm</span>'}</button>`;
    }

    productHeightGroup(product) {
      if (!product?.placeable) return 'other';
      const sku = String(product.sku || '').toUpperCase();
      const height = Number(product.height || 0);
      if (/^AL/.test(sku) && height === 870) return 'adjustable-870';
      if (product.category === 'wall' && height === 720) return 'wall-720';
      if (product.category === 'tall' && height === 1950) return 'tall-1950';
      if (product.category !== 'wall' && product.category !== 'tall' && height === 820) return 'fixed-820';
      return `height-${height}`;
    }

    productRangeLabel(product) {
      const group = this.productHeightGroup(product);
      const labels = {
        'fixed-820': 'Fixed / freestanding · 820 mm',
        'adjustable-870': 'Adjustable · 870 mm',
        'wall-720': 'Wall · 720 mm',
        'tall-1950': 'Tall · 1950 mm'
      };
      return labels[group] || (product?.height ? `${product.height} mm high` : '');
    }

    resetFacetFilters(options = {}) {
      this.activeColour = 'all';
      this.activeWorktop = 'all';
      this.activeHeight = 'all';
      this.activeWidth = 'all';

      this.renderColourFilters();
      this.renderWorktopFilters();
      this.renderHeightFilters();
      this.renderWidthFilters();

      if (options.renderResults !== false) {
        this.renderCatalog();
        this.renderMobileSearchResults();
        if (this.el.quickResults && !this.el.quickResults.hidden) this.renderQuickResults(true);
      }
    }

    resetProductFilters() {
      this.resetFacetFilters({ renderResults: false });
      this.mobileSearchLayer = 'all';

      if (this.el.category) this.el.category.value = 'all';
      if (this.el.search) this.el.search.value = '';
      if (this.el.quickSearch) this.el.quickSearch.value = '';
      if (this.el.mobileSearchInput) this.el.mobileSearchInput.value = '';

      this.renderCatalog();
      this.renderMobileSearchResults();

      if (this.el.quickResults) {
        this.el.quickResults.hidden = true;
        this.el.quickResults.innerHTML = '';
      }

      this.setStatus('Search filters reset. All cabinet choices are shown.');
    }

    matchesActiveFilters(product) {
      if (this.activeColour !== 'all' && this.productDoorColour(product) !== this.activeColour) return false;
      if (this.activeWorktop !== 'all' && this.productWorktopGroup(product) !== this.activeWorktop) return false;
      if (this.activeHeight !== 'all' && this.productHeightGroup(product) !== this.activeHeight) return false;
      if (this.activeWidth !== 'all' && Number(product.width) !== Number(this.activeWidth)) return false;
      return true;
    }

    matchesProductSearch(product, query) {
      const tokens = String(query || '')
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (!tokens.length) return true;

      const searchable = [
        product.sku,
        product.productTitle,
        product.variantTitle,
        product.familyLabel,
        product.colour,
        this.productDoorColour(product),
        product.category,
        product.width,
        product.depth,
        product.height
      ].join(' ').toLowerCase();

      return tokens.every(token => searchable.includes(token));
    }

    openMobileSearch() {
      if (!(this.isMobile() || this.isCompactUi()) || !this.el.mobileSearch) return;
      if (!this.el.mobileSearch.hidden) return;

      this.mobileSearchScrollY = window.scrollY;
      this.mobileBodyStyle = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow
      };

      // Next 3.8 creates a header stacking context above section content on
      // iPhone. Portal the search sheet to <body> while it is open so the
      // close button and "View plan" bar cannot be trapped behind the theme
      // header or Shopify preview chrome.
      if (!this.mobileSearchHome) {
        this.mobileSearchHome = {
          parent: this.el.mobileSearch.parentElement,
          next: this.el.mobileSearch.nextSibling
        };
      }

      if (!this.mobileSearchPortal) {
        const portal = document.createElement('div');
        portal.className = 'jkb jkb__mobile-search-portal';
        this.mobileSearchPortal = portal;
      }

      const portal = this.mobileSearchPortal;
      ['is-compact-ui', 'is-extra-compact-ui', 'is-touch-ui', 'is-shopify-preview'].forEach(name => {
        portal.classList.toggle(name, this.root.classList.contains(name));
      });
      if (!portal.isConnected) document.body.appendChild(portal);
      if (this.el.mobileSearch.parentElement !== portal) portal.appendChild(this.el.mobileSearch);

      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.mobileSearchScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      this.el.mobileSearch.querySelectorAll('[data-mobile-search-fixed-back]').forEach(button => button.remove());
      this.el.mobileSearch.hidden = false;
      this.el.mobileSearch.setAttribute('aria-hidden', 'false');
      if (this.el.mobileAddedBar) this.el.mobileAddedBar.hidden = true;
      this.el.mobileSearchInput.value = this.el.quickSearch.value || '';
      this.renderMobileSearchResults();

      requestAnimationFrame(() => {
        this.updateMobileFilterOverflow();
        try {
          this.el.mobileSearchInput.focus({ preventScroll: true });
        } catch (_) {
          this.el.mobileSearchInput.focus();
        }
      });
    }

    closeMobileSearch() {
      if (!this.el.mobileSearch || this.el.mobileSearch.hidden) return;

      this.el.mobileSearchInput?.blur();
      this.el.mobileSearch.hidden = true;
      this.el.mobileSearch.setAttribute('aria-hidden', 'true');

      const query = this.el.mobileSearchInput?.value || '';
      this.el.quickSearch.value = query;
      this.el.search.value = query;
      this.renderCatalog();

      const old = this.mobileBodyStyle || {};
      document.body.style.position = old.position || '';
      document.body.style.top = old.top || '';
      document.body.style.width = old.width || '';
      document.body.style.overflow = old.overflow || '';

      const home = this.mobileSearchHome;
      if (home?.parent && this.el.mobileSearch.parentElement !== home.parent) {
        if (home.next && home.next.parentElement === home.parent) {
          home.parent.insertBefore(this.el.mobileSearch, home.next);
        } else {
          home.parent.appendChild(this.el.mobileSearch);
        }
      }
      if (this.mobileSearchPortal?.isConnected) this.mobileSearchPortal.remove();

      window.scrollTo(0, Number(this.mobileSearchScrollY) || 0);

      if (this.lastAddedId) {
        requestAnimationFrame(() => this.revealJustAdded(this.lastAddedId));
      }
    }

    renderMobileSearchResults() {
      if (!this.el.mobileSearchResults) return;
      const query = this.el.mobileSearchInput?.value || '';

      const hasQuery = String(query || '').trim().length > 0;
      const matches = this.catalog
        .filter(product => {
          if (!hasQuery && !this.productVisibleInLayerFilter(product, this.mobileSearchLayer)) return false;
          if (!hasQuery && !this.matchesActiveFilters(product)) return false;
          return this.matchesProductSearch(product, query);
        });

      if (this.el.mobileSearchCount) {
        const suffix = this.mobileSearchLayer === 'wall' ? 'wall units' : this.mobileSearchLayer === 'floor' ? 'floor units' : 'matches';
        this.el.mobileSearchCount.textContent = `${matches.length} ${suffix}`;
      }

      this.el.mobileSearchLayers?.querySelectorAll('[data-mobile-layer]').forEach(item => {
        item.classList.toggle('is-active', item.dataset.mobileLayer === this.mobileSearchLayer);
      });

      this.el.mobileSearchResults.innerHTML = matches.length ? matches.map(product => `
        <article class="jkb__mobile-search-row">
          <div class="jkb__mobile-search-thumb">
            ${product.image
              ? `<img src="${this.escape(product.image)}" alt="" loading="lazy">`
              : `<span aria-hidden="true">▣</span>`}
          </div>
          <div class="jkb__mobile-search-copy">
            <strong>${this.escape(product.productTitle)}</strong>
            <span>${this.escape(product.sku)} · ${this.escape(product.familyLabel)}</span>
            <small>${product.placeable
              ? `W${product.width} · D${product.depth} · H${product.height}`
              : product.panelSpec
                ? `H${product.panelSpec.height} · D${product.panelSpec.depth} · T${product.panelSpec.thickness}`
                : 'Basket item'} · ${this.escape(product.placeable ? 'Door: ' + this.productDoorColour(product) : product.colour)}</small>
            ${product.placeable ? `<em class="jkb__range-badge">${this.escape(this.productRangeLabel(product))}</em>` : ''}
            ${!product.available ? '<em class="jkb__stock-status">Out of stock · planning available</em>' : ''}
            ${this.productWorktopLabel(product) ? `<em class="jkb__worktop-badge is-${this.productWorktopGroup(product)}">${this.escape(this.productWorktopLabel(product))}</em>` : ''}
          </div>
          <div class="jkb__mobile-search-side">
            <b>${this.money(product.price)}</b>
            <button
              type="button"
              data-mobile-add="${product.id}"
              ${product.available || product.placeable ? '' : 'disabled aria-disabled="true"'}
            >${product.available
                ? 'Add'
                : product.placeable
                  ? 'Plan'
                  : 'Out of stock'}</button>
          </div>
        </article>
      `).join('') : '<p class="jkb__mobile-search-empty">No matching products.</p>';
    }

    renderQuickResults(forceOpen = false) {
      if (!this.el.quickSearch || !this.el.quickResults) return;

      const query = this.el.quickSearch.value.trim();
      if (!query && !forceOpen) {
        this.el.quickResults.hidden = true;
        return;
      }

      const hasQuery = String(query || '').trim().length > 0;
      const matches = this.catalog
        .filter(product => {
          if (!hasQuery && !this.productVisibleInLayerFilter(product, this.activeLayer)) return false;
          if (!hasQuery && !this.matchesActiveFilters(product)) return false;
          return this.matchesProductSearch(product, query);
        })
        .slice(0, 10);

      this.el.quickResults.innerHTML = matches.length ? matches.map(product => `
        <div class="jkb__quick-result" data-quick-product-id="${product.id}">
          <span class="jkb__quick-drag-item" aria-hidden="true">${product.placeable ? '⋮⋮' : ''}</span>
          <span class="jkb__quick-thumb">${product.image ? `<img src="${this.escape(product.image)}" alt="" loading="lazy">` : ''}</span>
          <span class="jkb__quick-result-copy">
            <strong>${this.escape(product.productTitle)}</strong>
            <small>${this.escape(product.sku)} · ${this.escape(product.familyLabel)}</small>
            <small>${product.placeable
              ? `W${product.width} · D${product.depth} · H${product.height}`
              : product.panelSpec
                ? `H${product.panelSpec.height} · D${product.panelSpec.depth} · T${product.panelSpec.thickness}`
                : 'Basket item'}</small>
            ${product.placeable ? `<em class="jkb__range-badge">${this.escape(this.productRangeLabel(product))}</em>` : ''}
            ${!product.available ? '<em class="jkb__stock-status">Out of stock · planning available</em>' : ''}
            ${this.productWorktopLabel(product) ? `<em class="jkb__worktop-badge is-${this.productWorktopGroup(product)}">${this.escape(this.productWorktopLabel(product))}</em>` : ''}
          </span>
          <span class="jkb__quick-result-side">
            <b>${this.money(product.price)}</b>
            <button
              type="button"
              class="jkb__quick-add"
              data-quick-add="${product.id}"
              ${product.available || product.placeable ? '' : 'disabled aria-disabled="true"'}
            >${product.available
                ? 'Add'
                : product.placeable
                  ? 'Plan'
                  : 'Out of stock'}</button>
          </span>
        </div>
      `).join('') : '<p class="jkb__quick-empty">No matching products.</p>';

      this.el.quickResults.hidden = false;
      requestAnimationFrame(() => this.positionQuickResults());
    }

    positionSearchForTyping() {
      if (!this.el.quickSearchWrap || !window.matchMedia('(max-width: 700px)').matches) return;
      if (!this.el.quickSearchWrap.classList.contains('is-searching')) return;

      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const trayHeight = this.el.quickSearchWrap.offsetHeight || 48;
      const top = Math.max(viewportTop + 8, viewportTop + viewportHeight - trayHeight - 10);
      this.el.quickSearchWrap.style.setProperty('--jkb-search-focus-top', top + 'px');
    }

    positionQuickResults() {
      if (!this.el.quickResults || this.el.quickResults.hidden) return;
      if (!window.matchMedia('(max-width: 700px)').matches) {
        this.el.quickResults.removeAttribute('data-mobile-positioned');
        return;
      }

      const trayRect = this.el.quickSearchWrap.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportBottom = viewportTop + viewportHeight;
      const viewportWidth = viewport?.width || window.innerWidth;
      const toolbar = this.root.querySelector('.jkb__workspace-toolbar');
      const toolbarRect = toolbar?.getBoundingClientRect();
      let safeTop = viewportTop + 8;
      if (toolbarRect && toolbarRect.bottom > 0 && toolbarRect.top < viewportHeight) {
        safeTop = Math.max(safeTop, Math.min(viewportHeight - 120, toolbarRect.bottom + 8));
      }

      const safeBottom = 8;
      const gap = 7;
      const above = Math.max(0, trayRect.top - safeTop - gap);
      const below = Math.max(0, viewportBottom - trayRect.bottom - safeBottom - gap);
      const useBelow = below >= 180 || below > above;
      const maxHeight = Math.max(120, Math.min(390, useBelow ? below : above));
      const left = Math.max(8, Math.min(trayRect.left, viewportWidth - 170));
      const right = Math.max(8, viewportWidth - Math.min(viewportWidth - 8, trayRect.right));

      this.el.quickResults.setAttribute('data-mobile-positioned', 'true');
      this.el.quickResults.style.setProperty('left', left + 'px', 'important');
      this.el.quickResults.style.setProperty('right', right + 'px', 'important');
      this.el.quickResults.style.setProperty('max-height', maxHeight + 'px', 'important');

      if (useBelow) {
        this.el.quickResults.style.setProperty('top', Math.min(viewportBottom - 120, trayRect.bottom + gap) + 'px', 'important');
        this.el.quickResults.style.setProperty('bottom', 'auto', 'important');
      } else {
        this.el.quickResults.style.setProperty('top', 'auto', 'important');
        this.el.quickResults.style.setProperty('bottom', Math.max(safeBottom, window.innerHeight - trayRect.top + gap) + 'px', 'important');
      }
    }

    renderCatalog() {
      const query = this.el.search.value.trim().toLowerCase();
      const category = this.el.category.value;

      const hasQuery = String(query || '').trim().length > 0;
      const filtered = this.catalog.filter(product => {
        if (!hasQuery && !this.productVisibleInLayerFilter(product, this.activeLayer)) return false;
        if (!hasQuery && category !== 'all' && product.category !== category) return false;
        if (!hasQuery && !this.matchesActiveFilters(product)) return false;

        return this.matchesProductSearch(product, query);
      });

      const layerLabel = this.activeLayer === 'wall'
        ? 'wall units'
        : this.activeLayer === 'floor'
          ? 'floor units'
          : 'products';
      this.el.productCount.textContent = `${filtered.length} ${layerLabel}`;

      this.el.products.innerHTML = filtered.length ? filtered.map(product => {
        const disabled = !product.available && !product.placeable;
        const variant = product.variantTitle && product.variantTitle !== 'Default Title'
          ? ` · ${product.variantTitle}`
          : '';
        const dims = product.placeable
          ? `W${product.width} × D${product.depth} × H${product.height} mm`
          : product.panelSpec
            ? `H${product.panelSpec.height} × D${product.panelSpec.depth} × T${product.panelSpec.thickness} mm`
            : (product.width ? `${product.width} mm` : 'Basket item');
        const isPlinth = product.category === 'plinth';
        const hasAlCabinet = !isPlinth || this.units.some(unit => {
          const cabinet = this.product(unit);
          return cabinet &&
            !cabinet.planningKind &&
            cabinet.category !== 'wall' &&
            /^AL/.test(String(cabinet.sku || '').toUpperCase());
        });
        const actionDisabled = disabled || (isPlinth && !hasAlCabinet);
        // Catalogue buttons only add an item. Physical positioning is a
        // separate step shown after an end panel has been added, so avoid
        // mixing "Place" and "Add to basket" in the same product list.
        const action = product.available
          ? 'Add'
          : product.placeable
            ? 'Plan'
            : isPlinth && !hasAlCabinet
              ? 'AL only'
              : 'Out of stock';
        const actionLabel = product.placeable
          ? `Add ${product.sku} to the kitchen plan`
          : `Add ${product.sku} as an extra item`;
        const finishLabel = product.placeable
          ? `Door: ${this.productDoorColour(product)}`
          : product.colour;

        return `
          <article
            class="jkb__product${product.placeable ? '' : ' jkb__product--extra'}"
            draggable="false"
            data-product-id="${product.id}"
            data-placeable="${product.placeable ? 'true' : 'false'}"
            aria-disabled="${disabled ? 'true' : 'false'}"
            title="${this.escape(product.productTitle + variant)}"
          >
            <span class="jkb__product-main">
              <strong class="jkb__product-title">${this.escape(product.productTitle)}</strong>
              <small class="jkb__product-meta">${this.escape(product.sku)}${this.escape(variant)}</small>
              <small>${this.escape(product.familyLabel)} · ${dims} · ${this.escape(finishLabel)}</small>
              ${product.placeable ? `<em class="jkb__range-badge">${this.escape(this.productRangeLabel(product))}</em>` : ''}
              ${!product.available ? '<em class="jkb__stock-status">Out of stock · planning available</em>' : ''}
              ${isPlinth && !hasAlCabinet ? '<em class="jkb__stock-status">AL adjustable cabinets only</em>' : ''}
              ${this.productWorktopLabel(product) ? `<em class="jkb__worktop-badge is-${this.productWorktopGroup(product)}">${this.escape(this.productWorktopLabel(product))}</em>` : ''}
            </span>
            <span class="jkb__product-side">
              <strong class="jkb__price">${this.money(product.price)}</strong>
              <button
                type="button"
                class="jkb__product-add"
                data-catalog-add="${product.id}"
                aria-label="${this.escape(actionLabel)}"
                title="${this.escape(actionLabel)}"
                ${actionDisabled ? 'disabled aria-disabled="true"' : ''}
              >${action}</button>
            </span>
          </article>
        `;
      }).join('') : '<p class="jkb__summary-empty">No matching products.</p>';
    }

    normaliseRoomShape(raw = null) {
      const source = raw && typeof raw === 'object' ? raw : {};
      const types = ['rectangle', 'chamfer', 'notch'];
      const corners = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];
      const type = types.includes(String(source.type || '')) ? String(source.type) : 'rectangle';
      const corner = corners.includes(String(source.corner || '')) ? String(source.corner) : 'top-right';

      const maxX = Math.max(100, Math.min(3000, Math.round(this.roomWidth * 0.45)));
      const maxY = Math.max(100, Math.min(3000, Math.round(this.roomDepth * 0.45)));
      let cutX = this.clamp(Math.round(Number(source.cutX) || 500), 100, maxX);
      let cutY = this.clamp(Math.round(Number(source.cutY) || 500), 100, maxY);

      if (type === 'chamfer') {
        const size = Math.min(cutX, cutY, maxX, maxY);
        cutX = size;
        cutY = size;
      }

      return { type, corner, cutX, cutY };
    }

    roomShapePolygon(shape = this.roomShape) {
      const s = this.normaliseRoomShape(shape);
      const w = this.roomWidth;
      const d = this.roomDepth;
      const x = s.cutX;
      const y = s.cutY;

      if (s.type === 'rectangle') {
        return [[0, 0], [w, 0], [w, d], [0, d]];
      }

      if (s.type === 'chamfer') {
        const polygons = {
          'top-left': [[x, 0], [w, 0], [w, d], [0, d], [0, y]],
          'top-right': [[0, 0], [w - x, 0], [w, y], [w, d], [0, d]],
          'bottom-right': [[0, 0], [w, 0], [w, d - y], [w - x, d], [0, d]],
          'bottom-left': [[0, 0], [w, 0], [w, d], [x, d], [0, d - y]]
        };
        return polygons[s.corner];
      }

      const polygons = {
        'top-left': [[x, 0], [w, 0], [w, d], [0, d], [0, y], [x, y]],
        'top-right': [[0, 0], [w - x, 0], [w - x, y], [w, y], [w, d], [0, d]],
        'bottom-right': [[0, 0], [w, 0], [w, d - y], [w - x, d - y], [w - x, d], [0, d]],
        'bottom-left': [[0, 0], [w, 0], [w, d], [x, d], [x, d - y], [0, d - y]]
      };
      return polygons[s.corner];
    }

    rectFitsRoomShape(rect, shape = this.roomShape) {
      const r = rect || {};
      const left = Number(r.x) || 0;
      const top = Number(r.y) || 0;
      const right = left + (Number(r.width) || 0);
      const bottom = top + (Number(r.depth) || 0);
      const epsilon = 0.5;

      if (
        left < -epsilon ||
        top < -epsilon ||
        right > this.roomWidth + epsilon ||
        bottom > this.roomDepth + epsilon
      ) return false;

      const s = this.normaliseRoomShape(shape);
      if (s.type === 'rectangle') return true;

      const x = s.cutX;
      const y = s.cutY;

      if (s.type === 'notch') {
        const cutouts = {
          'top-left': { x: 0, y: 0, width: x, depth: y },
          'top-right': { x: this.roomWidth - x, y: 0, width: x, depth: y },
          'bottom-right': { x: this.roomWidth - x, y: this.roomDepth - y, width: x, depth: y },
          'bottom-left': { x: 0, y: this.roomDepth - y, width: x, depth: y }
        };
        const cut = cutouts[s.corner];
        const overlapX = Math.min(right, cut.x + cut.width) - Math.max(left, cut.x);
        const overlapY = Math.min(bottom, cut.y + cut.depth) - Math.max(top, cut.y);
        return overlapX <= epsilon || overlapY <= epsilon;
      }

      let score = Infinity;
      if (s.corner === 'top-left') score = left / x + top / y;
      else if (s.corner === 'top-right') score = (this.roomWidth - right) / x + top / y;
      else if (s.corner === 'bottom-right') score = (this.roomWidth - right) / x + (this.roomDepth - bottom) / y;
      else if (s.corner === 'bottom-left') score = left / x + (this.roomDepth - bottom) / y;
      return score >= 1 - 0.001;
    }

    unitFitsRoomShape(unit) {
      return Boolean(unit && this.rectFitsRoomShape(this.rect(unit)));
    }

    applyRoomShapeVisual() {
      if (!this.el.room) return;
      const shape = this.normaliseRoomShape(this.roomShape);
      const points = this.roomShapePolygon(shape);
      const cssPoints = points
        .map(([x, y]) => `${((x / this.roomWidth) * 100).toFixed(3)}% ${((y / this.roomDepth) * 100).toFixed(3)}%`)
        .join(', ');

      this.el.room.classList.toggle('has-custom-shape', shape.type !== 'rectangle');
      if (shape.type === 'rectangle') {
        this.el.room.style.clipPath = '';
        this.el.room.style.webkitClipPath = '';
      } else {
        const clip = `polygon(${cssPoints})`;
        this.el.room.style.clipPath = clip;
        this.el.room.style.webkitClipPath = clip;
      }

      let svg = this.el.room.querySelector('[data-room-shape-outline]');
      if (!svg) {
        svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('data-room-shape-outline', '');
        svg.classList.add('jkb__room-shape-outline');
        svg.setAttribute('aria-hidden', 'true');
        this.el.room.appendChild(svg);
      }

      svg.hidden = shape.type === 'rectangle';
      svg.setAttribute('viewBox', `0 0 ${this.roomWidth} ${this.roomDepth}`);
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.innerHTML = `<polygon points="${points.map(point => point.join(',')).join(' ')}" vector-effect="non-scaling-stroke"></polygon>`;
    }

    openRoomShapeEditor() {
      if (!this.el.roomShapeModal) return;
      this.roomShapeDraft = { ...this.normaliseRoomShape(this.roomShape) };
      this.renderRoomShapeEditor();
      this.el.roomShapeModal.hidden = false;
      this.el.roomShapeModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('jkb-room-shape-open');
    }

    closeRoomShapeEditor() {
      if (!this.el.roomShapeModal) return;
      this.el.roomShapeModal.hidden = true;
      this.el.roomShapeModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('jkb-room-shape-open');
      this.roomShapeDraft = null;
    }

    renderRoomShapeEditor() {
      if (!this.roomShapeDraft) return;
      const draft = this.normaliseRoomShape(this.roomShapeDraft);
      this.roomShapeDraft = { ...draft };

      this.el.roomShapeTypes?.querySelectorAll('[data-room-shape-type]').forEach(button => {
        button.classList.toggle('is-active', button.dataset.roomShapeType === draft.type);
        button.setAttribute('aria-pressed', button.dataset.roomShapeType === draft.type ? 'true' : 'false');
      });
      this.el.roomShapeCorners?.querySelectorAll('[data-room-shape-corner]').forEach(button => {
        button.classList.toggle('is-active', button.dataset.roomShapeCorner === draft.corner);
        button.setAttribute('aria-pressed', button.dataset.roomShapeCorner === draft.corner ? 'true' : 'false');
      });

      if (this.el.roomShapeChamferField) this.el.roomShapeChamferField.hidden = draft.type !== 'chamfer';
      if (this.el.roomShapeNotchFields) this.el.roomShapeNotchFields.hidden = draft.type !== 'notch';
      if (this.el.roomShapeCorners) {
        this.el.roomShapeCorners.hidden = draft.type === 'rectangle';
        const cornerBlock = this.el.roomShapeCorners.closest('.jkb__room-shape-corner-block');
        if (cornerBlock) cornerBlock.hidden = draft.type === 'rectangle';
      }

      if (this.el.roomShapeCut && document.activeElement !== this.el.roomShapeCut) {
        this.el.roomShapeCut.value = Math.round(draft.cutX);
      }
      if (this.el.roomShapeCutX && document.activeElement !== this.el.roomShapeCutX) {
        this.el.roomShapeCutX.value = Math.round(draft.cutX);
      }
      if (this.el.roomShapeCutY && document.activeElement !== this.el.roomShapeCutY) {
        this.el.roomShapeCutY.value = Math.round(draft.cutY);
      }

      this.updateRoomShapePreview();
    }

    updateRoomShapePreview() {
      if (!this.roomShapeDraft) return;
      const draft = this.normaliseRoomShape(this.roomShapeDraft);
      const points = this.roomShapePolygon(draft);
      if (this.el.roomShapePreview) {
        this.el.roomShapePreview.setAttribute('viewBox', `0 0 ${this.roomWidth} ${this.roomDepth}`);
        this.el.roomShapePreview.innerHTML = `
          <polygon class="jkb__room-shape-preview-floor" points="${points.map(point => point.join(',')).join(' ')}"></polygon>
          <polyline class="jkb__room-shape-preview-wall" points="${points.concat([points[0]]).map(point => point.join(',')).join(' ')}"></polyline>
        `;
      }

      if (this.el.roomShapeSummary) {
        const cornerLabels = {
          'top-left': 'top-left',
          'top-right': 'top-right',
          'bottom-right': 'bottom-right',
          'bottom-left': 'bottom-left'
        };
        this.el.roomShapeSummary.textContent = draft.type === 'rectangle'
          ? `Rectangle · ${this.roomWidth} × ${this.roomDepth} mm`
          : draft.type === 'chamfer'
            ? `45° cut · ${cornerLabels[draft.corner]} · ${draft.cutX} mm`
            : `Corner recess · ${cornerLabels[draft.corner]} · ${draft.cutX} × ${draft.cutY} mm`;
      }
    }

    applyRoomShapeEditor() {
      if (!this.roomShapeDraft) return;
      const next = this.normaliseRoomShape(this.roomShapeDraft);
      const previous = { ...this.normaliseRoomShape(this.roomShape) };
      const positions = new Map(this.units.map(unit => [unit.uid, { x: unit.x, y: unit.y }]));

      this.roomShape = next;
      const affected = this.units.filter(unit => !this.unitFitsRoomShape(unit));

      if (
        affected.length &&
        !window.confirm(`${affected.length} item${affected.length === 1 ? '' : 's'} sit inside the corner being removed. Move ${affected.length === 1 ? 'it' : 'them'} to the nearest free space?`)
      ) {
        this.roomShape = previous;
        return;
      }

      let failed = false;
      for (const unit of affected) {
        const open = this.findOpenPosition(unit);
        if (!open) {
          failed = true;
          break;
        }
        Object.assign(unit, open);
      }

      if (failed) {
        this.roomShape = previous;
        this.units.forEach(unit => {
          const old = positions.get(unit.uid);
          if (old) Object.assign(unit, old);
        });
        this.setStatus('That room shape leaves too little valid space for the current items. Move or remove cabinets first.');
        return;
      }

      this.closeRoomShapeEditor();
      this.renderAll();
      this.save();
      this.setStatus(next.type === 'rectangle'
        ? 'Room returned to a rectangle.'
        : next.type === 'chamfer'
          ? `45° corner cut applied at ${next.corner}.`
          : `Corner recess applied at ${next.corner}.`);
    }

    applyRoomDimensions() {
      const width = this.clamp(Number(this.el.widthInput.value), 1000, 15000);
      const depth = this.clamp(Number(this.el.depthInput.value), 1000, 15000);
      if (!width || !depth) return;

      const previousWidth = this.roomWidth;
      const previousDepth = this.roomDepth;
      const anchors = new Map();
      const outOfBounds = [];

      this.units.forEach(unit => {
        const d = this.unitDimensions(unit);
        anchors.set(unit.uid, {
          left: Math.abs(unit.x) <= 1,
          right: Math.abs(previousWidth - (unit.x + d.width)) <= 1,
          top: Math.abs(unit.y) <= 1,
          bottom: Math.abs(previousDepth - (unit.y + d.depth)) <= 1
        });
        if (unit.x + d.width > width || unit.y + d.depth > depth) {
          outOfBounds.push(unit);
        }
      });

      if (
        outOfBounds.length &&
        !window.confirm('Some items are outside the new room size. They will stay attached to their wall and be repositioned where possible. Continue?')
      ) return;

      this.roomWidth = width;
      this.roomDepth = depth;
      this.roomShape = this.normaliseRoomShape(this.roomShape);

      // Preserve exact wall attachment when a room grows or shrinks. Without
      // this, a fitted run can be left floating where the old wall used to be.
      this.units.forEach(unit => {
        const d = this.unitDimensions(unit);
        const maxX = Math.max(0, width - d.width);
        const maxY = Math.max(0, depth - d.depth);
        const anchor = anchors.get(unit.uid) || {};

        unit.x = this.clamp(unit.x, 0, maxX);
        unit.y = this.clamp(unit.y, 0, maxY);
        if (anchor.left) unit.x = 0;
        else if (anchor.right) unit.x = maxX;
        if (anchor.top) unit.y = 0;
        else if (anchor.bottom) unit.y = maxY;
      });

      // Keep doors, windows and radiators wall-aligned after the resize.
      this.units.forEach(unit => {
        const product = this.product(unit);
        if (product?.planningKind && product.planningKind !== 'obstacle') {
          this.snapRoomFeatureToNearestWall(unit, {
            render: false,
            save: false,
            silent: true
          });
        }
      });

      // A resized shaped room can leave an item inside a removed corner.
      // Relocate it to the first valid open position before resolving overlaps.
      for (const unit of this.units) {
        if (this.unitFitsRoomShape(unit)) continue;
        const open = this.findOpenPosition(unit);
        if (open) Object.assign(unit, open);
      }

      // A smaller room can push several cabinets onto the same coordinates.
      // Relocate only genuine collisions; wall units remain allowed above bases.
      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product || product.planningKind || !this.hasCollision(unit)) continue;

        if (product.cornerShape || product.category === 'corner') {
          const d = this.unitDimensions(unit);
          const candidates = [
            { x: 0, y: 0 },
            { x: Math.max(0, width - d.width), y: 0 },
            { x: 0, y: Math.max(0, depth - d.depth) },
            { x: Math.max(0, width - d.width), y: Math.max(0, depth - d.depth) }
          ];
          const clearCorner = candidates.find(candidate => {
            const before = { x: unit.x, y: unit.y };
            unit.x = candidate.x;
            unit.y = candidate.y;
            const clear = !this.hasCollision(unit);
            if (!clear) Object.assign(unit, before);
            return clear;
          });
          if (clearCorner) {
            unit.x = clearCorner.x;
            unit.y = clearCorner.y;
            unit.cornerTurn = 0;
            continue;
          }
        }

        const open = this.findOpenPosition(unit);
        if (open) Object.assign(unit, open);
      }

      this.renderAll();
      this.save();

      const unresolved = this.cabinetCollisionPairs().length + this.roomFeatureConflicts().length;
      if (unresolved) {
        this.setStatus(`Room updated. ${unresolved} overlap${unresolved === 1 ? '' : 's'} still need attention in Smart Fit.`);
      } else {
        this.setStatus('Room dimensions updated. Wall-fitted items stayed aligned.');
      }
    }

    addUnit(productId, rawX = null, rawY = null) {
      const product = this.productById.get(String(productId));
      if (!product || !product.placeable) return;

      const unit = {
        uid: `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
        productId: String(product.id),
        x: 0,
        y: 0,
        rotation: 0,
        cornerTurn: 0,
        doorOpen: false,
        hingeSide: 'left',
        locked: false,
        wallLinked: true,
        frontEdgeOverride: null,
        serviceGapOverride: null
      };

      if (rawX !== null && rawY !== null) {
        const position = this.snapPosition(unit, rawX, rawY);
        unit.x = position.x;
        unit.y = position.y;
        if (this.hasCollision(unit)) {
          const open = this.findOpenPosition(unit);
          if (!open) {
            this.setStatus('There is no free space for that cabinet.');
            return;
          }
          Object.assign(unit, open);
        }
      } else {
        const open = this.findOpenPosition(unit);
        if (!open) {
          this.setStatus('There is no free space for that cabinet.');
          return;
        }
        Object.assign(unit, open);
      }

      this.units.push(unit);
      this.justAddedIds.add(unit.uid);
      this.lastAddedId = unit.uid;
      if (!this.unitVisibleInLayer(unit)) this.setLayer('all');
      this.select(unit.uid);
      this.renderAll();
      this.save();

      requestAnimationFrame(() => {
        if (!this.el.mobileSearch || this.el.mobileSearch.hidden) this.revealJustAdded(unit.uid);
      });

      if (!product.available && !product.custom) {
        this.setStatus(`${product.sku} is out of stock but has been kept in the plan. Checkout will remain unavailable until it is back in stock or removed.`);
      }

      return unit;
    }

    addExtra(productId) {
      const product = this.productById.get(String(productId));
      if (!product || !product.available) return;

      if (product.category === 'plinth') {
        const hasCompatibleAlCabinet = this.units.some(unit => {
          const cabinet = this.product(unit);
          return cabinet &&
            !cabinet.planningKind &&
            cabinet.category !== 'wall' &&
            /^AL/.test(String(cabinet.sku || '').toUpperCase());
        });
        if (!hasCompatibleAlCabinet) {
          this.setStatus('The optional 2.7 m colour-matched plinth is only for AL adjustable floor cabinets. A white plinth is already supplied with the cabinets.');
          return;
        }
      }

      const key = String(product.id);
      this.extras[key] = (Number(this.extras[key]) || 0) + 1;
      const isEndPanel = Boolean(this.endPanelSpec(product.sku));

      if (isEndPanel) {
        this.renderSelection();
        this.sync3D();
      }
      this.renderSummary();
      this.save();

      this.setStatus(isEndPanel
        ? `${product.sku} added. Select a compatible cabinet and choose which exposed end to place it on.`
        : `${product.sku} added to your kitchen.`);
    }

    removeExtra(productId) {
      const product = this.productById.get(String(productId));
      const key = String(productId);
      const qty = Number(this.extras[key]) || 0;
      if (qty <= 1) delete this.extras[key];
      else this.extras[key] = qty - 1;

      if (product && this.endPanelSpec(product.sku)) {
        this.trimEndPanelAssignmentsForProduct(key);
        if (String(this.activeEndPanelProductId || '') === key && this.availableEndPanelQuantity(key) < 1) {
          this.activeEndPanelProductId = null;
        }
        this.renderUnits();
        this.renderSelection();
        this.sync3D();
      }
      this.renderSummary();
      this.save();
    }

    findOpenPosition(unit) {
      const product = this.product(unit);
      if (!product) return null;

      if (product.category === 'wall') {
        const overlay = this.findWallOverlayPosition(unit);
        if (overlay) return overlay;
      }

      const d = this.unitDimensions(unit);
      const preferredY = Math.max(0, this.roomDepth - d.depth);

      const rows = [];
      for (let y = preferredY; y <= this.roomDepth - d.depth; y += this.grid) rows.push(y);
      for (let y = 0; y < preferredY; y += this.grid) rows.push(y);

      for (const y of rows) {
        for (let x = 0; x <= this.roomWidth - d.width; x += this.grid) {
          const candidate = { ...unit, x, y };
          if (!this.hasCollision(candidate)) return { x, y, rotation: candidate.rotation };
        }
      }
      return null;
    }

    findWallOverlayPosition(unit) {
      const product = this.product(unit);
      if (!product || product.category !== 'wall') return null;

      const tolerance = Math.max(this.grid, 75);
      const floorUnits = this.units
        .filter(other => {
          const p = this.product(other);
          return p && p.category !== 'wall' && p.category !== 'tall';
        })
        .sort((a, b) => {
          const ar = this.rect(a);
          const br = this.rect(b);
          const aBottom = Math.abs((ar.y + ar.depth) - this.roomDepth) <= tolerance ? 0 : 1;
          const bBottom = Math.abs((br.y + br.depth) - this.roomDepth) <= tolerance ? 0 : 1;
          return aBottom - bBottom || ar.y - br.y || ar.x - br.x;
        });

      for (const floorUnit of floorUnits) {
        const floorRect = this.rect(floorUnit);

        if (Math.abs((floorRect.y + floorRect.depth) - this.roomDepth) <= tolerance) {
          const test = { ...unit, rotation: 0 };
          const d = this.unitDimensions(test);
          const candidate = {
            ...test,
            x: this.clamp(this.roundGrid(floorRect.x), 0, Math.max(0, this.roomWidth - d.width)),
            y: Math.max(0, this.roomDepth - d.depth)
          };
          if (!this.hasCollision(candidate)) return { x: candidate.x, y: candidate.y, rotation: candidate.rotation };
        }

        if (Math.abs(floorRect.y) <= tolerance) {
          const test = { ...unit, rotation: 0 };
          const d = this.unitDimensions(test);
          const candidate = {
            ...test,
            x: this.clamp(this.roundGrid(floorRect.x), 0, Math.max(0, this.roomWidth - d.width)),
            y: 0
          };
          if (!this.hasCollision(candidate)) return { x: candidate.x, y: candidate.y, rotation: candidate.rotation };
        }

        if (Math.abs(floorRect.x) <= tolerance) {
          const test = { ...unit, rotation: 90 };
          const d = this.unitDimensions(test);
          const candidate = {
            ...test,
            x: 0,
            y: this.clamp(this.roundGrid(floorRect.y), 0, Math.max(0, this.roomDepth - d.depth))
          };
          if (!this.hasCollision(candidate)) return { x: candidate.x, y: candidate.y, rotation: candidate.rotation };
        }

        if (Math.abs((floorRect.x + floorRect.width) - this.roomWidth) <= tolerance) {
          const test = { ...unit, rotation: 90 };
          const d = this.unitDimensions(test);
          const candidate = {
            ...test,
            x: Math.max(0, this.roomWidth - d.width),
            y: this.clamp(this.roundGrid(floorRect.y), 0, Math.max(0, this.roomDepth - d.depth))
          };
          if (!this.hasCollision(candidate)) return { x: candidate.x, y: candidate.y, rotation: candidate.rotation };
        }
      }

      const d = this.unitDimensions(unit);
      const y = Math.max(0, this.roomDepth - d.depth);
      for (let x = 0; x <= this.roomWidth - d.width; x += this.grid) {
        const candidate = { ...unit, x, y };
        if (!this.hasCollision(candidate)) return { x, y, rotation: candidate.rotation };
      }

      return null;
    }

    onSearchDragMove(e) {
      if (!this.searchDrag || e.pointerId !== this.searchDrag.pointerId) return;
      if (e.cancelable) e.preventDefault();

      const searchSurface = this.viewMode === '3d' ? this.el.view3d : this.el.stage;
      const stageRect = searchSurface.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(this.searchDrag.width, stageRect.width - margin * 2);
      const height = this.el.quickSearchWrap.offsetHeight;
      const left = Math.max(margin, Math.min(stageRect.width - width - margin, e.clientX - stageRect.left - this.searchDrag.offsetX));
      const top = Math.max(margin, Math.min(stageRect.height - height - margin, e.clientY - stageRect.top - this.searchDrag.offsetY));

      if (this.viewMode === '3d') {
        // 3D layout uses !important positioning rules, so the drag position
        // must win with inline !important values as well.
        this.el.quickSearchWrap.style.setProperty('left', `${left}px`, 'important');
        this.el.quickSearchWrap.style.setProperty('right', 'auto', 'important');
        this.el.quickSearchWrap.style.setProperty('top', `${top}px`, 'important');
        this.el.quickSearchWrap.style.setProperty('bottom', 'auto', 'important');
        this.el.quickSearchWrap.style.setProperty('width', `${width}px`, 'important');
        this.el.quickSearchWrap.style.setProperty('transform', 'none', 'important');
      } else {
        Object.assign(this.el.quickSearchWrap.style, {
          left: `${left}px`,
          top: `${top}px`,
          bottom: 'auto',
          width: `${width}px`,
          transform: 'none'
        });
      }

      if (this.el.quickResults && !this.el.quickResults.hidden) this.positionQuickResults();
    }

    onSearchDragEnd(e) {
      if (!this.searchDrag || e.pointerId !== this.searchDrag.pointerId) return;
      this.searchDrag = null;
      this.el.quickSearchWrap.classList.remove('is-moving');
      if (this.el.quickResults && !this.el.quickResults.hidden) this.positionQuickResults();
    }

    onCatalogPointerMove(e) {
      const drag = this.catalogDrag;
      if (!drag || e.pointerId !== drag.pointerId) return;

      const distance = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY);
      if (!drag.dragging && distance < 6) return;

      if (!drag.dragging) {
        const product = this.productById.get(String(drag.productId));
        if (!product) return;
        drag.dragging = true;
        if (drag.sourceRow) drag.sourceRow.dataset.dragging = 'true';
        drag.ghost = document.createElement('div');
        drag.ghost.className = 'jkb__drag-ghost';
        drag.ghost.innerHTML = `<strong>${this.escape(product.sku)}</strong><small>W${product.width} · D${product.depth} · H${product.height}</small>`;
        document.body.appendChild(drag.ghost);
      }

      if (e.cancelable) e.preventDefault();
      drag.ghost.style.left = `${e.clientX + 14}px`;
      drag.ghost.style.top = `${e.clientY + 14}px`;

      const rect = this.el.room.getBoundingClientRect();
      const overRoom = e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      this.el.room.classList.toggle('is-drop-target', overRoom);
    }

    onCatalogPointerUp(e, cancelled = false) {
      const drag = this.catalogDrag;
      if (!drag || e.pointerId !== drag.pointerId) return;
      this.catalogDrag = null;
      this.el.room.classList.remove('is-drop-target');

      if (drag.ghost) drag.ghost.remove();
      if (drag.sourceRow) {
        window.setTimeout(() => { delete drag.sourceRow.dataset.dragging; }, 0);
      }
      if (!drag.dragging || cancelled) return;

      this.suppressCatalogClick = true;
      window.setTimeout(() => { this.suppressCatalogClick = false; }, 0);

      const rect = this.el.room.getBoundingClientRect();
      const overRoom = e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!overRoom) return;

      const product = this.productById.get(String(drag.productId));
      if (!product) return;
      const point = this.roomPoint(e);
      this.addUnit(drag.productId, point.x - product.width / 2, point.y - product.depth / 2);
    }

    roomPoint(e) {
      const rect = this.el.room.getBoundingClientRect();
      const borderX = this.el.room.clientLeft || 0;
      const borderY = this.el.room.clientTop || 0;
      return {
        x: (e.clientX - rect.left - borderX) / this.scale,
        y: (e.clientY - rect.top - borderY) / this.scale
      };
    }

    lockCabinetDragGesture() {
      if (this.dragGestureLock) return;
      const stage = this.el.stageScroll;
      this.dragGestureLock = {
        stageTouchAction: stage?.style.touchAction || '',
        roomTouchAction: this.el.room?.style.touchAction || ''
      };
      if (stage) stage.style.setProperty('touch-action', 'none', 'important');
      if (this.el.room) this.el.room.style.setProperty('touch-action', 'none', 'important');
      this.root.classList.add('is-unit-dragging');
    }

    unlockCabinetDragGesture() {
      const lock = this.dragGestureLock;
      if (!lock) return;
      if (this.el.stageScroll) {
        if (lock.stageTouchAction) this.el.stageScroll.style.touchAction = lock.stageTouchAction;
        else this.el.stageScroll.style.removeProperty('touch-action');
      }
      if (this.el.room) {
        if (lock.roomTouchAction) this.el.room.style.touchAction = lock.roomTouchAction;
        else this.el.room.style.removeProperty('touch-action');
      }
      this.dragGestureLock = null;
      this.root.classList.remove('is-unit-dragging');
    }

    onPointerMove(e) {
      if (!this.drag || e.pointerId !== this.drag.pointerId) return;
      const unit = this.units.find(u => u.uid === this.drag.uid);
      const node = this.drag.node;
      if (!unit || !node) return;

      if (e.cancelable) e.preventDefault();
      e.stopPropagation();

      const point = this.roomPoint(e);
      const snapped = this.snapPosition(unit, point.x - this.drag.grabX, point.y - this.drag.grabY);
      unit.x = snapped.x;
      unit.y = snapped.y;

      // Move the existing captured DOM element. Rebuilding all units here
      // releases iOS pointer capture and lets Safari scroll the page instead.
      node.style.left = `${unit.x * this.scale}px`;
      node.style.top = `${unit.y * this.scale}px`;
      node.classList.toggle('is-colliding', this.hasCollision(unit));
      this.renderDragGuides(unit);
    }

    clearDragGuides() {
      this.el.room.querySelectorAll('[data-drag-guide]').forEach(node => node.remove());
    }

    renderDragGuides(unit) {
      this.clearDragGuides();
      if (!unit) return;

      const r = this.rect(unit);
      const vertical = new Set();
      const horizontal = new Set();
      const epsilon = 1;

      const addIfClose = (set, value, target) => {
        if (Math.abs(value - target) <= epsilon) set.add(target);
      };

      addIfClose(vertical, r.x, 0);
      addIfClose(vertical, r.x + r.width, this.roomWidth);
      addIfClose(horizontal, r.y, 0);
      addIfClose(horizontal, r.y + r.depth, this.roomDepth);

      for (const other of this.units) {
        if (other.uid === unit.uid || !this.sameCollisionLayer(unit, other)) continue;
        const o = this.rect(other);
        const unitXs = [r.x, r.x + r.width];
        const otherXs = [o.x, o.x + o.width];
        const unitYs = [r.y, r.y + r.depth];
        const otherYs = [o.y, o.y + o.depth];

        unitXs.forEach(value => otherXs.forEach(target => addIfClose(vertical, value, target)));
        unitYs.forEach(value => otherYs.forEach(target => addIfClose(horizontal, value, target)));
      }

      [...vertical].slice(0, 3).forEach(value => {
        const line = document.createElement('i');
        line.className = 'jkb__drag-guide is-vertical';
        line.dataset.dragGuide = 'vertical';
        line.style.left = `${value * this.scale}px`;
        this.el.room.appendChild(line);
      });

      [...horizontal].slice(0, 3).forEach(value => {
        const line = document.createElement('i');
        line.className = 'jkb__drag-guide is-horizontal';
        line.dataset.dragGuide = 'horizontal';
        line.style.top = `${value * this.scale}px`;
        this.el.room.appendChild(line);
      });
    }

    onPointerUp(e, cancelled = false) {
      if (!this.drag || e.pointerId !== this.drag.pointerId) return;
      const dragState = this.drag;
      const unit = this.units.find(u => u.uid === dragState.uid);
      const dragNode = dragState.node;
      const gestureDistance = Math.hypot(
        Number(e.clientX) - Number(dragState.startClientX),
        Number(e.clientY) - Number(dragState.startClientY)
      );
      const stationaryRepeatTap =
        !cancelled &&
        Boolean(dragState.wasSelected) &&
        gestureDistance <= 8 &&
        Date.now() - Number(dragState.startedAt || 0) <= 550;
      let moved = false;

      if (unit && cancelled) {
        // Safari may cancel a pointer when a system gesture, tab switch or
        // incoming call interrupts a drag. Never commit that partial position.
        unit.x = dragState.originX;
        unit.y = dragState.originY;
      } else if (unit && !this.unitFitsRoomShape(unit)) {
        unit.x = dragState.originX;
        unit.y = dragState.originY;
        this.setStatus('That position is outside the edited room shape.');
      } else if (unit && this.hasCollision(unit)) {
        unit.x = dragState.originX;
        unit.y = dragState.originY;
        this.setStatus('That position overlaps another cabinet or a door, window, radiator or obstacle.');
      } else if (unit) {
        moved = unit.x !== dragState.originX || unit.y !== dragState.originY;
      }

      if (moved && unit) {
        this.justAddedIds.delete(unit.uid);
        if (this.lastAddedId === unit.uid) this.lastAddedId = null;

        const product = this.product(unit);
        if (product?.planningKind && product.planningKind !== 'obstacle') {
          this.snapRoomFeatureToNearestWall(unit, {
            render: false,
            save: false,
            silent: true
          });
        }
      }

      dragNode?.classList.remove('is-dragging');
      this.clearDragGuides();
      try { dragNode?.releasePointerCapture?.(e.pointerId); } catch (_) {}
      this.drag = null;
      this.unlockCabinetDragGesture();
      this.renderAll();
      this.save();

      if (unit && !moved && stationaryRepeatTap) {
        this.selectStackedCounterpart(unit);
      }
    }

    snapPosition(unit, rawX, rawY, step = this.grid) {
      const d = this.unitDimensions(unit);
      const moveStep = Math.max(1, Number(step) || this.grid);
      let x = Math.round(Number(rawX || 0) / moveStep) * moveStep;
      let y = Math.round(Number(rawY || 0) / moveStep) * moveStep;
      const tolerance = Math.max(this.grid, 75);

      if (unit.wallLinked !== false) {
        const boundaryX = [0, this.roomWidth - d.width];
        const boundaryY = [0, this.roomDepth - d.depth];
        boundaryX.forEach(v => { if (Math.abs(x - v) <= tolerance) x = v; });
        boundaryY.forEach(v => { if (Math.abs(y - v) <= tolerance) y = v; });
      }

      const product = this.product(unit);

      // TS utility-sink cabinets are standalone products. Keep room-edge snapping
      // but do not magnetically join them into a standard fitted cabinet run.
      if (!product?.standalone) {
        for (const other of this.units) {
          if (other.uid === unit.uid || !this.sameCollisionLayer(unit, other)) continue;
          const od = this.unitDimensions(other);
          const xCandidates = [other.x - d.width, other.x + od.width, other.x];
          const yCandidates = [other.y - d.depth, other.y + od.depth, other.y];
          for (const v of xCandidates) if (Math.abs(x - v) <= tolerance) x = v;
          for (const v of yCandidates) if (Math.abs(y - v) <= tolerance) y = v;
        }
      }

      if (product?.category === 'wall') {
        for (const other of this.units) {
          if (other.uid === unit.uid) continue;
          const otherProduct = this.product(other);
          if (!otherProduct || otherProduct.category === 'wall' || otherProduct.category === 'tall') continue;
          const od = this.unitDimensions(other);
          const xCandidates = [other.x, other.x + od.width - d.width];
          for (const v of xCandidates) if (Math.abs(x - v) <= tolerance) x = v;

          if (Math.abs(other.y) <= tolerance && Math.abs(y) <= tolerance * 2) y = 0;
          if (Math.abs((other.y + od.depth) - this.roomDepth) <= tolerance &&
              Math.abs((y + d.depth) - this.roomDepth) <= tolerance * 2) {
            y = Math.max(0, this.roomDepth - d.depth);
          }
        }
      }

      return {
        x: this.clamp(x, 0, Math.max(0, this.roomWidth - d.width)),
        y: this.clamp(y, 0, Math.max(0, this.roomDepth - d.depth))
      };
    }

    startNudgeRepeat(direction) {
      this.stopNudgeRepeat();
      const moved = this.nudgeSelected(direction);
      if (!moved) return;

      this.nudgeHoldTimer = window.setTimeout(() => {
        this.nudgeRepeatTimer = window.setInterval(() => {
          if (!this.nudgeSelected(direction)) this.stopNudgeRepeat();
        }, 160);
      }, 360);
    }

    stopNudgeRepeat() {
      if (this.nudgeHoldTimer) window.clearTimeout(this.nudgeHoldTimer);
      if (this.nudgeRepeatTimer) window.clearInterval(this.nudgeRepeatTimer);
      this.nudgeHoldTimer = null;
      this.nudgeRepeatTimer = null;
    }

    nudgeSelected(direction) {
      const unit = this.selected();
      if (!unit) return false;
      if (unit.locked) {
        this.setStatus('Unlock this cabinet before moving it.');
        return false;
      }

      const product = this.product(unit);
      const d = this.unitDimensions(unit);
      const step = this.precisionStep === 10 ? 10 : 50;
      const old = { x: unit.x, y: unit.y };
      let x = unit.x;
      let y = unit.y;

      if (direction === 'left') x -= step;
      else if (direction === 'right') x += step;
      else if (direction === 'up') y -= step;
      else if (direction === 'down') y += step;
      else return false;

      // Round only the axis that actually moved. The untouched axis may be
      // an exact wall coordinate that is not on the 50 mm grid (for example an
      // AL cabinet with 578 mm depth in a 3000 mm room sits at y = 2422).
      // Rounding both axes would pull it 22 mm away from the wall.
      const roundStep = value => Math.round(value / step) * step;
      if (direction === 'left' || direction === 'right') {
        unit.x = this.clamp(roundStep(x), 0, Math.max(0, this.roomWidth - d.width));
        unit.y = this.clamp(y, 0, Math.max(0, this.roomDepth - d.depth));
      } else {
        unit.x = this.clamp(x, 0, Math.max(0, this.roomWidth - d.width));
        unit.y = this.clamp(roundStep(y), 0, Math.max(0, this.roomDepth - d.depth));
      }

      if (unit.x === old.x && unit.y === old.y) {
        this.setStatus('Cabinet is already against the room edge.');
        return false;
      }

      if (!this.unitFitsRoomShape(unit)) {
        unit.x = old.x;
        unit.y = old.y;
        this.setStatus('That move would place the cabinet outside the edited room shape.');
        return false;
      }

      if (this.hasCollision(unit)) {
        unit.x = old.x;
        unit.y = old.y;
        this.setStatus('That move would overlap another cabinet.');
        return false;
      }

      this.justAddedIds.delete(unit.uid);
      if (this.lastAddedId === unit.uid) this.lastAddedId = null;
      this.renderAll();
      this.save();
      this.setStatus(`${product?.sku || 'Cabinet'} moved ${step} mm ${direction}.`);
      return true;
    }

    snapRoomFeatureToNearestWall(unit, options = {}) {
      const product = unit ? this.product(unit) : null;
      if (!unit || !product?.planningKind || product.planningKind === 'obstacle') return false;
      if (unit.locked) {
        if (!options.silent) this.setStatus('Unlock this room feature before moving it.');
        return false;
      }

      const current = this.rect(unit);
      const distances = [
        { edge: 'top', distance: current.y },
        { edge: 'bottom', distance: Math.max(0, this.roomDepth - (current.y + current.depth)) },
        { edge: 'left', distance: current.x },
        { edge: 'right', distance: Math.max(0, this.roomWidth - (current.x + current.width)) }
      ].sort((a, b) => a.distance - b.distance);

      const target = distances[0]?.edge || 'bottom';
      const old = { x: unit.x, y: unit.y, rotation: unit.rotation };

      if (target === 'top' || target === 'bottom') {
        unit.rotation = 0;
        const d = this.unitDimensions(unit);
        const currentCentre = current.x + current.width / 2;
        unit.x = this.clamp(this.roundGrid(currentCentre - d.width / 2), 0, Math.max(0, this.roomWidth - d.width));
        unit.y = target === 'top' ? 0 : Math.max(0, this.roomDepth - d.depth);
      } else {
        unit.rotation = 90;
        const d = this.unitDimensions(unit);
        const currentCentre = current.y + current.depth / 2;
        unit.y = this.clamp(this.roundGrid(currentCentre - d.depth / 2), 0, Math.max(0, this.roomDepth - d.depth));
        unit.x = target === 'left' ? 0 : Math.max(0, this.roomWidth - d.width);
      }

      const changed = unit.x !== old.x || unit.y !== old.y || unit.rotation !== old.rotation;
      if (!changed) {
        if (!options.silent) this.setStatus(`${product.productTitle} is already aligned to the ${target} wall.`);
        return false;
      }

      this.justAddedIds.delete(unit.uid);
      if (this.lastAddedId === unit.uid) this.lastAddedId = null;

      if (options.render !== false) this.renderAll();
      if (options.save !== false) this.save();
      if (!options.silent) this.setStatus(`${product.productTitle} aligned to the ${target} wall.`);
      return true;
    }

    snapSelectedToWall() {
      const unit = this.selected();
      if (!unit) return;
      if (unit.locked) {
        this.setStatus('Unlock this cabinet before moving it.');
        return;
      }

      const product = this.product(unit);
      if (!product) return;

      if (product.planningKind && product.planningKind !== 'obstacle') {
        this.snapRoomFeatureToNearestWall(unit);
        return;
      }

      const old = {
        x: unit.x,
        y: unit.y,
        rotation: unit.rotation,
        cornerTurn: unit.cornerTurn
      };
      const current = this.rect(unit);

      // Corner cabinets belong in an actual room corner, not merely against one
      // wall. Try the nearest corner first, then the remaining clear corners.
      if (product.cornerShape || product.category === 'corner') {
        const centreX = current.x + current.width / 2;
        const centreY = current.y + current.depth / 2;
        const d = this.unitDimensions(unit);
        const corners = [
          { name: 'top-left', x: 0, y: 0 },
          { name: 'top-right', x: Math.max(0, this.roomWidth - d.width), y: 0 },
          { name: 'bottom-left', x: 0, y: Math.max(0, this.roomDepth - d.depth) },
          { name: 'bottom-right', x: Math.max(0, this.roomWidth - d.width), y: Math.max(0, this.roomDepth - d.depth) }
        ].sort((a, b) => {
          const ad = Math.hypot(centreX - (a.x + d.width / 2), centreY - (a.y + d.depth / 2));
          const bd = Math.hypot(centreX - (b.x + d.width / 2), centreY - (b.y + d.depth / 2));
          return ad - bd;
        });

        let chosenCorner = null;
        for (const corner of corners) {
          unit.x = corner.x;
          unit.y = corner.y;
          unit.cornerTurn = 0;
          if (!this.hasCollision(unit)) {
            chosenCorner = corner;
            break;
          }
        }

        if (!chosenCorner) {
          Object.assign(unit, old);
          this.setStatus('No clear room corner is available for this cabinet.');
          return;
        }

        const changed = unit.x !== old.x || unit.y !== old.y || unit.cornerTurn !== old.cornerTurn;
        if (!changed) {
          this.setStatus(`${product.sku} is already fitted to the ${chosenCorner.name.replace('-', ' ')} corner.`);
          return;
        }

        this.justAddedIds.delete(unit.uid);
        if (this.lastAddedId === unit.uid) this.lastAddedId = null;
        this.renderAll();
        this.save();
        this.setStatus(`${product.sku} fitted to the ${chosenCorner.name.replace('-', ' ')} corner.`);
        return;
      }

      const options = [
        { edge: 'left', distance: current.x, rotation: 90 },
        { edge: 'right', distance: Math.max(0, this.roomWidth - (current.x + current.width)), rotation: 90 },
        { edge: 'top', distance: current.y, rotation: 0 },
        { edge: 'bottom', distance: Math.max(0, this.roomDepth - (current.y + current.depth)), rotation: 0 }
      ].sort((a, b) => a.distance - b.distance);

      const centreX = current.x + current.width / 2;
      const centreY = current.y + current.depth / 2;
      let chosen = null;

      for (const option of options) {
        unit.rotation = option.rotation;
        const d = this.unitDimensions(unit);
        const maxX = Math.max(0, this.roomWidth - d.width);
        const maxY = Math.max(0, this.roomDepth - d.depth);

        if (option.edge === 'left' || option.edge === 'right') {
          unit.x = option.edge === 'left' ? 0 : maxX;
          unit.y = this.clamp(this.roundGrid(centreY - d.depth / 2), 0, maxY);
        } else {
          unit.x = this.clamp(this.roundGrid(centreX - d.width / 2), 0, maxX);
          unit.y = option.edge === 'top' ? 0 : maxY;
        }

        if (!this.hasCollision(unit)) {
          chosen = option;
          break;
        }
      }

      if (!chosen) {
        Object.assign(unit, old);
        this.setStatus('No clear wall position is available for this cabinet.');
        return;
      }

      const changed = unit.x !== old.x || unit.y !== old.y || unit.rotation !== old.rotation;
      if (!changed) {
        this.setStatus(`Cabinet is already aligned against the ${chosen.edge} wall.`);
        return;
      }

      this.justAddedIds.delete(unit.uid);
      if (this.lastAddedId === unit.uid) this.lastAddedId = null;
      this.renderAll();
      this.save();
      this.setStatus(`${product.sku} rotated and aligned against the ${chosen.edge} wall.`);
    }

    rotateSelected() {
      const unit = this.selected();
      if (!unit) return;
      if (unit.locked) {
        this.setStatus('Unlock this cabinet before rotating it.');
        return;
      }
      const product = this.product(unit);
      if (product && (product.cornerShape || product.category === 'corner')) {
        unit.cornerTurn = ((Number(unit.cornerTurn) || 0) + 1) % 4;
        this.justAddedIds.delete(unit.uid);
        if (this.lastAddedId === unit.uid) this.lastAddedId = null;
        this.renderAll();
        this.save();
        this.setStatus(`${product.sku} corner rotated exactly 90°.`);
        return;
      }

      // Rotate around the cabinet centre. Rotation is deliberately independent
      // from wall snapping; To wall remains a separate explicit action.
      const before = this.unitDimensions(unit);
      const centreX = unit.x + before.width / 2;
      const centreY = unit.y + before.depth / 2;
      const preserveWall = unit.wallLinked !== false;
      const wasAtLeft = preserveWall && Math.abs(unit.x) < 0.5;
      const wasAtRight = preserveWall && Math.abs((unit.x + before.width) - this.roomWidth) < 0.5;
      const wasAtTop = preserveWall && Math.abs(unit.y) < 0.5;
      const wasAtBottom = preserveWall && Math.abs((unit.y + before.depth) - this.roomDepth) < 0.5;
      if (unit.wallLinked === false) {
        const directions = ['top', 'right', 'bottom', 'left'];
        const current = this.frontEdgeFor(unit);
        const index = Math.max(0, directions.indexOf(current));
        unit.frontEdgeOverride = directions[(index + 1) % directions.length];
      }
      unit.rotation = unit.rotation === 90 ? 0 : 90;

      const after = this.unitDimensions(unit);
      const maxX = Math.max(0, this.roomWidth - after.width);
      const maxY = Math.max(0, this.roomDepth - after.depth);
      const rotationStep = this.precisionStep === 10 ? 10 : 50;
      const snapRotationCoordinate = value => Math.round(value / rotationStep) * rotationStep;
      const centredX = centreX - after.width / 2;
      const centredY = centreY - after.depth / 2;

      // Odd cabinet depths (for example 463 mm) create a .5 mm centre.
      // Quantise free axes to the selected movement grid so repeated 90°
      // rotations cannot accumulate 1 mm drift. A genuinely wall-linked axis
      // keeps its exact wall coordinate, even when the room edge is off-grid.
      unit.x = wasAtLeft
        ? 0
        : wasAtRight
          ? maxX
          : this.clamp(snapRotationCoordinate(centredX), 0, maxX);
      unit.y = wasAtTop
        ? 0
        : wasAtBottom
          ? maxY
          : this.clamp(snapRotationCoordinate(centredY), 0, maxY);

      this.justAddedIds.delete(unit.uid);
      if (this.lastAddedId === unit.uid) this.lastAddedId = null;
      const overlaps = this.hasCollision(unit);
      this.renderAll();
      this.save();
      const frontText = product?.planningKind ? '' : this.frontDirectionText(unit);
      this.setStatus(overlaps
        ? 'Cabinet rotated 90°. It now overlaps another item—move it into a clear position.'
        : (frontText ? `Cabinet rotated 90°. FRONT now faces ${frontText}.` : 'Item rotated 90°.'));
    }

    toggleSelectedWallLink() {
      const unit = this.selected();
      const product = unit ? this.product(unit) : null;
      if (!unit || !product || product.planningKind || product.cornerShape || product.category === 'corner') return;

      if (unit.wallLinked === false) {
        unit.wallLinked = true;
        unit.frontEdgeOverride = null;
        this.setStatus('Automatic wall positioning and wall-facing FRONT restored.');
      } else {
        unit.frontEdgeOverride = this.frontEdgeFor(unit);
        unit.wallLinked = false;
        unit.serviceGapOverride = null;
        this.setStatus('Free rotation enabled. Rotate now forces FRONT through all four sides.');
      }
      this.renderAll();
      this.save();
    }

    toggleSelectedLock() {
      const unit = this.selected();
      if (!unit) return;
      unit.locked = !unit.locked;
      this.renderUnits();
      this.renderSelection();
      this.save();
      this.setStatus(unit.locked ? 'Cabinet position locked.' : 'Cabinet unlocked.');
    }

    duplicateSelected() {
      const source = this.selected();
      if (!source) return null;

      const duplicate = {
        uid: `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
        productId: String(source.productId),
        x: source.x,
        y: source.y,
        rotation: source.rotation,
        cornerTurn: ((Number(source.cornerTurn) || 0) % 4 + 4) % 4,
        doorOpen: false,
        hingeSide: source.hingeSide === 'right' ? 'right' : 'left',
        locked: false,
        wallLinked: source.wallLinked !== false,
        frontEdgeOverride: source.wallLinked === false ? String(source.frontEdgeOverride || '') : null,
        serviceGapOverride:
          source.serviceGapOverride !== null &&
          source.serviceGapOverride !== undefined &&
          source.serviceGapOverride !== '' &&
          Number.isFinite(Number(source.serviceGapOverride))
            ? Number(source.serviceGapOverride)
            : null
      };

      const d = this.unitDimensions(duplicate);
      const preferred = this.snapPosition(duplicate, source.x + d.width, source.y);
      duplicate.x = preferred.x;
      duplicate.y = preferred.y;

      if (this.hasCollision(duplicate)) {
        const open = this.findOpenPosition(duplicate);
        if (!open) {
          this.setStatus('There is no free space to duplicate this cabinet.');
          return null;
        }
        Object.assign(duplicate, open);
      }

      this.units.push(duplicate);
      this.justAddedIds.add(duplicate.uid);
      this.lastAddedId = duplicate.uid;
      this.selectedId = duplicate.uid;
      this.renderAll();
      this.save();
      requestAnimationFrame(() => this.revealJustAdded(duplicate.uid));
      return duplicate;
    }

    deleteSelected() {
      if (!this.selectedId) return;
      const deletedUid = this.selectedId;
      const targetUnit = this.units.find(unit => unit.uid === deletedUid);
      const targetProduct = targetUnit ? this.product(targetUnit) : null;
      const label = targetProduct?.sku || 'this item';
      if (!window.confirm(`Remove ${label} from your kitchen plan?`)) return;
      this.units = this.units.filter(unit => unit.uid !== deletedUid);
      this.releaseEndPanelsForUnit(deletedUid);
      const removedPlinth = this.plinthApplicationForUnit(deletedUid);
      if (removedPlinth) {
        const productId = String(removedPlinth.productId || '');
        const requiredBefore = this.requiredPlinthSticks(productId);
        this.plinthApplications = (this.plinthApplications || []).filter(item => item !== removedPlinth);
        const requiredAfter = this.requiredPlinthSticks(productId);
        const current = Math.max(0, Number(this.extras?.[productId]) || 0);
        const autoReduction = Math.max(0, requiredBefore - requiredAfter);
        const next = Math.max(requiredAfter, current - autoReduction);
        if (next === 0) delete this.extras[productId];
        else this.extras[productId] = next;
      }
      this.justAddedIds.delete(deletedUid);
      if (this.lastAddedId === deletedUid) this.lastAddedId = null;
      this.selectedId = null;
      this.renderAll();
      this.save();
    }

    selectStackedCounterpart(unit) {
      const product = unit ? this.product(unit) : null;
      if (!product || this.activeLayer !== 'all') return false;

      const sourceRect = this.rect(unit);
      const wantsFloor = product.category === 'wall';
      const candidates = this.units.filter(other => {
        if (other.uid === unit.uid || !this.unitVisibleInLayer(other)) return false;
        const otherProduct = this.product(other);
        if (!otherProduct || otherProduct.planningKind) return false;
        if (wantsFloor) {
          if (otherProduct.category === 'wall') return false;
        } else if (otherProduct.category !== 'wall') {
          return false;
        }
        return this.rectsOverlap(sourceRect, this.rect(other));
      });

      if (!candidates.length) return false;
      const target = candidates[0];
      const targetProduct = this.product(target);
      this.selectedId = target.uid;
      this.renderUnits();
      this.renderSelection();
      this.setStatus(`${targetProduct?.sku || 'Cabinet'} selected ${wantsFloor ? 'under the wall unit' : 'above the floor unit'}.`);
      return true;
    }

    select(uid) {
      this.selectedId = uid;
      this.renderSelection();
      this.renderUnits();
    }

    selected() {
      return this.units.find(unit => unit.uid === this.selectedId) || null;
    }

    renderAll() {
      this.fitRoom();
      this.renderUnits();
      this.renderGapRulers();
      this.renderSelection();
      this.renderSummary();
      this.el.empty.hidden = this.units.length > 0;
      this.el.designId.textContent = this.designId;
      if (this.el.projectTitle) this.el.projectTitle.textContent = this.projectName || 'My kitchen';
      this.sync3D();
    }

    fitRoom() {
      const mobile = window.matchMedia('(max-width: 700px)').matches;
      const desktopDock = !mobile && window.matchMedia('(min-width: 1181px)').matches;
      const dockReserve = desktopDock ? 290 : 0;
      const { height: viewportHeight } = this.viewportMetrics();
      const stageRect = this.el.stage.getBoundingClientRect();
      const viewportBudget = mobile
        ? viewportHeight
        : Math.max(360, Math.min(800, viewportHeight - Math.max(0, stageRect.top) - 18));

      const availableWidth = mobile
        ? Math.max(280, this.el.stage.clientWidth - 12)
        : Math.max(280, this.el.stage.clientWidth - 58 - dockReserve);

      // Size from the real visible viewport, not a fixed desktop height.
      // visualViewport changes when browser zoom changes, so Surface/PC zoom
      // levels automatically refit the plan without leaving a large empty stage.
      const availableHeight = mobile
        ? Math.max(320, viewportHeight * 0.58)
        : Math.max(300, Math.min(desktopDock ? 680 : 570, viewportBudget - (desktopDock ? 50 : 78)));

      this.scale = Math.min(availableWidth / this.roomWidth, availableHeight / this.roomDepth);
      this.scale = Math.max(0.035, Math.min(this.scale, mobile ? 0.28 : (desktopDock ? 0.265 : 0.225)));

      const roomStyle = window.getComputedStyle(this.el.room);
      const borderX = (parseFloat(roomStyle.borderLeftWidth) || 0) + (parseFloat(roomStyle.borderRightWidth) || 0);
      const borderY = (parseFloat(roomStyle.borderTopWidth) || 0) + (parseFloat(roomStyle.borderBottomWidth) || 0);
      const roomPixelWidth = Math.round(this.roomWidth * this.scale + borderX);
      const roomPixelHeight = Math.round(this.roomDepth * this.scale + borderY);
      this.el.room.style.width = roomPixelWidth + 'px';
      this.el.room.style.height = roomPixelHeight + 'px';
      this.el.room.style.backgroundSize = `${this.grid * this.scale}px ${this.grid * this.scale}px`;
      this.applyRoomShapeVisual();

      if (mobile) {
        const stageHeight = Math.max(220, roomPixelHeight + 62);
        this.el.stage.style.setProperty('--jkb-stage-height', stageHeight + 'px');
        this.el.stage.style.height = stageHeight + 'px';
        this.el.stage.style.minHeight = stageHeight + 'px';
        this.el.stageScroll.style.height = '100%';
        this.el.stageScroll.style.minHeight = '0';
      } else {
        // Keep just enough working area around the room. The right inspector
        // scrolls internally when necessary, so its content must not force a
        // huge checkerboard area on shorter Surface/Windows viewports.
        const roomNeeded = roomPixelHeight + (desktopDock ? 72 : 96);
        const minimum = desktopDock ? 440 : 450;
        const desiredStageHeight = Math.round(Math.max(minimum, Math.min(viewportBudget, roomNeeded)));
        this.el.stage.style.setProperty('--jkb-stage-height', desiredStageHeight + 'px');
        this.el.stage.style.height = '';
        this.el.stage.style.minHeight = '';
        this.el.stageScroll.style.height = '';
        this.el.stageScroll.style.minHeight = '';
      }
      this.el.widthLabel.textContent = `${this.roomWidth} mm`;
      this.el.depthLabel.textContent = `${this.roomDepth} mm`;
      this.el.widthInput.value = this.roomWidth;
      this.el.depthInput.value = this.roomDepth;
    }

    frontEdgeFor(unit) {
      if (!unit) return 'top';
      const product = this.product(unit);
      if (product?.planningKind) return '';
      const override = String(unit.frontEdgeOverride || '');
      if (unit.wallLinked === false && ['top', 'right', 'bottom', 'left'].includes(override)) {
        return override;
      }

      const r = this.rect(unit);
      const distances = {
        top: r.y,
        bottom: Math.max(0, this.roomDepth - (r.y + r.depth)),
        left: r.x,
        right: Math.max(0, this.roomWidth - (r.x + r.width))
      };
      const backEdge = Object.entries(distances).sort((a, b) => a[1] - b[1])[0]?.[0] || 'bottom';

      const opposite = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      };
      const rotateQuarter = {
        top: 'left',
        left: 'bottom',
        bottom: 'right',
        right: 'top'
      };
      const rotateBackQuarter = {
        top: 'right',
        right: 'bottom',
        bottom: 'left',
        left: 'top'
      };

      let front = opposite[backEdge] || 'top';
      const expectedRotation = (backEdge === 'left' || backEdge === 'right') ? 90 : 0;
      const actualRotation = Number(unit.rotation) === 90 ? 90 : 0;
      const delta = (actualRotation - expectedRotation + 360) % 360;

      if (delta === 90) front = rotateQuarter[front] || front;
      else if (delta === 270) front = rotateBackQuarter[front] || front;

      return front;
    }

    frontDirectionText(unit) {
      const edge = this.frontEdgeFor(unit);
      const labels = {
        top: '↑ top',
        bottom: '↓ bottom',
        left: '← left',
        right: '→ right'
      };
      return labels[edge] || '';
    }

    wallGapSegments(layer = 'floor') {
      const walls = {
        top: { length: this.roomWidth, intervals: [] },
        bottom: { length: this.roomWidth, intervals: [] },
        left: { length: this.roomDepth, intervals: [] },
        right: { length: this.roomDepth, intervals: [] }
      };
      const tolerance = 80;
      const wallOnly = layer === 'wall';

      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product) continue;
        const planningKind = String(product.planningKind || '');
        const planning = Boolean(planningKind);
        const tall = product.category === 'tall';
        // A room feature blocks a ruler only when its vertical range crosses
        // that cabinet band. Windows intentionally never block the base run.
        if (planning) {
          if (planningKind === 'window' && !wallOnly) continue;
          const featureBottom = Math.max(0, Number(product.elevation) || 0);
          const featureTop = featureBottom + Math.max(0, Number(product.height) || 0);
          const bandBottom = wallOnly ? 1450 : 0;
          const bandTop = wallOnly ? 2300 : 1000;
          if (Math.min(featureTop, bandTop) - Math.max(featureBottom, bandBottom) <= 20) continue;
        }
        if (!planning && !tall && wallOnly !== (product.category === 'wall')) continue;

        const r = this.rect(unit);
        const touched = [];
        if (r.y <= tolerance) touched.push('top');
        if (this.roomDepth - (r.y + r.depth) <= tolerance) touched.push('bottom');
        if (r.x <= tolerance) touched.push('left');
        if (this.roomWidth - (r.x + r.width) <= tolerance) touched.push('right');

        for (const edge of touched) {
          const horizontal = edge === 'top' || edge === 'bottom';
          const intervalStart = horizontal ? r.x : r.y;
          const intervalEnd = intervalStart + (horizontal ? r.width : r.depth);
          walls[edge].intervals.push([
            this.clamp(intervalStart, 0, walls[edge].length),
            this.clamp(intervalEnd, 0, walls[edge].length)
          ]);
        }
      }

      const gaps = [];
      for (const [edge, wall] of Object.entries(walls)) {
        const merged = wall.intervals
          .filter(([intervalStart, intervalEnd]) => intervalEnd > intervalStart)
          .sort((left, right) => left[0] - right[0])
          .reduce((result, interval) => {
            const previous = result[result.length - 1];
            if (!previous || interval[0] > previous[1] + 5) result.push([...interval]);
            else previous[1] = Math.max(previous[1], interval[1]);
            return result;
          }, []);

        let cursor = 0;
        for (const [intervalStart, intervalEnd] of merged) {
          if (intervalStart - cursor >= 250) {
            gaps.push({ layer, edge, start: cursor, end: intervalStart, mm: Math.round(intervalStart - cursor) });
          }
          cursor = Math.max(cursor, intervalEnd);
        }
        if (wall.length - cursor >= 250) {
          gaps.push({ layer, edge, start: cursor, end: wall.length, mm: Math.round(wall.length - cursor) });
        }
      }
      return gaps;
    }

    renderGapRulers() {
      this.el.room.querySelectorAll('[data-gap-ruler]').forEach(node => node.remove());
      const layers = this.activeLayer === 'all'
        ? ['floor', 'wall']
        : [this.activeLayer === 'wall' ? 'wall' : 'floor'];
      const gaps = layers.flatMap(layer => this.wallGapSegments(layer));

      for (const gap of gaps) {
        const vertical = gap.edge === 'left' || gap.edge === 'right';
        const label = gap.layer === 'wall' ? 'WALL' : 'BASE';
        const node = document.createElement('div');
        node.className = `jkb__gap-ruler is-${gap.edge} is-${gap.layer}`;
        node.dataset.gapRuler = gap.edge;
        node.dataset.rulerLayer = gap.layer;
        node.setAttribute('aria-label', `${label.toLowerCase()} run: ${gap.mm} millimetres clear space`);
        node.innerHTML = `<span><b>${label}</b> ${gap.mm} mm</span>`;
        node.style.setProperty('--jkb-ruler-inset', `${(gap.layer === 'wall' ? 156 : 260) * this.scale}px`);

        if (vertical) {
          node.style.top = `${gap.start * this.scale}px`;
          node.style.height = `${Math.max(1, (gap.end - gap.start) * this.scale)}px`;
        } else {
          node.style.left = `${gap.start * this.scale}px`;
          node.style.width = `${Math.max(1, (gap.end - gap.start) * this.scale)}px`;
        }
        this.el.room.appendChild(node);
      }
    }

    renderUnits() {
      this.el.room.querySelectorAll('[data-unit-id]').forEach(node => node.remove());

      const endPanelsByUnit = new Map();
      for (const panel of this.endPanelPlacements()) {
        if (!endPanelsByUnit.has(panel.unitId)) endPanelsByUnit.set(panel.unitId, []);
        endPanelsByUnit.get(panel.unitId).push(panel);
      }

      const roomConflictIds = new Set();
      this.roomFeatureConflicts().forEach(conflict => {
        roomConflictIds.add(conflict.feature.uid);
        roomConflictIds.add(conflict.unit.uid);
      });

      this.units.forEach(unit => {
        const product = this.product(unit);
        if (!product || !this.unitVisibleInLayer(unit)) return;
        const appliedEndPanels = endPanelsByUnit.get(String(unit.uid)) || [];
        const d = this.unitDimensions(unit);
        const mismatch = this.heightMismatchFor(unit);
        const heightWarnings = this.heightWarningsFor(unit);
        const frontEdge = this.frontEdgeFor(unit);
        const fitInfo = this.fittedRunInfo(unit);
        const floorUnitsBelow = product.category === 'wall' ? this.floorUnitsBelowWall(unit) : [];
        const compactAll = this.activeLayer === 'all' && unit.uid !== this.selectedId && !product.planningKind;
        const panelPlacementTargets = this.placementTargetsForUnit(unit);
        const node = document.createElement('div');
        node.className = 'jkb__unit';
        if (unit.uid === this.selectedId) node.classList.add('is-selected');
        if (compactAll) node.classList.add('is-compact-all');
        if (panelPlacementTargets.length) node.classList.add('is-panel-placement-target');
        if (floorUnitsBelow.length) node.classList.add('is-stacked-wall');
        if (appliedEndPanels.length) node.classList.add('has-end-panel');
        const appliedPlinth = this.plinthApplicationForUnit(unit.uid);
        if (appliedPlinth) {
          node.classList.add('has-applied-plinth');
          node.style.setProperty('--jkb-applied-plinth-length', `${Math.max(100, Number(appliedPlinth.length) || 0) * this.scale}px`);
          const plinthProduct = this.productById.get(String(appliedPlinth.productId));
          node.style.setProperty('--jkb-applied-plinth-colour', this.endPanelCssColour(plinthProduct?.colour || product.colour));
        }
        if (fitInfo) {
          node.classList.add('is-fitted-run');
          if (fitInfo.includedWorktop) node.classList.add('has-included-worktop');
          if (fitInfo.serviceGap > 0) node.classList.add('has-service-gap');
          node.style.setProperty('--jkb-fitted-depth', `${fitInfo.fittedDepth}`);
          node.style.setProperty('--jkb-carcass-depth-pct', `${((fitInfo.carcassDepth / fitInfo.fittedDepth) * 100).toFixed(3)}%`);
          node.style.setProperty('--jkb-service-gap-pct', `${((fitInfo.serviceGap / fitInfo.fittedDepth) * 100).toFixed(3)}%`);
          node.style.setProperty('--jkb-front-allowance-pct', `${((fitInfo.frontAllowance / fitInfo.fittedDepth) * 100).toFixed(3)}%`);
        }
        if (product.custom) node.classList.add('is-custom');
        if (!product.custom && !product.available) node.classList.add('is-out-of-stock');
        if (product.planningKind) node.classList.add('is-room-feature', `is-${product.planningKind}`);
        if (
          product.familyLabel === 'Bridging wall unit' ||
          /^(?:W)?EX\d/.test(String(product.sku || '').toUpperCase())
        ) {
          node.classList.add('is-extractor');
        }
        if (unit.locked) node.classList.add('is-locked');
        if (this.justAddedIds.has(unit.uid)) node.classList.add('is-just-added');
        if (this.hasCollision(unit)) node.classList.add('is-colliding');
        if (heightWarnings.length) node.classList.add('has-height-warning');
        if (roomConflictIds.has(unit.uid)) node.classList.add('has-room-conflict');
        if (product.category === 'wall' && this.wallOverlapsFloor(unit)) node.classList.add('is-over-base');
        node.dataset.unitId = unit.uid;
        node.dataset.category = product.category;
        if (Boolean(product.cornerShape || product.category === 'corner')) {
          node.classList.add('is-corner-unit');
          node.dataset.roomCorner = this.cornerOrientation(unit);

          // Fixed, adjustable and wall corners have different arm depths.
          // Drive the 2D L footprint from the actual product geometry instead
          // of using one hard-coded notch for every corner family.
          const armDepth = Math.max(1, Number(product.cornerArmDepth) || Math.min(d.width, d.depth) * 0.55);
          const armX = this.clamp((armDepth / Math.max(1, d.width)) * 100, 18, 82);
          const armY = this.clamp((armDepth / Math.max(1, d.depth)) * 100, 18, 82);
          node.style.setProperty('--jkb-corner-arm-x', `${armX.toFixed(2)}%`);
          node.style.setProperty('--jkb-corner-arm-y', `${armY.toFixed(2)}%`);
        }
        if (product.planningKind) node.dataset.planningKind = product.planningKind;
        node.dataset.layer = product.category === 'wall' ? 'wall' : 'floor';
        node.dataset.edge = this.nearestRoomEdge(unit);
        node.style.left = `${unit.x * this.scale}px`;
        node.style.top = `${unit.y * this.scale}px`;
        node.style.width = `${Math.max(18, d.width * this.scale)}px`;
        node.style.height = `${Math.max(14, d.depth * this.scale)}px`;
        const pixelWidth = Math.max(18, d.width * this.scale);
        const skuFont = Math.max(5.5, Math.min(8, pixelWidth / Math.max(7, product.sku.length * 0.72)));
        node.style.setProperty('--jkb-sku-font', `${skuFont}px`);
        node.innerHTML = `
          ${fitInfo ? `
            <span class="jkb__fitted-carcass is-front-${frontEdge}" aria-hidden="true"></span>
            ${fitInfo.serviceGap > 0
              ? `<span class="jkb__service-gap-zone is-back-${({ top: 'bottom', bottom: 'top', left: 'right', right: 'left' })[frontEdge] || 'top'}" aria-hidden="true">${compactAll ? '' : `<i>${Math.round(fitInfo.serviceGap)} GAP</i>`}</span>`
              : ''}
          ` : ''}
          <span class="jkb__unit-sku">${this.escape(product.sku)}</span>
          <small>W${product.width}</small>
          ${compactAll ? '' : `
            <small>D${fitInfo ? fitInfo.fittedDepth : product.depth}${fitInfo ? ` · carcass ${fitInfo.carcassDepth}` : ''}</small>
            <small>H${product.height}</small>
          `}
          ${Boolean(product.cornerShape || product.category === 'corner')
            ? '<span class="jkb__corner-fronts" aria-hidden="true"><i></i><b></b></span>'
            : (!product.planningKind && frontEdge && !compactAll ? `<span class="jkb__unit-front is-${frontEdge}" aria-hidden="true"><b>FRONT</b></span>` : '')}
          ${product.planningKind ? `<em class="jkb__room-feature-badge">${this.escape(product.familyLabel)}${Number(product.elevation) ? ` · +${Math.round(product.elevation)} mm` : ''}</em>` : ''}
          ${panelPlacementTargets.map(side => `
            <button type="button"
                    class="jkb__panel-drop is-${side.roomSide}"
                    data-panel-drop-unit="${unit.uid}"
                    data-panel-drop-side="${side.localSide}"
                    aria-label="Place end panel on ${side.localSide} end of ${this.escape(product.sku)}">+</button>
          `).join('')}
          ${appliedEndPanels.map(panel => `<span class="jkb__end-panel is-${panel.roomSide} run-${panel.edge}${panel.colourMismatch ? ' is-colour-mismatch' : ''}" data-panel-sku="${this.escape(panel.sku)}" style="--jkb-end-panel-thickness:${Math.max(3, panel.thickness * this.scale).toFixed(1)}px;--jkb-end-panel-depth:${Math.max(8, panel.depth * this.scale).toFixed(1)}px;--jkb-end-panel-colour:${this.endPanelCssColour(panel.colour)}" aria-hidden="true"></span>`).join('')}
          ${unit.uid === this.selectedId ? `
            <button type="button" class="jkb__unit-delete" data-unit-delete="${unit.uid}" aria-label="Remove ${this.escape(product.sku)}">×</button>
            <button type="button" class="jkb__unit-rotate" data-unit-rotate="${unit.uid}" aria-label="Rotate ${this.escape(product.sku)} 90 degrees" title="Rotate 90°">↻</button>
          ` : ''}
          ${unit.locked ? '<em class="jkb__locked-badge" title="Position locked">LOCKED</em>' : ''}
          ${this.justAddedIds.has(unit.uid) ? '<em class="jkb__just-added" title="Newly added cabinet">NEW</em>' : ''}
          ${!product.custom && !product.available ? '<em class="jkb__stock-badge" title="Out of stock – retained for planning">OUT OF STOCK</em>' : ''}
          ${heightWarnings.map(warning => `
            <b class="jkb__unit-warning is-${warning.side}"
               aria-label="${this.escape(warning.title)}"
               title="${this.escape(warning.title)}">
              <span class="jkb__warning-neighbour">${warning.direction}</span>
              <span class="jkb__warning-height">${warning.heightDirection}${warning.delta}<small>mm</small></span>
            </b>
          `).join('')}
        `;
        const panelTitle = appliedEndPanels.length
          ? ` · End panel: ${appliedEndPanels.map(panel => panel.sku).join(', ')}`
          : '';
        node.title = mismatch
          ? `${product.sku} · W${product.width} × D${product.depth} × H${product.height} mm · Height differs from adjacent ${this.product(mismatch).sku}${panelTitle}`
          : `${product.sku} · W${product.width} × D${fitInfo ? fitInfo.fittedDepth : product.depth} × H${product.height} mm${fitInfo ? ` · carcass D${fitInfo.carcassDepth} · rear gap ${fitInfo.serviceGap} · front allowance ${fitInfo.frontAllowance}` : ''} · ${product.colour}${panelTitle}`;
        this.el.room.appendChild(node);
      });

      this.renderAppliedPlinths2D();
    }

    renderAppliedPlinths2D() {
      if (!this.el.room) return;
      // Remove every previously-rendered plinth run before drawing the current
      // geometry. Older builds used data-applied-plinth2d (dataset camel-case)
      // while the cleanup selector expected data-applied-plinth-2d, so stale
      // runs accumulated after every render/resize until a full page refresh.
      this.el.room
        .querySelectorAll('[data-applied-plinth-2d], [data-applied-plinth2d]')
        .forEach(node => node.remove());

      for (const run of this.appliedPlinthRuns()) {
        const line = document.createElement('span');
        line.className = `jkb__plinth-run-2d is-front-${run.edge}`;
        line.setAttribute('data-applied-plinth-2d', run.anchorUnitId);
        line.classList.toggle('is-selected-run', Boolean(run.selected));
        line.style.setProperty('--jkb-plinth-run-colour', this.endPanelCssColour(run.colour));
        line.style.setProperty('--jkb-plinth-run-length', `${Math.max(8, run.length * this.scale)}px`);

        if (run.edge === 'top') {
          line.style.left = `${run.start * this.scale}px`;
          line.style.top = `${Math.max(0, run.front * this.scale - 3)}px`;
        } else if (run.edge === 'bottom') {
          line.style.left = `${run.start * this.scale}px`;
          line.style.top = `${Math.max(0, run.front * this.scale - 3)}px`;
        } else if (run.edge === 'left') {
          line.style.left = `${Math.max(0, run.front * this.scale - 3)}px`;
          line.style.top = `${run.start * this.scale}px`;
        } else if (run.edge === 'right') {
          line.style.left = `${Math.max(0, run.front * this.scale - 3)}px`;
          line.style.top = `${run.start * this.scale}px`;
        } else {
          continue;
        }

        line.innerHTML = `
          <i aria-hidden="true"></i>
          ${run.selected ? `<b>PLINTH · ${run.length} mm</b>` : ''}
        `;
        line.title = `${run.sku} · ${run.length} mm planned coverage across ${run.unitIds.length} connected cabinet${run.unitIds.length === 1 ? '' : 's'} · supplied as full 2700 mm length; customer cuts to required size`;
        this.el.room.appendChild(line);
      }
    }

    revealJustAdded(uid) {
      if (!uid) return;
      const node = this.el.room.querySelector(`[data-unit-id="${uid}"]`);
      if (!node) return;

      node.classList.add('is-just-added');

      if (!this.isMobile() || !this.el.quickSearchWrap) return;

      const nodeRect = node.getBoundingClientRect();
      const searchRect = this.el.quickSearchWrap.getBoundingClientRect();
      const stageRect = this.el.stage.getBoundingClientRect();
      const overlaps = nodeRect.left < searchRect.right &&
        nodeRect.right > searchRect.left &&
        nodeRect.top < searchRect.bottom &&
        nodeRect.bottom > searchRect.top;

      if (!overlaps) return;

      const moveToTop = nodeRect.top + nodeRect.height / 2 > stageRect.top + stageRect.height / 2;
      const top = moveToTop
        ? 6
        : Math.max(6, stageRect.height - searchRect.height - 6);

      Object.assign(this.el.quickSearchWrap.style, {
        left: '6px',
        right: '6px',
        top: top + 'px',
        bottom: 'auto',
        width: 'auto',
        transform: 'none'
      });
    }

    nearestRoomEdge(unit) {
      const r = this.rect(unit);
      const distances = {
        top: r.y,
        bottom: Math.max(0, this.roomDepth - (r.y + r.depth)),
        left: r.x,
        right: Math.max(0, this.roomWidth - (r.x + r.width))
      };
      return Object.entries(distances).sort((a, b) => a[1] - b[1])[0][0];
    }

    nearestRoomCorner(unit) {
      const r = this.rect(unit);
      const centreX = r.x + r.width / 2;
      const centreY = r.y + r.depth / 2;
      const horizontal = centreX <= this.roomWidth / 2 ? 'left' : 'right';
      const vertical = centreY <= this.roomDepth / 2 ? 'top' : 'bottom';
      return `${vertical}-${horizontal}`;
    }

    cornerOrientation(unit) {
      const order = ['top-left', 'bottom-left', 'bottom-right', 'top-right'];
      const base = order.indexOf(this.nearestRoomCorner(unit));
      const turn = ((Number(unit?.cornerTurn) || 0) % 4 + 4) % 4;
      return order[(Math.max(0, base) + turn) % 4];
    }

    renderSelection() {
      this.sync2DSelectionDock();
      const unit = this.selected();
      const desktopDock = this.el.selection?.classList.contains('is-desktop-dock');
      this.el.selection?.classList.toggle('is-empty', !unit);
      this.el.selection.hidden = !unit && !desktopDock;
      if (!unit) {
        if (this.el.endPanelPlacement) {
          this.el.endPanelPlacement.hidden = true;
          this.el.endPanelPlacement.innerHTML = '';
        }
        if (this.el.plinthPlacement) {
          this.el.plinthPlacement.hidden = true;
          this.el.plinthPlacement.innerHTML = '';
        }
        if (this.el.selected3d) this.el.selected3d.hidden = true;
        this.viewer3d?.setSelected(null);
        return;
      }
      const product = this.product(unit);
      const mismatch = this.heightMismatchFor(unit);
      this.el.selectionTitle.textContent = `${product.sku} · ${product.colour}`;
      const dims = this.unitDimensions(unit);
      const wallLeft = Math.max(0, Math.round(unit.x));
      const wallRight = Math.max(0, Math.round(this.roomWidth - (unit.x + dims.width)));
      const wallTop = Math.max(0, Math.round(unit.y));
      const wallBottom = Math.max(0, Math.round(this.roomDepth - (unit.y + dims.depth)));
      const frontText = product.planningKind ? '' : this.frontDirectionText(unit);
      const fitInfo = this.fittedRunInfo(unit);
      const appliedPanels = this.appliedEndPanelsForUnit(unit);
      const panelText = appliedPanels.length
        ? ` · END PANEL: ${appliedPanels.map(panel => `${panel.sku} ${panel.roomSide}`).join(' + ')}`
        : '';
      const belowUnits = product.category === 'wall' ? this.floorUnitsBelowWall(unit) : [];
      const stackText = belowUnits.length
        ? ` · above ${belowUnits.map(item => this.product(item)?.sku || 'base').join(', ')}`
        : '';
      const depthText = fitInfo
        ? `D${fitInfo.fittedDepth} fitted · carcass D${fitInfo.carcassDepth} · rear gap ${fitInfo.serviceGap} mm · front ${fitInfo.frontAllowance} mm${fitInfo.includedWorktop ? ' worktop overhang' : ' worktop allowance'}`
        : `D${product.depth}`;
      this.el.selectionSize.textContent = `W${product.width} × ${depthText} × H${product.height} mm${frontText ? ` · FRONT ${frontText}` : ''}${!product.custom && !product.available ? ' · OUT OF STOCK – planning only' : ''}${panelText}${stackText} · Wall gaps ←${wallLeft} →${wallRight} ↑${wallTop} ↓${wallBottom} mm${mismatch ? ` · Height ${product.height} → ${this.product(mismatch).height} mm` : ''}`;
      this.renderEndPanelPlacementControls(this.el.endPanelPlacement, unit);
      this.renderPlinthPlacementControls(this.el.plinthPlacement, unit);
      if (this.el.moveStep) {
        this.el.moveStep.textContent = `${this.precisionStep} mm`;
        this.el.moveStep.setAttribute('aria-pressed', this.precisionStep === 10 ? 'true' : 'false');
        this.el.moveStep.removeAttribute('title');
        this.el.moveStep.setAttribute(
          'aria-label',
          this.precisionStep === 10
            ? 'Fine 10 millimetre movement. Tap for 50 millimetres.'
            : 'Normal 50 millimetre movement. Tap for 10 millimetre fine adjustment.'
        );
      }
      if (this.el.snapWall) {
        const isCorner = Boolean(product.cornerShape || product.category === 'corner');
        this.el.snapWall.textContent = isCorner ? 'Snap to corner' : 'Snap to wall';
        this.el.snapWall.setAttribute('aria-label', isCorner ? 'Move cabinet to nearest clear corner now' : 'Move cabinet to nearest clear wall now');
      }
      if (this.el.wallLink) {
        const supportsFreeFacing = !product.planningKind && !product.cornerShape && product.category !== 'corner';
        this.el.wallLink.hidden = !supportsFreeFacing;
        this.el.wallLink.textContent = unit.wallLinked === false ? 'Wall: OFF' : 'Wall: ON';
        this.el.wallLink.setAttribute('aria-label', unit.wallLinked === false ? 'Turn automatic wall alignment on' : 'Turn automatic wall alignment off for free positioning');
        this.el.wallLink.setAttribute('aria-pressed', unit.wallLinked === false ? 'false' : 'true');
        this.el.wallLink.classList.toggle('is-active', unit.wallLinked !== false);
      }
      if (this.el.serviceGap) {
        const canToggleGap = this.supportsServiceGapToggle(unit);
        this.el.serviceGap.classList.toggle('is-gap-applicable', canToggleGap);
        this.el.serviceGap.hidden = !canToggleGap;
        const gapHelp = this.root.querySelector('[data-service-gap-help]');
        if (gapHelp) {
          gapHelp.classList.toggle('is-gap-applicable', canToggleGap);
          gapHelp.hidden = !canToggleGap;
        }
        if (canToggleGap) {
          const gap = this.serviceGapForUnit(unit);
          const standard = this.defaultServiceGap(product);
          this.el.serviceGap.textContent = `Rear gap ${Math.round(gap)} mm`;
          this.el.serviceGap.title = gap > 0
            ? `Required position for this 463 mm carcass: leave ${Math.round(standard)} mm behind it so the front sits at 578 mm and a 600 mm worktop leaves about 22 mm overhang.`
            : `Rear gap is off. Restore ${Math.round(standard)} mm for the normal 600 mm worktop position.`;
          this.el.serviceGap.setAttribute('aria-pressed', gap > 0 ? 'true' : 'false');
        }
      }
      if (this.el.lock) {
        this.el.lock.textContent = unit.locked ? 'Unlock' : 'Lock';
        this.el.lock.setAttribute('aria-pressed', unit.locked ? 'true' : 'false');
      }
      this.render3DSelection();
    }

    endPanelSummaryStatus(product, qty) {
      if (!product || !this.endPanelSpec(product.sku)) return '';
      const total = Math.max(0, Number(qty) || 0);
      const placed = Math.min(total, this.endPanelAssignedCount(product.id));
      const unplaced = Math.max(0, total - placed);
      return `${placed} placed · ${unplaced} unplaced`;
    }

    renderUnplacedEndPanelTray() {
      const tray = this.el.panelUnplacedTray;
      if (!tray) return;

      const lines = [];
      for (const [productId, rawQty] of Object.entries(this.extras || {})) {
        const product = this.productById.get(String(productId));
        if (!product || !this.endPanelSpec(product.sku)) continue;

        const available = this.availableEndPanelQuantity(product.id);
        if (available < 1) continue;

        const family = this.endPanelFamily(product.sku);
        const targetLabel = family === 'ALWEND'
          ? 'wall cabinet'
          : family === 'ALBEND'
            ? 'adjustable AL base cabinet'
            : family === 'ALDEC'
              ? 'T400 or ALT500 tall cabinet'
              : 'standard base cabinet';
        const active = String(this.activeEndPanelProductId || '') === String(product.id);

        lines.push(`
          <div class="jkb__panel-tray-line${active ? ' is-active' : ''}">
            <div>
              <b>${this.escape(product.sku)} · ${this.escape(product.colour || '')}</b>
              <span>${available} unplaced · place on an exposed ${targetLabel} end</span>
            </div>
            ${active
              ? `<button type="button" data-panel-cancel-place="${product.id}">Cancel</button>`
              : `<button type="button" data-panel-start-place="${product.id}">Position</button>`}
          </div>
        `);
      }

      if (!lines.length) {
        tray.hidden = true;
        tray.innerHTML = '';
        return;
      }

      tray.hidden = false;
      tray.innerHTML = `
        <div class="jkb__panel-tray-head">
          <div>
            <b>Unplaced end panels</b>
            <span>Choose a panel, then tap the + on the exact exposed cabinet end.</span>
          </div>
        </div>
        <div class="jkb__panel-tray-lines">${lines.join('')}</div>
      `;
    }

    renderSummary() {
      const grouped = new Map();
      let total = 0;
      let basketQty = 0;

      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product) continue;
        if (!product.custom) {
          total += Number(product.price) || 0;
          basketQty += 1;
        }
        const key = String(product.id);
        if (!grouped.has(key)) grouped.set(key, { product, qty: 0 });
        grouped.get(key).qty += 1;
      }

      for (const [productId, qtyRaw] of Object.entries(this.extras || {})) {
        const product = this.productById.get(String(productId));
        const qty = Number(qtyRaw) || 0;
        if (!product || qty < 1) continue;
        if (!product.custom) {
          total += (Number(product.price) || 0) * qty;
          basketQty += qty;
        }
        const key = String(product.id);
        if (!grouped.has(key)) grouped.set(key, { product, qty: 0 });
        grouped.get(key).qty += qty;
      }

      if (!grouped.size) {
        this.el.summary.innerHTML = '<p class="jkb__summary-empty">No products added yet.</p>';
      } else {
        this.el.summary.innerHTML = [...grouped.values()].map(({ product, qty }) => `
          <div class="jkb__summary-line">
            <div class="jkb__summary-thumb">
              ${product.image
                ? `<img src="${this.escape(product.image)}" alt="" loading="lazy">`
                : '<span aria-hidden="true">▣</span>'}
            </div>

            <div class="jkb__summary-copy">
              <strong class="jkb__summary-title">${this.escape(product.productTitle)}</strong>
              <span class="jkb__summary-meta">${this.escape(product.sku)} · ${this.escape(product.familyLabel)}${this.endPanelSpec(product.sku) ? ' · EXTRA ITEM' : ''}</span>
              <small>${product.placeable
                ? (this.isFittedBaseRun(product)
                    ? `W${product.width} · carcass D${product.depth} · fitted D600 · H${product.height}`
                    : `W${product.width} · D${product.depth} · H${product.height}`)
                : this.escape(product.colour || 'Basket item')}${this.endPanelSpec(product.sku) ? ` · ${this.endPanelSummaryStatus(product, qty)}` : ''}${product.category === 'plinth' && this.appliedPlinthLengthByProduct(product.id) > 0 ? ` · ${this.appliedPlinthLengthByProduct(product.id)} mm applied · cut from ${qty} × 2700 mm` : ''}${!product.custom && !product.available ? ' · OUT OF STOCK' : ''}</small>
            </div>

            <div class="jkb__summary-actions">
              <span class="line-price ${product.custom ? 'is-plan-only' : (!product.available ? 'is-out-of-stock' : '')}">${product.custom ? 'Plan only' : (!product.available ? `Out of stock · ${this.money(product.price * qty)}` : this.money(product.price * qty))}</span>
              ${this.endPanelSpec(product.sku) && this.availableEndPanelQuantity(product.id) > 0
                ? (String(this.activeEndPanelProductId || '') === String(product.id)
                    ? `<button type="button" class="jkb__panel-place-action is-active" data-panel-cancel-place="${product.id}">Cancel</button>`
                    : `<button type="button" class="jkb__panel-place-action" data-panel-start-place="${product.id}">Position</button>`)
                : ''}
              <div class="jkb__summary-controls">
                <div class="jkb__summary-qty" aria-label="Quantity for ${this.escape(product.sku)}">
                  <button type="button" data-summary-qty="-1" data-product-id="${product.id}" aria-label="Decrease ${this.escape(product.sku)}">−</button>
                  <span>${qty}</span>
                  <button type="button" data-summary-qty="1" data-product-id="${product.id}" aria-label="Increase ${this.escape(product.sku)}">+</button>
                </div>
                <button type="button" class="jkb__summary-line-remove" data-summary-remove-line="${product.id}" aria-label="Remove ${this.escape(product.sku)}">×</button>
              </div>
            </div>
          </div>
        `).join('');
      }

      this.renderUnplacedEndPanelTray();
      this.el.total.textContent = this.money(total);
      this.renderFitAssistant();
      this.renderHeightAlert();

      const totalQty = [...grouped.values()].reduce((sum, line) => sum + line.qty, 0);
      if (this.el.summaryCount) this.el.summaryCount.textContent = totalQty ? `${totalQty} item${totalQty === 1 ? '' : 's'}` : '';
      this.updateCheckoutState(basketQty > 0);
      requestAnimationFrame(() => this.updateSummaryScrollHint());
    }

    edgeDistance(unit) {
      const r = this.rect(unit);
      return Math.min(
        r.x,
        r.y,
        Math.max(0, this.roomWidth - (r.x + r.width)),
        Math.max(0, this.roomDepth - (r.y + r.depth))
      );
    }

    runEdge(unit) {
      const tolerance = Math.max(100, this.grid * 2);
      if (this.edgeDistance(unit) > tolerance) return 'free';
      return this.nearestRoomEdge(unit);
    }

    edgeLabel(edge) {
      return ({
        top: 'Top wall',
        bottom: 'Bottom wall',
        left: 'Left wall',
        right: 'Right wall',
        free: 'Free-standing'
      })[edge] || 'Run';
    }

    runLength(unit, edge = null) {
      const product = this.product(unit);
      if (!product) return 0;

      // Worktop/plinth material follows the cabinet FRONTAGE.
      // Rotation changes which room wall the cabinet sits on, but it does not
      // change the cabinet's nominal front width.
      return Math.max(0, Number(product.width) || 0);
    }

    isIncludedWorktopSku(sku) {
      // Worktop is the final W after the frontage colour code:
      // B300DGW, B300GW, B300WGLW, B300WW, etc.
      return /^(?:AL)?(?:B|DR|S|O)\d{3,4}(?:WGL|DG|G|W|S|N|C)W$/
        .test(String(sku || '').toUpperCase());
    }

    hasIncludedWorktop(product) {
      if (!product) return false;
      const text = `${product.productTitle || ''} ${product.variantTitle || ''}`.toLowerCase();
      return this.isIncludedWorktopSku(product.sku) ||
        /pre[- ]?cut worktop|worktop included|includes? (?:a )?worktop|with worktop/.test(text);
    }

    isWorktopUnit(product) {
      if (!product || product.planningKind || product.category === 'wall' || product.category === 'tall' || Boolean(product.cornerShape || product.category === 'corner')) return false;
      if (product.standalone || /^TS/.test(String(product.sku || '').toUpperCase())) return false;
      return ['base', 'drawer', 'sink', 'oven'].includes(product.category);
    }

    isFittedBaseRun(product) {
      return Boolean(product && !product.custom && this.isWorktopUnit(product));
    }

    fittedRunDepth(product) {
      if (!product) return 0;
      return this.isFittedBaseRun(product)
        ? 600
        : Math.max(0, Number(product.depth) || 0);
    }

    defaultServiceGap(product) {
      if (!this.isFittedBaseRun(product)) return 0;
      const sku = String(product.sku || '').toUpperCase();
      if (/^AL/.test(sku)) return 0;

      // Fixed-height JD Greta carcasses are 463 mm deep. Standard installation
      // keeps the carcass front on the same 578 mm line as AL units, leaving
      // ~22 mm for a 600 mm worktop front overhang.
      return Math.max(0, 578 - (Number(product.depth) || 0));
    }

    serviceGapForUnit(unit) {
      const product = unit ? this.product(unit) : null;
      if (!product || !this.isFittedBaseRun(product)) return 0;
      if (/^AL/.test(String(product.sku || '').toUpperCase())) return 0;
      if (this.hasIncludedWorktop(product)) return this.defaultServiceGap(product);

      const rawOverride = unit?.serviceGapOverride;
      if (rawOverride !== null && rawOverride !== undefined && rawOverride !== '') {
        const override = Number(rawOverride);
        if (Number.isFinite(override)) {
          return this.clamp(override, 0, this.defaultServiceGap(product));
        }
      }
      return this.defaultServiceGap(product);
    }

    supportsServiceGapToggle(unit) {
      const product = unit ? this.product(unit) : null;
      if (!unit || !product || unit.wallLinked === false) return false;
      if (this.edgeDistance(unit) > Math.max(100, this.grid * 2)) return false;
      if (!this.isFittedBaseRun(product) || /^AL/.test(String(product.sku || '').toUpperCase())) return false;

      // Only the standard 463 mm carcasses need the 115 mm rear service gap.
      // AL carcasses are already 578 mm deep and other depths should not expose
      // a misleading "Gap 115" control.
      const carcassDepth = Math.round(Number(product.depth) || 0);
      if (carcassDepth !== 463) return false;

      // Included-worktop products always keep the correct 600 mm fitted line.
      return !this.hasIncludedWorktop(product) && this.defaultServiceGap(product) > 0;
    }

    fittedRunInfo(unit) {
      const product = unit ? this.product(unit) : null;
      if (!product || !this.isFittedBaseRun(product)) return null;

      const fittedDepth = this.fittedRunDepth(product);
      const carcassDepth = Math.max(0, Number(product.depth) || 0);
      const serviceGap = this.serviceGapForUnit(unit);
      const frontAllowance = Math.max(0, fittedDepth - serviceGap - carcassDepth);

      return {
        fittedDepth,
        carcassDepth,
        serviceGap,
        frontAllowance,
        includedWorktop: this.hasIncludedWorktop(product),
        adjustable: /^AL/.test(String(product.sku || '').toUpperCase())
      };
    }

    toggleSelectedServiceGap() {
      const unit = this.selected();
      if (!this.supportsServiceGapToggle(unit)) return false;
      if (unit.locked) {
        this.setStatus('Unlock this cabinet before changing its rear service gap.');
        return false;
      }

      const product = this.product(unit);
      const standard = this.defaultServiceGap(product);
      const current = this.serviceGapForUnit(unit);
      unit.serviceGapOverride = current > 0 ? 0 : standard;

      this.renderAll();
      this.save();

      if (unit.serviceGapOverride > 0) {
        this.setStatus(`${product.sku}: ${standard} mm service gap restored. Carcass front aligns at 578 mm; a 600 mm worktop leaves about 22 mm overhang.`);
      } else {
        this.setStatus(`${product.sku}: carcass moved ${standard} mm back to the wall. Standard position is ${standard} mm forward for the 600 mm fitted line.`);
      }
      return true;
    }

    isPlinthCompatibleCabinet(product) {
      if (!product || product.planningKind || product.category === 'wall') return false;
      return /^AL/.test(String(product.sku || '').toUpperCase());
    }

    plinthRunForUnit(unit) {
      const cabinet = unit ? this.product(unit) : null;
      if (!unit || !this.isPlinthCompatibleCabinet(cabinet)) return null;

      const colour = String(cabinet.colour || '').trim().toLowerCase();
      const edge = this.frontEdgeFor(unit);
      if (!['top', 'bottom', 'left', 'right'].includes(edge)) return null;

      // L-corner plinths turn through 90 degrees, so do not merge them into a
      // straight run. Linear AL units on the same front line are grouped.
      const isCorner = Boolean(cabinet.cornerShape || cabinet.category === 'corner');
      const horizontal = edge === 'top' || edge === 'bottom';
      const tolerance = Math.max(20, this.grid / 2);

      const metrics = candidate => {
        const rect = this.rect(candidate);
        return {
          unit: candidate,
          product: this.product(candidate),
          start: horizontal ? rect.x : rect.y,
          end: horizontal ? rect.x + rect.width : rect.y + rect.depth,
          front: edge === 'top'
            ? rect.y
            : edge === 'bottom'
              ? rect.y + rect.depth
              : edge === 'left'
                ? rect.x
                : rect.x + rect.width
        };
      };

      const seed = metrics(unit);
      let members = [seed];

      if (!isCorner) {
        const candidates = this.units
          .filter(other => {
            const product = this.product(other);
            if (!product || !this.isPlinthCompatibleCabinet(product)) return false;
            if (Boolean(product.cornerShape || product.category === 'corner')) return false;
            if (String(product.colour || '').trim().toLowerCase() !== colour) return false;
            return this.frontEdgeFor(other) === edge;
          })
          .map(metrics);

        const connected = new Set([String(unit.uid)]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const candidate of candidates) {
            if (connected.has(String(candidate.unit.uid))) continue;

            const touchesRun = candidates.some(existing =>
              connected.has(String(existing.unit.uid)) &&
              Math.abs(candidate.front - existing.front) <= tolerance &&
              Math.max(0, Math.max(candidate.start, existing.start) - Math.min(candidate.end, existing.end)) <= tolerance
            );

            if (touchesRun) {
              connected.add(String(candidate.unit.uid));
              changed = true;
            }
          }
        }

        members = candidates.filter(item => connected.has(String(item.unit.uid)));
      }

      members.sort((a, b) => a.start - b.start);
      const runStart = Math.min(...members.map(item => item.start));
      const runEnd = Math.max(...members.map(item => item.end));
      const front = members.reduce((sum, item) => sum + item.front, 0) / Math.max(1, members.length);
      const unitIds = members.map(item => String(item.unit.uid));
      const anchor = members[0]?.unit || unit;

      return {
        key: unitIds.slice().sort().join('|'),
        anchorUnitId: String(anchor.uid),
        unitIds,
        units: members.map(item => item.unit),
        products: members.map(item => item.product),
        edge,
        horizontal,
        start: runStart,
        end: runEnd,
        front,
        length: Math.max(100, Math.round(runEnd - runStart)),
        colour: String(cabinet.colour || 'White')
      };
    }

    plinthApplicationForRun(run) {
      if (!run) return null;
      const runIds = new Set(run.unitIds.map(String));

      return (this.plinthApplications || []).find(item => {
        const anchorId = String(item.unitId || '');
        if (runIds.has(anchorId)) return true;
        return (Array.isArray(item.unitIds) ? item.unitIds : [])
          .some(id => runIds.has(String(id)));
      }) || null;
    }

    plinthApplicationForUnit(unitId) {
      const unit = this.units.find(item => String(item.uid) === String(unitId || ''));
      const run = unit ? this.plinthRunForUnit(unit) : null;
      if (run) return this.plinthApplicationForRun(run);

      return (this.plinthApplications || []).find(item =>
        String(item.unitId || '') === String(unitId || '') ||
        (Array.isArray(item.unitIds) && item.unitIds.some(id => String(id) === String(unitId || '')))
      ) || null;
    }

    appliedPlinthRuns() {
      const output = [];
      const seen = new Set();

      for (const application of this.plinthApplications || []) {
        const anchor = this.units.find(item => String(item.uid) === String(application.unitId || '')) ||
          this.units.find(item => (Array.isArray(application.unitIds) ? application.unitIds : [])
            .some(id => String(id) === String(item.uid)));
        if (!anchor) continue;

        const run = this.plinthRunForUnit(anchor);
        if (!run || seen.has(run.key)) continue;
        seen.add(run.key);

        const plinthProduct = this.productById.get(String(application.productId || ''));
        const length = this.clamp(
          Math.round(Number(application.length) || run.length),
          100,
          10000
        );

        output.push({
          productId: String(application.productId || ''),
          anchorUnitId: run.anchorUnitId,
          unitIds: run.unitIds.slice(),
          edge: run.edge,
          start: run.start,
          end: run.end,
          front: run.front,
          detectedLength: run.length,
          length,
          colour: String(plinthProduct?.colour || run.colour || 'White'),
          sku: String(plinthProduct?.sku || ''),
          selected: run.unitIds.some(id => String(id) === String(this.selectedId || ''))
        });
      }

      return output;
    }

    appliedPlinthLengthByProduct(productId) {
      const target = String(productId || '');
      return (this.plinthApplications || [])
        .filter(item => String(item.productId || '') === target)
        .reduce((sum, item) => sum + Math.max(0, Number(item.length) || 0), 0);
    }

    requiredPlinthSticks(productId) {
      const mm = this.appliedPlinthLengthByProduct(productId);
      return mm > 0 ? Math.ceil(mm / 2700) : 0;
    }

    syncAppliedPlinthBasket(productId) {
      const target = String(productId || '');
      if (!target) return;
      const required = this.requiredPlinthSticks(target);
      const current = Math.max(0, Number(this.extras?.[target]) || 0);
      if (required > current) this.extras[target] = required;
    }

    renderPlinthPlacementControls(container, unit) {
      if (!container) return;
      const cabinet = unit ? this.product(unit) : null;
      const run = unit ? this.plinthRunForUnit(unit) : null;

      if (!run || !cabinet) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      const plinth = this.matchingLongPlinthProduct(cabinet.colour);
      const applied = this.plinthApplicationForRun(run);
      const length = applied
        ? this.clamp(Math.round(Number(applied.length) || run.length), 100, 10000)
        : run.length;
      const required = applied && plinth ? this.requiredPlinthSticks(plinth.id) : 0;
      const totalApplied = applied && plinth ? this.appliedPlinthLengthByProduct(plinth.id) : 0;
      const leftover = required ? Math.max(0, required * 2700 - totalApplied) : 0;
      const runSkus = run.products.map(product => String(product?.sku || '')).filter(Boolean);
      const runLabel = runSkus.length > 3
        ? `${runSkus.slice(0, 3).join(' + ')} + ${runSkus.length - 3} more`
        : runSkus.join(' + ');

      const content = `
        <div class="jkb__plinth-placement-head">
          <div>
            <b>Optional colour-matched plinth · whole run</b>
            <span>${run.unitIds.length} cabinet${run.unitIds.length === 1 ? '' : 's'} together · detected run ${run.length} mm · sold as full 2700 mm lengths for the customer to cut on site</span>
          </div>
          <em>White plinth is already included</em>
        </div>
        ${plinth ? `
          <div class="jkb__plinth-run-summary">
            <b>${this.escape(runLabel)}</b>
            <span>Apply once for this continuous ${this.escape(cabinet.colour || '')} AL run — not once per cabinet.</span>
          </div>
          <div class="jkb__plinth-action-help">
            Enter the run length to cover, then press <b>${applied ? 'Update plinth length' : 'Add plinth to basket'}</b>. We calculate how many full 2700 mm lengths are required. We do not cut the plinth.
          </div>
          <div class="jkb__plinth-placement-row">
            <label>
              <span>Run length to cover</span>
              <div><input type="number" min="100" max="10000" step="10" value="${length}" data-plinth-length> <small>mm</small></div>
            </label>
            <button type="button" class="jkb__plinth-primary-action" data-plinth-apply>${applied ? 'Update plinth length' : 'Add plinth to basket'}</button>
            <div class="jkb__plinth-product">
              <b>${this.escape(plinth.sku)} · ${this.escape(cabinet.colour || '')}</b>
              <span>${this.money(plinth.price)} per full 2700 mm length · supplied uncut</span>
            </div>
            ${applied ? '<button type="button" class="is-remove" data-plinth-remove>Remove plinth</button>' : ''}
          </div>
          ${applied ? `
            <div class="jkb__plinth-cut-note">
              <b>${length} mm planned coverage</b>
              <span>Basket quantity: ${required} × full 2700 mm length${required === 1 ? '' : 's'}. Customer cuts to the required size${leftover ? ` · approx. ${leftover} mm spare after the planned cuts` : ''}.</span>
            </div>
          ` : ''}
        ` : `
          <div class="jkb__plinth-unavailable">
            Matching ${this.escape(cabinet.colour || '')} 2700 mm plinth is not currently available in the builder catalogue.
          </div>
        `}
`;

      container.hidden = false;
      const existingDetails = container.querySelector('details');
      const keepOpen = Boolean(
        existingDetails?.open &&
        existingDetails.dataset.unitId === String(unit.uid)
      );
      const summaryStatus = applied
        ? `${length} mm cover`
        : `${run.length} mm run`;
      const detailsClass = container === this.el.plinthPlacement3d
        ? 'jkb__3d-addon'
        : 'jkb__selection-addon';
      const bodyClass = container === this.el.plinthPlacement3d
        ? 'jkb__3d-addon-body'
        : 'jkb__selection-addon-body';
      container.innerHTML = `
        <details class="${detailsClass}" data-unit-id="${this.escape(String(unit.uid))}"${keepOpen ? ' open' : ''}>
          <summary>
            <b>Plinth</b>
            <span>${summaryStatus}</span>
          </summary>
          <div class="${bodyClass}">${content}</div>
        </details>
      `;
    }

    applySelectedPlinth(container = null) {
      const unit = this.selected();
      const cabinet = unit ? this.product(unit) : null;
      const run = unit ? this.plinthRunForUnit(unit) : null;
      if (!unit || !cabinet || !run) {
        this.setStatus('Colour-matched 2.7 m plinth can only be applied to AL adjustable floor cabinet runs.');
        return false;
      }

      const plinth = this.matchingLongPlinthProduct(cabinet.colour);
      if (!plinth) {
        this.setStatus(`No matching ${cabinet.colour || ''} 2.7 m plinth was found in the catalogue.`);
        return false;
      }

      const input = container?.querySelector?.('[data-plinth-length]');
      const requested = Math.round(Number(input?.value) || run.length);
      const length = this.clamp(requested, 100, 10000);
      const runIds = new Set(run.unitIds.map(String));

      // A continuous run gets one application record. Remove older per-cabinet
      // applications that belong to the same current run before saving it.
      const replacedProductIds = new Set();
      this.plinthApplications = (this.plinthApplications || []).filter(item => {
        const ids = [String(item.unitId || ''), ...(Array.isArray(item.unitIds) ? item.unitIds.map(String) : [])];
        const overlaps = ids.some(id => runIds.has(id));
        if (overlaps && item.productId) replacedProductIds.add(String(item.productId));
        return !overlaps;
      });

      const application = {
        productId: String(plinth.id),
        unitId: run.anchorUnitId,
        unitIds: run.unitIds.slice(),
        length
      };
      this.plinthApplications.push(application);

      replacedProductIds.add(String(plinth.id));
      replacedProductIds.forEach(productId => {
        const required = this.requiredPlinthSticks(productId);
        const current = Math.max(0, Number(this.extras?.[productId]) || 0);
        if (required === 0) delete this.extras[productId];
        else if (current < required) this.extras[productId] = required;
      });

      this.renderAll();
      this.save();

      const sticks = this.requiredPlinthSticks(plinth.id);
      const total = this.appliedPlinthLengthByProduct(plinth.id);
      const leftover = Math.max(0, sticks * 2700 - total);
      this.setStatus(`${length} mm ${cabinet.colour} plinth coverage planned across ${run.unitIds.length} connected AL cabinet${run.unitIds.length === 1 ? '' : 's'}. Basket contains ${sticks} × full 2700 mm length${sticks === 1 ? '' : 's'}; customer cuts to the required size${leftover ? `; approx. ${leftover} mm spare` : ''}.`);
      return true;
    }

    removeSelectedPlinth() {
      const unit = this.selected();
      const run = unit ? this.plinthRunForUnit(unit) : null;
      if (!unit || !run) return false;
      const existing = this.plinthApplicationForRun(run);
      if (!existing) return false;

      const productId = String(existing.productId || '');
      const requiredBefore = this.requiredPlinthSticks(productId);
      this.plinthApplications = (this.plinthApplications || []).filter(item => item !== existing);

      const requiredAfter = this.requiredPlinthSticks(productId);
      const current = Math.max(0, Number(this.extras?.[productId]) || 0);
      const autoReduction = Math.max(0, requiredBefore - requiredAfter);
      const next = Math.max(requiredAfter, current - autoReduction);
      if (next === 0) delete this.extras[productId];
      else this.extras[productId] = next;

      this.renderAll();
      this.save();
      this.setStatus('Colour-matched plinth removed from this connected AL run.');
      return true;
    }

    plinthHeightFor(product) {
      if (!product || product.planningKind || product.category === 'wall') return 0;
      const sku = String(product.sku || '').toUpperCase();
      if (/^AL/.test(sku) || /^TS/.test(sku)) return 150;
      if (['base', 'drawer', 'sink', 'oven', 'corner', 'tall'].includes(product.category)) return 90;
      return product.custom && product.category !== 'wall' ? 90 : 0;
    }

    isRoomCorner(unit) {
      const r = this.rect(unit);
      const tolerance = Math.max(75, this.grid * 1.5);
      const left = r.x <= tolerance;
      const right = this.roomWidth - (r.x + r.width) <= tolerance;
      const top = r.y <= tolerance;
      const bottom = this.roomDepth - (r.y + r.depth) <= tolerance;
      return (left || right) && (top || bottom);
    }

    adjacentUnits(unit, layer = null) {
      const a = this.rect(unit);
      const tolerance = Math.max(10, this.grid / 2);
      const matches = [];

      for (const other of this.units) {
        if (other.uid === unit.uid) continue;
        const p = this.product(other);
        if (!p) continue;

        if (layer === 'wall' && p.category !== 'wall') continue;
        if (layer === 'floor' && p.category === 'wall') continue;

        const b = this.rect(other);
        const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
        const overlapY = Math.min(a.y + a.depth, b.y + b.depth) - Math.max(a.y, b.y);
        const touchesX = Math.abs((a.x + a.width) - b.x) <= tolerance ||
          Math.abs((b.x + b.width) - a.x) <= tolerance;
        const touchesY = Math.abs((a.y + a.depth) - b.y) <= tolerance ||
          Math.abs((b.y + b.depth) - a.y) <= tolerance;

        if ((touchesX && overlapY > tolerance) || (touchesY && overlapX > tolerance)) {
          matches.push(other);
        }
      }

      return matches;
    }

    exposedRunSides(unit, layer) {
      const edge = this.runEdge(unit);
      const a = this.rect(unit);
      const tolerance = Math.max(10, this.grid / 2);
      const boundaryTolerance = Math.max(20, this.grid / 2);

      if (edge === 'free') {
        const rotated = Number(unit.rotation) === 90;
        return [
          {
            end: 'start',
            edge,
            roomSide: rotated ? 'top' : 'left',
            localSide: 'left'
          },
          {
            end: 'end',
            edge,
            roomSide: rotated ? 'bottom' : 'right',
            localSide: 'right'
          }
        ];
      }

      const neighbours = this.units.filter(other => {
        if (other.uid === unit.uid) return false;
        const p = this.product(other);
        if (!p) return false;
        if (layer === 'wall' && p.category !== 'wall') return false;
        if (layer === 'floor' && p.category === 'wall') return false;
        return true;
      });

      let startExposed = false;
      let endExposed = false;

      if (edge === 'top' || edge === 'bottom') {
        const leftNeighbour = neighbours.some(other => {
          const b = this.rect(other);
          const overlapY = Math.min(a.y + a.depth, b.y + b.depth) - Math.max(a.y, b.y);
          return Math.abs((b.x + b.width) - a.x) <= tolerance && overlapY > tolerance;
        });
        const rightNeighbour = neighbours.some(other => {
          const b = this.rect(other);
          const overlapY = Math.min(a.y + a.depth, b.y + b.depth) - Math.max(a.y, b.y);
          return Math.abs((a.x + a.width) - b.x) <= tolerance && overlapY > tolerance;
        });
        startExposed = !leftNeighbour && a.x > boundaryTolerance;
        endExposed = !rightNeighbour && this.roomWidth - (a.x + a.width) > boundaryTolerance;
      } else {
        const upperNeighbour = neighbours.some(other => {
          const b = this.rect(other);
          const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
          return Math.abs((b.y + b.depth) - a.y) <= tolerance && overlapX > tolerance;
        });
        const lowerNeighbour = neighbours.some(other => {
          const b = this.rect(other);
          const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
          return Math.abs((a.y + a.depth) - b.y) <= tolerance && overlapX > tolerance;
        });
        startExposed = !upperNeighbour && a.y > boundaryTolerance;
        endExposed = !lowerNeighbour && this.roomDepth - (a.y + a.depth) > boundaryTolerance;
      }

      const mapping = {
        top: {
          start: { roomSide: 'left', localSide: 'left' },
          end: { roomSide: 'right', localSide: 'right' }
        },
        bottom: {
          start: { roomSide: 'left', localSide: 'right' },
          end: { roomSide: 'right', localSide: 'left' }
        },
        left: {
          start: { roomSide: 'top', localSide: 'right' },
          end: { roomSide: 'bottom', localSide: 'left' }
        },
        right: {
          start: { roomSide: 'top', localSide: 'left' },
          end: { roomSide: 'bottom', localSide: 'right' }
        }
      }[edge];

      const sides = [];
      if (startExposed) sides.push({ end: 'start', edge, ...mapping.start });
      if (endExposed) sides.push({ end: 'end', edge, ...mapping.end });
      return sides;
    }

    exposedRunEnds(unit, layer) {
      return this.exposedRunSides(unit, layer).length;
    }

    endPanelSkuForProduct(product) {
      if (
        !product ||
        product.planningKind ||
        product.custom ||
        product.standalone ||
        /^TS/.test(String(product.sku || '').toUpperCase()) ||
        Boolean(product.cornerShape || product.category === 'corner')
      ) return null;

      const sku = String(product.sku || '').toUpperCase();
      if (/^(?:T400|ALT500)(?:WGL|DG|G|W|S|N|C)?$/.test(sku)) {
        return 'ALDEC';
      }

      if (product.category === 'tall') return null;

      if (product.category === 'wall') {
        // The stocked wall end panel is 720 mm high. Do not apply it to
        // 400 mm bridging/extractor units.
        return Number(product.height) === 720 ? 'ALWEND' : null;
      }

      if (!['base', 'drawer', 'sink', 'oven'].includes(product.category)) return null;
      return /^AL/.test(String(product.sku || '').toUpperCase())
        ? 'ALBEND'
        : 'GBEND';
    }

    endPanelAssignedCount(productId) {
      const target = String(productId || '');
      return (this.endPanelAssignments || []).filter(item =>
        String(item.productId || '') === target
      ).length;
    }

    availableEndPanelQuantity(productId) {
      const target = String(productId || '');
      const purchased = Math.max(0, Number(this.extras?.[target]) || 0);
      return Math.max(0, purchased - this.endPanelAssignedCount(target));
    }

    endPanelInventoryForFamily(family) {
      const targetFamily = String(family || '').toUpperCase();
      const items = [];

      for (const [productId, rawQty] of Object.entries(this.extras || {})) {
        const product = this.productById.get(String(productId));
        const info = product ? this.endPanelInfo(product.sku) : null;
        const qty = Math.max(0, Number(rawQty) || 0);
        if (!product || !info || info.family !== targetFamily || qty < 1) continue;

        const assigned = this.endPanelAssignedCount(productId);
        items.push({
          product,
          family: info.family,
          purchased: qty,
          assigned,
          available: Math.max(0, qty - assigned)
        });
      }

      return items;
    }

    preferredAvailableEndPanel(unit) {
      const cabinet = unit ? this.product(unit) : null;
      const family = cabinet ? this.endPanelSkuForProduct(cabinet) : null;
      if (!cabinet || !family) return null;

      const inventory = this.endPanelInventoryForFamily(family)
        .filter(item => item.available > 0);
      if (!inventory.length) return null;

      const colour = String(cabinet.colour || '').toLowerCase();
      return inventory.find(item =>
        String(item.product.colour || '').toLowerCase() === colour
      ) || inventory[0];
    }

    panelSideGeometry(unit, localSide) {
      if (!unit || !['left', 'right'].includes(localSide)) return null;
      const edge = this.runEdge(unit);

      const exposed = this.exposedRunSides(
        unit,
        this.product(unit)?.category === 'wall' ? 'wall' : 'floor'
      ).find(side => side.localSide === localSide);
      if (exposed) return { ...exposed, exposed: true };

      if (edge === 'free') {
        const rotated = Number(unit.rotation) === 90;
        return {
          end: localSide === 'left' ? 'start' : 'end',
          edge,
          roomSide: rotated
            ? (localSide === 'left' ? 'top' : 'bottom')
            : localSide,
          localSide,
          exposed: false
        };
      }

      const mapping = {
        top: {
          left: { end: 'start', roomSide: 'left' },
          right: { end: 'end', roomSide: 'right' }
        },
        bottom: {
          left: { end: 'end', roomSide: 'right' },
          right: { end: 'start', roomSide: 'left' }
        },
        left: {
          left: { end: 'end', roomSide: 'bottom' },
          right: { end: 'start', roomSide: 'top' }
        },
        right: {
          left: { end: 'start', roomSide: 'top' },
          right: { end: 'end', roomSide: 'bottom' }
        }
      };

      const resolved = mapping[edge]?.[localSide];
      return resolved
        ? { ...resolved, edge, localSide, exposed: false }
        : null;
    }

    endPanelPlacements() {
      const placements = [];
      const purchasedUse = new Map();

      for (const assignment of this.endPanelAssignments || []) {
        const productId = String(assignment?.productId || '');
        const unitId = String(assignment?.unitId || '');
        const localSide = assignment?.localSide === 'right' ? 'right' : 'left';
        if (!productId || !unitId) continue;

        const panelProduct = this.productById.get(productId);
        const panelInfo = panelProduct ? this.endPanelInfo(panelProduct.sku) : null;
        const unit = this.units.find(item => String(item.uid) === unitId);
        const cabinet = unit ? this.product(unit) : null;
        const requiredFamily = cabinet ? this.endPanelSkuForProduct(cabinet) : null;
        if (!panelProduct || !panelInfo || !unit || !cabinet || panelInfo.family !== requiredFamily) continue;

        const purchased = Math.max(0, Number(this.extras?.[productId]) || 0);
        const used = purchasedUse.get(productId) || 0;
        if (used >= purchased) continue;
        purchasedUse.set(productId, used + 1);

        const side = this.panelSideGeometry(unit, localSide);
        if (!side) continue;

        const spec = this.endPanelSpec(panelProduct.sku);
        placements.push({
          unitId,
          productId,
          sku: String(panelProduct.sku || panelInfo.canonical),
          family: panelInfo.family,
          colour: String(panelProduct.colour || panelInfo.colour || 'White'),
          cabinetColour: String(cabinet.colour || ''),
          colourMismatch:
            String(cabinet.colour || '').toLowerCase() !==
            String(panelProduct.colour || panelInfo.colour || 'White').toLowerCase(),
          ...side,
          height: spec?.height || 820,
          depth: spec?.depth || Number(cabinet.depth) || 500,
          thickness: spec?.thickness || 16
        });
      }

      return placements;
    }

    assignmentForUnitSide(unitId, localSide) {
      return (this.endPanelAssignments || []).find(item =>
        String(item.unitId || '') === String(unitId || '') &&
        String(item.localSide || '') === String(localSide || '')
      ) || null;
    }

    matchingUnplacedEndPanel(unit) {
      const cabinet = unit ? this.product(unit) : null;
      const family = cabinet ? this.endPanelSkuForProduct(cabinet) : null;
      if (!cabinet || !family) return null;

      const colour = String(cabinet.colour || '').trim().toLowerCase();
      return this.endPanelInventoryForFamily(family).find(item =>
        item.available > 0 &&
        String(item.product.colour || '').trim().toLowerCase() === colour
      ) || null;
    }

    addAndPlaceSelectedEndPanel(localSide) {
      const unit = this.selected();
      const cabinet = unit ? this.product(unit) : null;
      if (!unit || !cabinet || !['left', 'right'].includes(localSide)) return false;

      const family = this.endPanelSkuForProduct(cabinet);
      if (!family) {
        this.setStatus('This cabinet does not use one of the stocked end-panel types.');
        return false;
      }

      if (this.assignmentForUnitSide(unit.uid, localSide)) {
        this.setStatus(`An end panel is already fitted on the ${localSide} end.`);
        return false;
      }

      const layer = cabinet.category === 'wall' ? 'wall' : 'floor';
      const exposed = this.exposedRunSides(unit, layer)
        .find(side => side.localSide === localSide);
      if (!exposed) {
        this.setStatus(`The ${localSide} end is not exposed.`);
        return false;
      }

      // Reuse an already-purchased matching panel before adding another one.
      const existing = this.matchingUnplacedEndPanel(unit);
      if (existing) {
        this.endPanelAssignments.push({
          productId: String(existing.product.id),
          unitId: String(unit.uid),
          localSide
        });
        this.renderAll();
        this.save();
        this.setStatus(`${existing.product.sku} fitted to ${cabinet.sku} — ${localSide} end. Existing unplaced extra used; no new basket item added.`);
        return true;
      }

      const panel = this.matchingEndPanelProduct(family, cabinet.colour);
      if (!panel) {
        this.setStatus(`No available ${cabinet.colour} ${family} end panel was found in the catalogue.`);
        return false;
      }

      const key = String(panel.id);
      this.extras[key] = (Number(this.extras[key]) || 0) + 1;
      this.endPanelAssignments.push({
        productId: key,
        unitId: String(unit.uid),
        localSide
      });

      this.renderAll();
      this.save();
      this.setStatus(`${panel.sku} added as a separate extra basket item and fitted to ${cabinet.sku} — ${localSide} end.`);
      return true;
    }

    placeSelectedEndPanel(localSide) {
      const unit = this.selected();
      const cabinet = unit ? this.product(unit) : null;
      if (!unit || !cabinet || !['left', 'right'].includes(localSide)) return false;

      const family = this.endPanelSkuForProduct(cabinet);
      if (!family) {
        this.setStatus('This cabinet does not use one of the stocked end-panel types.');
        return false;
      }

      if (this.assignmentForUnitSide(unit.uid, localSide)) {
        this.setStatus(`An end panel is already placed on the ${localSide} end.`);
        return false;
      }

      const layer = cabinet.category === 'wall' ? 'wall' : 'floor';
      const exposed = this.exposedRunSides(unit, layer)
        .find(side => side.localSide === localSide);
      if (!exposed) {
        this.setStatus(`The ${localSide} end is not exposed. Move the cabinet or choose the other end.`);
        return false;
      }

      const inventory = this.preferredAvailableEndPanel(unit);
      if (!inventory) {
        this.setStatus(`No unplaced ${family} panel is in your kitchen. Add a compatible end panel first.`);
        return false;
      }

      this.endPanelAssignments.push({
        productId: String(inventory.product.id),
        unitId: String(unit.uid),
        localSide
      });

      this.renderAll();
      this.save();

      const mismatch = String(inventory.product.colour || '').toLowerCase() !==
        String(cabinet.colour || '').toLowerCase();
      this.setStatus(
        `${inventory.product.sku} placed on ${cabinet.sku} — ${localSide} end${mismatch ? ` · colour differs (${inventory.product.colour} / ${cabinet.colour})` : ''}.`
      );
      return true;
    }

    removeSelectedEndPanel(localSide) {
      const unit = this.selected();
      if (!unit || !['left', 'right'].includes(localSide)) return false;

      const index = (this.endPanelAssignments || []).findIndex(item =>
        String(item.unitId || '') === String(unit.uid) &&
        String(item.localSide || '') === localSide
      );
      if (index < 0) return false;

      const [removed] = this.endPanelAssignments.splice(index, 1);
      const panel = this.productById.get(String(removed.productId));
      this.renderAll();
      this.save();
      this.setStatus(`${panel?.sku || 'End panel'} removed from the ${localSide} end and returned to the unplaced panel quantity.`);
      return true;
    }

    trimEndPanelAssignmentsForProduct(productId) {
      const target = String(productId || '');
      const allowed = Math.max(0, Number(this.extras?.[target]) || 0);
      let kept = 0;

      this.endPanelAssignments = (this.endPanelAssignments || []).filter(item => {
        if (String(item.productId || '') !== target) return true;
        kept += 1;
        return kept <= allowed;
      });
    }

    releaseEndPanelsForUnit(unitId) {
      const target = String(unitId || '');
      this.endPanelAssignments = (this.endPanelAssignments || []).filter(item =>
        String(item.unitId || '') !== target
      );
    }

    startEndPanelPlacement(productId) {
      const product = this.productById.get(String(productId || ''));
      if (!product || !this.endPanelSpec(product.sku)) return false;
      if (this.availableEndPanelQuantity(product.id) < 1) {
        this.setStatus(`${product.sku} has no unplaced quantity available.`);
        return false;
      }

      this.activeEndPanelProductId = String(product.id);
      this.setViewMode?.('2d');
      this.renderUnits();
      this.renderSummary();
      this.el.stage?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      this.setStatus(`Placing ${product.sku}. Tap the + on the exact exposed cabinet end where you want this panel.`);
      return true;
    }

    cancelEndPanelPlacement() {
      if (!this.activeEndPanelProductId) return;
      this.activeEndPanelProductId = null;
      this.renderUnits();
      this.renderSummary();
      this.setStatus('End-panel placement cancelled.');
    }

    activeEndPanelProduct() {
      if (!this.activeEndPanelProductId) return null;
      const product = this.productById.get(String(this.activeEndPanelProductId));
      if (!product || !this.endPanelSpec(product.sku)) return null;
      if (this.availableEndPanelQuantity(product.id) < 1) return null;
      return product;
    }

    placementTargetsForUnit(unit) {
      const panel = this.activeEndPanelProduct();
      const cabinet = unit ? this.product(unit) : null;
      if (!panel || !cabinet) return [];

      const panelFamily = this.endPanelFamily(panel.sku);
      const requiredFamily = this.endPanelSkuForProduct(cabinet);
      if (!panelFamily || panelFamily !== requiredFamily) return [];

      const layer = cabinet.category === 'wall' ? 'wall' : 'floor';
      return this.exposedRunSides(unit, layer)
        .filter(side => !this.assignmentForUnitSide(unit.uid, side.localSide));
    }

    placeActiveEndPanel(unitId, localSide) {
      const panel = this.activeEndPanelProduct();
      const unit = this.units.find(item => String(item.uid) === String(unitId || ''));
      const cabinet = unit ? this.product(unit) : null;
      if (!panel || !unit || !cabinet || !['left', 'right'].includes(localSide)) return false;

      const targets = this.placementTargetsForUnit(unit);
      if (!targets.some(side => side.localSide === localSide)) {
        this.setStatus('That cabinet end is not a compatible exposed end for this panel.');
        return false;
      }

      this.endPanelAssignments.push({
        productId: String(panel.id),
        unitId: String(unit.uid),
        localSide
      });

      this.selectedId = unit.uid;
      const mismatch = String(panel.colour || '').toLowerCase() !== String(cabinet.colour || '').toLowerCase();

      if (this.availableEndPanelQuantity(panel.id) < 1) {
        this.activeEndPanelProductId = null;
      }

      this.renderAll();
      this.save();
      this.setStatus(`${panel.sku} placed on ${cabinet.sku} — ${localSide} end${mismatch ? ` · colour differs (${panel.colour} / ${cabinet.colour})` : ''}.`);
      return true;
    }

    renderEndPanelPlacementControls(container, unit) {
      if (!container) return;
      const cabinet = unit ? this.product(unit) : null;
      const family = cabinet ? this.endPanelSkuForProduct(cabinet) : null;

      if (!unit || !cabinet || !family) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      const layer = cabinet.category === 'wall' ? 'wall' : 'floor';
      const exposed = new Map(
        this.exposedRunSides(unit, layer).map(side => [side.localSide, side])
      );
      const exactExisting = this.matchingUnplacedEndPanel(unit);
      const matchingProduct = this.matchingEndPanelProduct(family, cabinet.colour);
      const panelSpec = this.endPanelSpec(family);
      const panelSizeText = panelSpec
        ? `H${panelSpec.height} × D${panelSpec.depth} × T${panelSpec.thickness} mm`
        : '';
      const exactAvailableQty = this.endPanelInventoryForFamily(family)
        .filter(item =>
          item.available > 0 &&
          String(item.product.colour || '').trim().toLowerCase() ===
          String(cabinet.colour || '').trim().toLowerCase()
        )
        .reduce((sum, item) => sum + item.available, 0);

      const sideButtons = ['left', 'right'].map(localSide => {
        const assigned = this.assignmentForUnitSide(unit.uid, localSide);
        const side = exposed.get(localSide);
        const label = localSide === 'left' ? 'Left end' : 'Right end';

        if (assigned) {
          const panel = this.productById.get(String(assigned.productId));
          return `
            <button type="button" class="is-applied" data-panel-remove-side="${localSide}">
              <b>${label} ✓</b>
              <span>${this.escape(panel?.sku || 'End panel')} · tap to remove</span>
            </button>
          `;
        }

        if (!side) {
          return `
            <button type="button" disabled>
              <b>${label}</b>
              <span>Not exposed</span>
            </button>
          `;
        }

        if (exactExisting) {
          return `
            <button type="button" data-panel-add-side="${localSide}">
              <b>Fit ${label}</b>
              <span>${this.escape(exactExisting.product.sku)} · use existing extra</span>
            </button>
          `;
        }

        if (matchingProduct) {
          return `
            <button type="button" class="is-extra-add" data-panel-add-side="${localSide}">
              <b>Add ${label}</b>
              <span>${this.escape(matchingProduct.sku)} · ${this.money(matchingProduct.price)}</span>
            </button>
          `;
        }

        return `
          <button type="button" disabled>
            <b>${label}</b>
            <span>Matching ${this.escape(cabinet.colour || '')} ${this.escape(family)} panel unavailable</span>
          </button>
        `;
      }).join('');

      const panelUiLabel = family === 'ALDEC' ? 'Tall decor panel' : 'End panel';
      const content = `
        <div class="jkb__end-panel-placement-head">
          <b>${panelUiLabel} · extra item</b>
          <span>Only needed on an exposed cabinet end.</span>
        </div>
        <div class="jkb__end-panel-match">
          <b>${this.escape(family)} · ${this.escape(cabinet.colour || '')}</b>
          ${matchingProduct ? `<em>${this.escape(matchingProduct.sku)} · ${this.money(matchingProduct.price)}</em>` : ''}
          ${exactAvailableQty ? `<em>${exactAvailableQty} already unplaced</em>` : ''}
        </div>
        <div class="jkb__end-panel-placement-sides">${sideButtons}</div>
      `;

      container.hidden = false;
      const existingDetails = container.querySelector('details');
      const keepOpen = Boolean(
        existingDetails?.open &&
        existingDetails.dataset.unitId === String(unit.uid)
      );
      const assignedCount = ['left', 'right'].filter(side => this.assignmentForUnitSide(unit.uid, side)).length;
      const exposedCount = exposed.size;
      const status = assignedCount
        ? `${assignedCount} fitted`
        : exposedCount
          ? `${exposedCount} exposed`
          : 'none needed';
      const detailsClass = container === this.el.endPanelPlacement3d
        ? 'jkb__3d-addon'
        : 'jkb__selection-addon';
      const bodyClass = container === this.el.endPanelPlacement3d
        ? 'jkb__3d-addon-body'
        : 'jkb__selection-addon-body';
      container.innerHTML = `
        <details class="${detailsClass}" data-unit-id="${this.escape(String(unit.uid))}"${keepOpen ? ' open' : ''}>
          <summary>
            <b>${panelUiLabel}</b>
            <span>${status}</span>
          </summary>
          <div class="${bodyClass}">${content}</div>
        </details>
      `;
    }

    appliedEndPanelsForUnit(unit) {
      if (!unit) return [];
      return this.endPanelPlacements().filter(panel => String(panel.unitId) === String(unit.uid));
    }

    smallWallGaps() {
      const byEdge = new Map();
      const threshold = 100;

      for (const unit of this.units) {
        const product = this.product(unit);
        // Room features and standalone utility sinks are not part of a fitted
        // cabinet run, so they must not create false filler recommendations.
        if (!product || product.planningKind || product.category === 'wall' || product.standalone) continue;
        const edge = this.runEdge(unit);
        if (edge === 'free') continue;
        if (!byEdge.has(edge)) byEdge.set(edge, []);
        byEdge.get(edge).push(unit);
      }

      const gaps = [];
      const addInternalGaps = (edge, entries, axis, size) => {
        for (let index = 0; index < entries.length - 1; index += 1) {
          const current = entries[index];
          const next = entries[index + 1];
          const currentRect = current.rect;
          const gap = Math.round(next.rect[axis] - (currentRect[axis] + currentRect[size]));
          if (gap <= 0 || gap > threshold) continue;
          const currentSku = this.product(current.unit)?.sku || 'cabinet';
          const nextSku = this.product(next.unit)?.sku || 'cabinet';
          gaps.push({
            edge,
            side: `between ${currentSku} and ${nextSku}`,
            mm: gap,
            unit: next.unit
          });
        }
      };

      for (const [edge, units] of byEdge) {
        if (!units.length) continue;

        if (edge === 'top' || edge === 'bottom') {
          const entries = units
            .map(unit => ({ unit, rect: this.rect(unit) }))
            .sort((a, b) => a.rect.x - b.rect.x);
          const start = Math.round(entries[0].rect.x);
          const last = entries[entries.length - 1].rect;
          const end = Math.round(this.roomWidth - (last.x + last.width));
          if (start > 0 && start <= threshold) gaps.push({ edge, side: 'left end', mm: start, unit: entries[0].unit });
          addInternalGaps(edge, entries, 'x', 'width');
          if (end > 0 && end <= threshold) gaps.push({ edge, side: 'right end', mm: end, unit: entries[entries.length - 1].unit });
        } else {
          const entries = units
            .map(unit => ({ unit, rect: this.rect(unit) }))
            .sort((a, b) => a.rect.y - b.rect.y);
          const start = Math.round(entries[0].rect.y);
          const last = entries[entries.length - 1].rect;
          const end = Math.round(this.roomDepth - (last.y + last.depth));
          if (start > 0 && start <= threshold) gaps.push({ edge, side: 'top end', mm: start, unit: entries[0].unit });
          addInternalGaps(edge, entries, 'y', 'depth');
          if (end > 0 && end <= threshold) gaps.push({ edge, side: 'bottom end', mm: end, unit: entries[entries.length - 1].unit });
        }
      }

      return gaps;
    }

    cabinetWallAlignmentIssues() {
      const tolerance = Math.max(100, this.grid * 2);
      return this.units.flatMap(unit => {
        const product = this.product(unit);
        if (!product || product.planningKind || product.standalone) return [];
        const distance = Math.round(this.edgeDistance(unit));
        if (distance <= tolerance) return [];
        return [{ unit, product, distance }];
      });
    }

    productVerticalRange(product) {
      if (!product) return { bottom: 0, top: 0 };

      if (product.planningKind) {
        const bottom = Math.max(0, Number(product.elevation) || 0);
        return {
          bottom,
          top: bottom + Math.max(0, Number(product.height) || 0)
        };
      }

      if (product.category === 'wall') {
        const bottom = product.familyLabel === 'Bridging wall unit' ? 1770 : 1450;
        return {
          bottom,
          top: bottom + Math.max(0, Number(product.height) || 0)
        };
      }

      return {
        bottom: 0,
        top: Math.max(0, Number(product.height) || 0)
      };
    }

    rectsOverlap(a, b) {
      return a.x < b.x + b.width &&
        a.x + a.width > b.x &&
        a.y < b.y + b.depth &&
        a.y + a.depth > b.y;
    }

    roomFeatureConflicts() {
      const features = this.units.filter(unit => this.product(unit)?.planningKind);
      const normalUnits = this.units.filter(unit => !this.product(unit)?.planningKind);
      const conflicts = [];

      for (const feature of features) {
        const featureProduct = this.product(feature);
        const featureRect = this.rect(feature);
        const featureY = this.productVerticalRange(featureProduct);

        for (const unit of normalUnits) {
          const product = this.product(unit);
          if (!product || !this.rectsOverlap(featureRect, this.rect(unit))) continue;

          const unitY = this.productVerticalRange(product);
          const verticalOverlap = Math.min(featureY.top, unitY.top) - Math.max(featureY.bottom, unitY.bottom);
          if (verticalOverlap <= 20) continue;

          conflicts.push({
            feature,
            featureProduct,
            unit,
            product,
            verticalOverlap: Math.round(verticalOverlap)
          });
        }
      }

      return conflicts;
    }

    roomFeatureWallIssues() {
      const tolerance = Math.max(50, this.grid);
      const issues = [];

      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product?.planningKind || product.planningKind === 'obstacle') continue;
        const distance = Math.round(this.edgeDistance(unit));
        if (distance <= tolerance) continue;
        issues.push({ unit, product, distance });
      }

      return issues;
    }

    finishingPlan() {
      const worktopRuns = new Map();
      const adjustablePlinthRuns = new Map();
      const includedWorktops = [];
      const cornerWarnings = [];
      const standaloneWarnings = [];
      let fixedPanels = 0;
      let adjustablePanels = 0;
      let wallPanels = 0;
      let customPanels = 0;
      let cornerCount = 0;
      let customCount = 0;

      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product) continue;

        if (product.custom) customCount += 1;
        if (product.planningKind) continue;

        const edge = this.runEdge(unit);
        const length = Math.max(0, Math.round(this.runLength(unit, edge)));

        if (this.isWorktopUnit(product)) {
          if (this.hasIncludedWorktop(product)) includedWorktops.push(product.sku);
          else worktopRuns.set(edge, (worktopRuns.get(edge) || 0) + length);
        }

        const sku = String(product.sku || '').toUpperCase();
        if (/^AL/.test(sku) && product.category !== 'wall') {
          const colour = String(product.colour || 'Other');
          adjustablePlinthRuns.set(colour, (adjustablePlinthRuns.get(colour) || 0) + Math.max(0, Number(product.width) || 0));
        }

        if (Boolean(product.cornerShape || product.category === 'corner')) {
          cornerCount += 1;
          if (!this.isRoomCorner(unit)) cornerWarnings.push({ unit, product });
        }

        if (product.standalone || /^TS/.test(String(product.sku || '').toUpperCase())) {
          const adjacent = this.adjacentUnits(unit, 'floor');
          if (adjacent.length) standaloneWarnings.push({ unit, product, adjacent });
        }

        if (product.custom) {
          const customLayer = product.category === 'wall' ? 'wall' : 'floor';
          customPanels += this.exposedRunEnds(unit, customLayer);
          continue;
        }

        const panelSku = this.endPanelSkuForProduct(product);
        if (!panelSku) continue;

        const panelLayer = product.category === 'wall' ? 'wall' : 'floor';
        const exposed = this.exposedRunEnds(unit, panelLayer);
        if (!exposed) continue;

        if (panelSku === 'ALWEND') wallPanels += exposed;
        else if (panelSku === 'ALBEND') adjustablePanels += exposed;
        else if (panelSku === 'GBEND') fixedPanels += exposed;
      }

      return {
        worktopRuns,
        adjustablePlinthRuns,
        includedWorktops,
        fixedPanels,
        adjustablePanels,
        wallPanels,
        customPanels,
        customCount,
        cornerCount,
        cornerWarnings,
        standaloneWarnings,
        cabinetCollisions: this.cabinetCollisionPairs(),
        fillerGaps: this.smallWallGaps(),
        cabinetWallAlignmentIssues: this.cabinetWallAlignmentIssues(),
        roomFeatureConflicts: this.roomFeatureConflicts(),
        roomFeatureWallIssues: this.roomFeatureWallIssues(),
        unavailableProducts: this.unavailableBasketProducts()
      };
    }

    renderRunLengths(map) {
      if (!map || !map.size) return '';
      return [...map.entries()]
        .filter(([, mm]) => mm > 0)
        .map(([edge, mm]) => `${this.edgeLabel(edge)} ${Math.round(mm)} mm`)
        .join(' · ');
    }

    catalogProductBySku(sku) {
      const rawTarget = String(sku || '').trim().toUpperCase();
      if (!rawTarget) return null;

      const targetInfo = this.endPanelInfo(rawTarget);
      if (targetInfo) {
        const matches = this.catalog.filter(product => {
          if (!product.available) return false;
          const info = this.endPanelInfo(product.sku);
          if (!info || info.family !== targetInfo.family) return false;
          return !targetInfo.colourCode || info.colourCode === targetInfo.colourCode;
        });
        if (targetInfo.colourCode) return matches[0] || null;
        return matches.find(product => String(product.colour || '').toLowerCase() === 'white') || matches[0] || null;
      }

      const target = this.normalizeEndPanelSku(rawTarget);
      return this.catalog.find(product =>
        product.available &&
        this.normalizeEndPanelSku(product.sku) === target
      ) || null;
    }

    extraQuantityBySku(sku) {
      const rawTarget = String(sku || '').trim().toUpperCase();
      if (!rawTarget) return 0;

      const targetInfo = this.endPanelInfo(rawTarget);
      const target = this.normalizeEndPanelSku(rawTarget);
      let qty = 0;

      for (const [productId, rawQty] of Object.entries(this.extras || {})) {
        const product = this.productById.get(String(productId));
        if (!product) continue;

        const productInfo = this.endPanelInfo(product.sku);
        if (targetInfo && productInfo) {
          const familyMatch = productInfo.family === targetInfo.family;
          const colourMatch = !targetInfo.colourCode || productInfo.colourCode === targetInfo.colourCode;
          if (familyMatch && colourMatch) qty += Number(rawQty) || 0;
          continue;
        }

        if (this.normalizeEndPanelSku(product.sku) === target) {
          qty += Number(rawQty) || 0;
        }
      }
      return qty;
    }

    plinthSkuForColour(colour) {
      const key = String(colour || '').trim().toLowerCase();
      const map = {
        'white': 'F-ALB27W',
        'white matt': 'F-ALB27W',
        'grey': 'F-ALB27G',
        'grey matt': 'F-ALB27G',
        'dark grey': 'F-ALB27DG',
        'dark grey matt': 'F-ALB27DG',
        'white gloss': 'F-ALB27WGL',
        'sage green': 'F-ALB27S',
        'sage green matt': 'F-ALB27S',
        'navy': 'F-ALB27N',
        'navy blue': 'F-ALB27N',
        'navy matt': 'F-ALB27N',
        'cashmere': 'F-ALB27C',
        'cashmere matt': 'F-ALB27C'
      };
      return map[key] || null;
    }

    matchingLongPlinthProduct(colour) {
      const targetColour = String(colour || '').trim().toLowerCase();
      const targetSku = this.plinthSkuForColour(colour);

      if (targetSku) {
        const exact = this.catalog.find(product =>
          product?.available &&
          String(product.sku || '').trim().toUpperCase() === targetSku
        );
        if (exact) return exact;
      }

      const candidates = this.catalog.filter(product => {
        if (!product?.available || product.category !== 'plinth') return false;
        const text = `${product.sku || ''} ${product.productTitle || ''} ${product.variantTitle || ''}`.toLowerCase();
        const longLength = /^F-ALB27(?:WGL|DG|[WGSNC])$/i.test(String(product.sku || '')) ||
          Number(product.width) >= 2500 ||
          /\b2700\s*mm\b/.test(text) ||
          /\b2[.,]7\s*m\b/.test(text);
        if (!longLength) return false;

        const productColour = String(product.colour || '').trim().toLowerCase();
        return productColour === targetColour || text.includes(targetColour);
      });

      return candidates[0] || null;
    }

    matchingPlinthRecommendations(plan = null) {
      const resolved = plan || this.finishingPlan();
      const recommendations = [];

      for (const [colour, rawRun] of resolved.adjustablePlinthRuns || []) {
        const runMm = Math.max(0, Math.round(Number(rawRun) || 0));
        if (!runMm) continue;

        const qty = Math.max(1, Math.ceil(runMm / 2700));
        const product = this.matchingLongPlinthProduct(colour);
        const inBasket = product ? (Number(this.extras[String(product.id)]) || 0) : 0;

        recommendations.push({
          colour,
          runMm,
          qty,
          product,
          inBasket,
          missing: product ? Math.max(0, qty - inBasket) : qty
        });
      }

      return recommendations.sort((a, b) => a.colour.localeCompare(b.colour));
    }

    addRecommendedPlinths() {
      const recommendations = this.matchingPlinthRecommendations();
      const addable = recommendations.filter(item => item.product && item.missing > 0);

      if (!addable.length) {
        this.setStatus('No additional matching 2.7 m AL plinth is needed or available.');
        return;
      }

      const added = [];
      for (const item of addable) {
        const key = String(item.product.id);
        this.extras[key] = (Number(this.extras[key]) || 0) + item.missing;
        added.push(`${item.colour} × ${item.missing}`);
      }

      this.renderSummary();
      this.save();
      this.setStatus(`Added optional matching AL plinth: ${added.join(', ')}.`);
    }

    matchingEndPanelProduct(family, colour) {
      const targetFamily = String(family || '').toUpperCase();
      const targetColour = String(colour || '').trim().toLowerCase();
      const candidates = this.catalog.filter(product => {
        if (!product?.available || product.category !== 'panel') return false;
        return this.endPanelFamily(product.sku) === targetFamily;
      });

      return candidates.find(product =>
        String(product.colour || '').trim().toLowerCase() === targetColour
      ) || null;
    }

    extraQuantityByPanelFamilyColour(family, colour) {
      const targetFamily = String(family || '').toUpperCase();
      const targetColour = String(colour || '').trim().toLowerCase();
      let qty = 0;

      for (const [productId, rawQty] of Object.entries(this.extras || {})) {
        const product = this.productById.get(String(productId));
        if (!product || this.endPanelFamily(product.sku) !== targetFamily) continue;
        if (String(product.colour || '').trim().toLowerCase() !== targetColour) continue;
        qty += Number(rawQty) || 0;
      }

      return qty;
    }

    requiredEndPanelsByColour() {
      const grouped = new Map();

      for (const unit of this.units) {
        const product = this.product(unit);
        const family = this.endPanelSkuForProduct(product);
        if (!family) continue;

        const layer = product.category === 'wall' ? 'wall' : 'floor';
        const qty = this.exposedRunEnds(unit, layer);
        if (!qty) continue;

        const colour = String(product.colour || 'White');
        const key = `${family}::${colour.toLowerCase()}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            family,
            colour,
            qty: 0,
            label: family === 'ALWEND'
              ? 'Wall end panel'
              : family === 'ALBEND'
                ? 'Adjustable base end panel'
                : family === 'ALDEC'
                  ? 'Tall decor panel'
                  : 'Standard base end panel'
          });
        }
        grouped.get(key).qty += qty;
      }

      return [...grouped.values()];
    }

    endPanelSuggestions(plan = null) {
      return this.requiredEndPanelsByColour().map(item => {
        const product = this.matchingEndPanelProduct(item.family, item.colour);
        const inBasket = this.extraQuantityByPanelFamilyColour(item.family, item.colour);
        return {
          ...item,
          sku: product?.sku || item.family,
          product,
          inBasket,
          missing: Math.max(0, item.qty - inBasket)
        };
      });
    }

    addSuggestedEndPanels() {
      const suggestions = this.endPanelSuggestions();
      const addable = suggestions.filter(item => item.product && item.missing > 0);

      if (!addable.length) {
        const unplaced = this.endPanelSuggestions().reduce((sum, item) => {
          if (!item.product) return sum;
          return sum + this.endPanelInventoryForFamily(item.family)
            .filter(inv => String(inv.product.colour || '').toLowerCase() === String(item.colour || '').toLowerCase())
            .reduce((qty, inv) => qty + inv.available, 0);
        }, 0);
        this.setStatus(unplaced
          ? `Matching end-panel quantities are already in your kitchen. ${unplaced} panel${unplaced === 1 ? '' : 's'} still need manual placement.`
          : 'No additional matching end panels are needed.');
        return;
      }

      const added = [];
      for (const item of addable) {
        const key = String(item.product.id);
        this.extras[key] = (Number(this.extras[key]) || 0) + item.missing;
        added.push(`${item.product.sku} × ${item.missing}`);
      }

      this.renderUnits();
      this.renderSelection();
      this.sync3D();
      this.renderSummary();
      this.save();
      this.setStatus(`Added suggested end panels: ${added.join(', ')}. Select each compatible cabinet to choose the side.`);
    }

    closeSmallRunGap(uid) {
      const gap = this.smallWallGaps().find(item =>
        item.unit &&
        String(item.unit.uid) === String(uid) &&
        String(item.side || '').startsWith('between ')
      );
      if (!gap) {
        this.setStatus('That cabinet gap is no longer present.');
        this.renderSummary();
        return false;
      }

      const unit = this.units.find(item => String(item.uid) === String(uid));
      if (!unit) return false;
      if (unit.locked) {
        this.setStatus('Unlock this cabinet before closing the gap.');
        this.focusFitIssue(unit.uid);
        return false;
      }

      const old = { x: unit.x, y: unit.y };
      if (gap.edge === 'top' || gap.edge === 'bottom') unit.x -= gap.mm;
      else unit.y -= gap.mm;

      const d = this.unitDimensions(unit);
      unit.x = this.clamp(unit.x, 0, Math.max(0, this.roomWidth - d.width));
      unit.y = this.clamp(unit.y, 0, Math.max(0, this.roomDepth - d.depth));

      if (this.hasCollision(unit)) {
        unit.x = old.x;
        unit.y = old.y;
        this.setStatus('The gap could not be closed because another cabinet blocks the move.');
        return false;
      }

      this.justAddedIds.delete(unit.uid);
      if (this.lastAddedId === unit.uid) this.lastAddedId = null;
      this.renderAll();
      this.save();
      this.focusFitIssue(unit.uid);
      this.setStatus(`Closed the ${gap.mm} mm gap.`);
      return true;
    }

    focusFitIssue(uid) {
      if (!uid) return;
      const unit = this.units.find(item => String(item.uid) === String(uid));
      if (!unit) return;

      const product = this.product(unit);
      if (product && !this.unitVisibleInLayer(unit)) this.setLayer('all');

      this.selectedId = unit.uid;
      this.setView('2d');
      this.renderUnits();
      this.renderSelection();

      requestAnimationFrame(() => {
        const node = this.el.room.querySelector(`[data-unit-id="${CSS.escape(String(unit.uid))}"]`);
        if (node) {
          node.classList.add('is-fit-focus');
          setTimeout(() => node.classList.remove('is-fit-focus'), 1800);
        }
        this.el.stage?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
    }

    renderFitAssistant() {
      if (!this.el.fitAssistant || !this.el.fitAssistantBody) return;

      if (!this.units.length) {
        this.el.fitAssistant.hidden = true;
        this.el.fitAssistantBody.innerHTML = '';
        if (this.el.fitAssistantCount) this.el.fitAssistantCount.textContent = '';
        return;
      }

      const plan = this.finishingPlan();
      const rows = [];
      let checks = 0;

      if (plan.worktopRuns.size) {
        rows.push(`
          <div class="jkb__fit-row is-info">
            <div><b>Worktop coverage</b><span>${this.escape(this.renderRunLengths(plan.worktopRuns))}</span></div>
            <em>Estimate</em>
          </div>
        `);
      }

      if (plan.includedWorktops.length) {
        rows.push(`
          <div class="jkb__fit-row is-ok">
            <div><b>Pre-cut worktop detected</b><span>${this.escape(plan.includedWorktops.join(', '))}</span></div>
            <em>Included</em>
          </div>
        `);
      }

      const plinthRecommendations = this.matchingPlinthRecommendations(plan);
      if (plinthRecommendations.length) {
        const missingQty = plinthRecommendations.reduce((sum, item) => sum + (item.product ? item.missing : 0), 0);
        const details = plinthRecommendations.map(item => {
          const base = `${item.colour}: ${item.runMm} mm run → ${item.qty} × 2.7 m`;
          if (item.product) return item.missing > 0 ? base : `${base} · already added`;
          return `${base} · matching plinth not found in catalogue`;
        });

        rows.push(`
          <div class="jkb__fit-row is-info">
            <div>
              <b>Optional 2.7 m colour-matched plinth · AL units only</b>
              <span>White plinth is already supplied with every cabinet. For AL adjustable units only, add this optional plinth to match the door colour. ${this.escape(details.join(' · '))}</span>
            </div>
            ${missingQty
              ? '<button type="button" class="jkb__fit-action" data-fit-add-plinth>Add plinth</button>'
              : '<em>Optional</em>'}
          </div>
        `);
      }

      const panelSuggestions = this.endPanelSuggestions(plan);
      const panelParts = panelSuggestions.map(item => {
        const sku = item.product?.sku || item.family;
        return `${sku} · ${item.colour} × ${item.qty}`;
      });
      if (plan.customPanels) panelParts.push(`custom exposed sides × ${plan.customPanels}`);

      if (panelParts.length) {
        const addablePanelQty = panelSuggestions.reduce((sum, item) => sum + (item.product ? item.missing : 0), 0);
        const unavailablePanels = panelSuggestions
          .filter(item => item.qty > 0 && !item.product)
          .map(item => `${item.family} ${item.colour}`);

        const statusBits = [];
        if (addablePanelQty) statusBits.push(`${addablePanelQty} colour-matched panel${addablePanelQty === 1 ? '' : 's'} missing`);
        if (unavailablePanels.length) statusBits.push(`${unavailablePanels.join(', ')} not found in catalogue`);
        if (!statusBits.length && !plan.customPanels) statusBits.push('colour-matched panels already covered');

        rows.push(`
          <div class="jkb__fit-row is-info">
            <div>
              <b>Colour-matched exposed end / decor panels</b>
              <span>${this.escape(panelParts.join(' · '))}${statusBits.length ? ` · ${this.escape(statusBits.join(' · '))}` : ''} · T400 and ALT500 use the 578 × 2095 mm ALDEC decor panel; place each purchased panel on the required exposed cabinet end in 2D or 3D</span>
            </div>
            ${addablePanelQty
              ? '<button type="button" class="jkb__fit-action" data-fit-add-panels>Add matching</button>'
              : '<em>Suggested</em>'}
          </div>
        `);
      }

      if (plan.cornerCount) {
        const firstCornerIssue = plan.cornerWarnings[0];
        rows.push(`
          <div class="jkb__fit-row ${plan.cornerWarnings.length ? 'is-warning' : 'is-info'}">
            <div><b>Corner worktop / fitting</b><span>Corner units need the worktop and final cut checked at installation.${plan.cornerWarnings.length ? ` ${this.escape(plan.cornerWarnings.map(item => item.product.sku).join(', '))} is not positioned at a room corner.` : ''}</span></div>
            ${firstCornerIssue
              ? `<button type="button" class="jkb__fit-action is-warning" data-fit-focus="${this.escape(firstCornerIssue.unit.uid)}">Show</button>`
              : '<em>Site cut</em>'}
          </div>
        `);
        checks += plan.cornerWarnings.length;
      }

      if (plan.standaloneWarnings.length) {
        checks += plan.standaloneWarnings.length;
        const firstStandaloneIssue = plan.standaloneWarnings[0];
        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div><b>Standalone utility sink</b><span>${this.escape(plan.standaloneWarnings.map(item => item.product.sku).join(', '))} is touching a fitted cabinet run. TS1000 is normally used as a standalone unit.</span></div>
            <button type="button" class="jkb__fit-action is-warning" data-fit-focus="${this.escape(firstStandaloneIssue.unit.uid)}">Show</button>
          </div>
        `);
      }

      if (plan.fillerGaps.length) {
        checks += plan.fillerGaps.length;
        const firstInternalGap = plan.fillerGaps.find(gap =>
          gap.unit && String(gap.side || '').startsWith('between ')
        );
        const firstGap = firstInternalGap || plan.fillerGaps.find(gap => gap.unit);
        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div>
              <b>Small run gaps</b>
              <span>${this.escape(plan.fillerGaps.map(gap => `${this.edgeLabel(gap.edge)} ${gap.side}: ${gap.mm} mm`).join(' · '))}. Close internal gaps or allow for a filler panel at the end of a run.</span>
            </div>
            ${firstInternalGap
              ? `<button type="button" class="jkb__fit-action is-warning" data-fit-close-gap="${this.escape(firstInternalGap.unit.uid)}">Fix</button>`
              : firstGap
                ? `<button type="button" class="jkb__fit-action is-warning" data-fit-focus="${this.escape(firstGap.unit.uid)}">Show</button>`
                : '<em>Filler?</em>'}
          </div>
        `);
      }

      if (plan.cabinetWallAlignmentIssues.length) {
        checks += plan.cabinetWallAlignmentIssues.length;
        const firstIssue = plan.cabinetWallAlignmentIssues[0];
        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div>
              <b>Cabinet away from wall</b>
              <span>${this.escape(plan.cabinetWallAlignmentIssues.map(item => `${item.product.sku}: ${item.distance} mm`).join(' · '))}. Move it into a wall run or confirm that it is intentionally free-standing.</span>
            </div>
            <button type="button" class="jkb__fit-action is-warning" data-fit-cabinet-wall="${this.escape(firstIssue.unit.uid)}">Fix</button>
          </div>
        `);
      }

      if (plan.unavailableProducts.length) {
        checks += plan.unavailableProducts.length;
        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div>
              <b>Products unavailable</b>
              <span>${this.escape(plan.unavailableProducts.map(product => product.sku).join(', '))} can no longer be added to the basket. Remove or replace these products.</span>
            </div>
            <em>Basket blocked</em>
          </div>
        `);
      }

      if (plan.cabinetCollisions.length) {
        checks += plan.cabinetCollisions.length;
        const firstOverlap = plan.cabinetCollisions[0];
        const examples = plan.cabinetCollisions.slice(0, 3).map(item =>
          `${item.productA.sku} × ${item.productB.sku}`
        );
        const more = plan.cabinetCollisions.length > 3
          ? ` + ${plan.cabinetCollisions.length - 3} more`
          : '';

        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div>
              <b>Cabinets overlapping</b>
              <span>${this.escape(examples.join(' · '))}${this.escape(more)}. Move or remove one of the overlapping cabinets before adding the kitchen to the basket.</span>
            </div>
            <button type="button" class="jkb__fit-action is-warning" data-fit-focus="${this.escape(firstOverlap.a.uid)}">Show</button>
          </div>
        `);
      }

      if (plan.roomFeatureConflicts.length) {
        checks += plan.roomFeatureConflicts.length;
        const firstConflict = plan.roomFeatureConflicts[0];
        const examples = plan.roomFeatureConflicts.slice(0, 3).map(item =>
          `${item.product.sku} × ${item.featureProduct.productTitle}`
        );
        const more = plan.roomFeatureConflicts.length > 3
          ? ` + ${plan.roomFeatureConflicts.length - 3} more`
          : '';

        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div>
              <b>Room feature clearance</b>
              <span>${this.escape(examples.join(' · '))}${this.escape(more)}. Move the cabinet or update the room feature position/elevation.</span>
            </div>
            <button type="button" class="jkb__fit-action is-warning" data-fit-focus="${this.escape(firstConflict.unit.uid)}">Show</button>
          </div>
        `);
      }

      if (plan.roomFeatureWallIssues.length) {
        checks += plan.roomFeatureWallIssues.length;
        const firstIssue = plan.roomFeatureWallIssues[0];
        rows.push(`
          <div class="jkb__fit-row is-warning">
            <div>
              <b>Room feature not on a wall</b>
              <span>${this.escape(plan.roomFeatureWallIssues.map(item => `${item.product.productTitle}: ${item.distance} mm from nearest wall`).join(' · '))}</span>
            </div>
            <button type="button" class="jkb__fit-action is-warning" data-fit-wall="${this.escape(firstIssue.unit.uid)}">Fix</button>
          </div>
        `);
      }

      if (plan.customCount) {
        rows.push(`
          <div class="jkb__fit-row is-plan">
            <div><b>Custom planning items</b><span>${plan.customCount} custom item${plan.customCount === 1 ? '' : 's'} will stay in the project but will not be added to the JD Greta basket.</span></div>
            <em>Plan only</em>
          </div>
        `);
      }

      if (!rows.length) {
        rows.push(`
          <div class="jkb__fit-row is-ok">
            <div><b>Layout check</b><span>No finishing calculations are needed yet.</span></div>
            <em>OK</em>
          </div>
        `);
      }

      this.el.fitAssistant.hidden = false;
      this.el.fitAssistantBody.innerHTML = rows.join('');

      if (this.el.fitAssistantCount) {
        this.el.fitAssistantCount.textContent = checks ? `${checks} check${checks === 1 ? '' : 's'}` : 'Ready';
        this.el.fitAssistantCount.classList.toggle('has-warnings', checks > 0);
      }
    }

    heightMismatchPairs() {
      const pairs = [];

      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product || product?.standalone) continue;

        for (const warning of this.heightWarningsFor(unit)) {
          const other = warning.other;
          const otherProduct = this.product(other);
          if (!other || !otherProduct) continue;

          pairs.push({
            unit,
            other,
            product,
            otherProduct,
            delta: warning.delta,
            side: warning.side,
            direction: warning.direction,
            relation: warning.heightDirection === '↑' ? 'higher' : 'lower'
          });
        }
      }

      return pairs;
    }

    doorColourWarningGroups() {
      const groups = new Map();

      this.units.forEach(unit => {
        const product = this.product(unit);
        if (!product || product.custom || product.planningKind) return;

        const colour = this.productDoorColour(product);
        if (!colour || colour === 'No Doors' || colour === 'Other') return;

        if (!groups.has(colour)) groups.set(colour, []);
        groups.get(colour).push(product.sku || product.productTitle || 'Cabinet');
      });

      return [...groups.entries()]
        .map(([colour, skus]) => ({
          colour,
          skus: [...new Set(skus.map(String))]
        }))
        .sort((a, b) => a.colour.localeCompare(b.colour));
    }

    renderHeightAlert() {
      if (!this.el.heightAlert || !this.el.heightAlertList || !this.el.heightConfirm) return;

      const pairs = this.heightMismatchPairs();
      const colourGroups = this.doorColourWarningGroups();
      const hasHeightMismatch = pairs.length > 0;
      const hasColourMismatch = colourGroups.length > 1;

      if (!hasHeightMismatch && !hasColourMismatch) {
        this.el.heightAlert.hidden = true;
        this.el.heightAlertList.innerHTML = '';
        this.el.heightConfirm.checked = false;
        this.heightWarningSignature = '';
        return;
      }

      const heightSignature = pairs
        .map(pair => [
          pair.unit.uid,
          pair.other.uid,
          pair.product.height,
          pair.otherProduct.height
        ].join(':'))
        .sort()
        .join('|');
      const colourSignature = colourGroups
        .map(group => `${group.colour}:${group.skus.join(',')}`)
        .join('|');
      const signature = `H:${heightSignature}||C:${colourSignature}`;

      if (signature !== this.heightWarningSignature) {
        this.el.heightConfirm.checked = false;
        this.heightWarningSignature = signature;
      }

      const label = this.el.heightAlert.querySelector('.jkb__height-alert-label');
      const heading = this.el.heightAlert.querySelector('.jkb__height-alert-heading strong');
      const confirmText = this.el.heightAlert.querySelector('.jkb__height-confirm span');

      if (label) {
        label.textContent = hasHeightMismatch && hasColourMismatch
          ? 'DESIGN CHECK'
          : hasColourMismatch
            ? 'COLOUR CHECK'
            : 'HEIGHT CHECK';
      }
      if (heading) {
        heading.textContent = hasHeightMismatch && hasColourMismatch
          ? 'Different cabinet heights and door colours'
          : hasColourMismatch
            ? 'Different door colours'
            : 'Different cabinet heights';
      }
      if (confirmText) {
        confirmText.textContent = hasHeightMismatch && hasColourMismatch
          ? 'I understand these cabinets have different heights and door colours.'
          : hasColourMismatch
            ? 'I understand this kitchen contains cabinets with different door colours.'
            : 'I understand these adjacent cabinets have different heights.';
      }

      const heightRows = pairs.map(pair => {
        const otherIsHigher = Number(pair.otherProduct.height) > Number(pair.product.height);
        const relation = otherIsHigher ? 'higher' : 'lower';
        return `
          <div class="jkb__height-alert-row">
            <strong>${this.escape(pair.product.sku)} H${pair.product.height}</strong>
            <span class="jkb__height-pair-separator" aria-hidden="true">VS</span>
            <strong>${this.escape(pair.otherProduct.sku)} H${pair.otherProduct.height}</strong>
            <em>${pair.delta} mm difference · ${this.escape(pair.otherProduct.sku)} is ${relation}</em>
          </div>
        `;
      });

      const colourRows = hasColourMismatch
        ? [`
          <div class="jkb__height-alert-row jkb__colour-alert-row">
            <strong>Door colours</strong>
            <em>${colourGroups.map(group =>
              `${this.escape(group.colour)}: ${group.skus.map(sku => this.escape(sku)).join(', ')}`
            ).join(' · ')}</em>
          </div>
        `]
        : [];

      this.el.heightAlert.hidden = false;
      this.el.heightAlertList.innerHTML = [...heightRows, ...colourRows].join('');
    }

    unavailableBasketProducts() {
      const unavailable = new Map();

      this.units.forEach(unit => {
        const product = this.product(unit);
        if (product && !product.custom && !product.available) {
          unavailable.set(String(product.id), product);
        }
      });

      Object.entries(this.extras || {}).forEach(([productId, qtyRaw]) => {
        if ((Number(qtyRaw) || 0) < 1) return;
        const product = this.productById.get(String(productId));
        if (product && !product.custom && !product.available) {
          unavailable.set(String(product.id), product);
        }
      });

      return [...unavailable.values()];
    }

    productQuantityMaximum(product) {
      if (!product || product.custom) return null;

      const limits = [];
      const ruleMaximum = Number(product.quantityMax);
      if (Number.isFinite(ruleMaximum) && ruleMaximum > 0) limits.push(ruleMaximum);

      const managed = Boolean(product.inventoryManagement);
      const deniesOverselling = String(product.inventoryPolicy || '').toLowerCase() !== 'continue';
      const inventoryMaximum = Number(product.inventoryQuantity);
      if (
        managed &&
        deniesOverselling &&
        Number.isFinite(inventoryMaximum) &&
        inventoryMaximum >= 0
      ) {
        limits.push(inventoryMaximum);
      }

      return limits.length ? Math.min(...limits) : null;
    }

    basketQuantityIssues() {
      const quantities = new Map();

      this.units.forEach(unit => {
        const product = this.product(unit);
        if (!product || product.custom) return;
        const key = String(product.id);
        if (!quantities.has(key)) quantities.set(key, { product, quantity: 0 });
        quantities.get(key).quantity += 1;
      });

      Object.entries(this.extras || {}).forEach(([productId, qtyRaw]) => {
        const quantity = Number(qtyRaw) || 0;
        if (quantity < 1) return;
        const product = this.productById.get(String(productId));
        if (!product || product.custom) return;
        const key = String(product.id);
        if (!quantities.has(key)) quantities.set(key, { product, quantity: 0 });
        quantities.get(key).quantity += quantity;
      });

      const issues = [];
      quantities.forEach(({ product, quantity }) => {
        const maximum = this.productQuantityMaximum(product);
        if (maximum == null) return;
        if (quantity > maximum) {
          issues.push({ product, quantity, maximum });
        }
      });

      return issues.sort((a, b) =>
        String(a.product.sku || '').localeCompare(String(b.product.sku || ''))
      );
    }

    updateCheckoutState(hasBasketProducts = null) {
      if (!this.el.addCart) return;

      const basketProductsPresent = hasBasketProducts === null
        ? this.units.some(unit => {
            const product = this.product(unit);
            return product && !product.custom;
          }) || Object.entries(this.extras || {}).some(([productId, qty]) => {
            const product = this.productById.get(String(productId));
            return product && !product.custom && Number(qty) > 0;
          })
        : Boolean(hasBasketProducts);

      const requiresHeightConfirm = this.heightMismatchPairs().length > 0;
      const requiresColourConfirm = this.doorColourWarningGroups().length > 1;
      const requiresDesignConfirm = requiresHeightConfirm || requiresColourConfirm;
      const confirmed = !requiresDesignConfirm || Boolean(this.el.heightConfirm?.checked);
      const cabinetOverlaps = this.cabinetCollisionPairs().length;
      const roomConflicts = this.roomFeatureConflicts().length;
      const hasBlockingLayoutIssue = cabinetOverlaps > 0 || roomConflicts > 0;
      const unavailableProducts = this.unavailableBasketProducts();
      const quantityIssues = this.basketQuantityIssues();

      this.el.addCart.disabled =
        !basketProductsPresent ||
        !confirmed ||
        hasBlockingLayoutIssue ||
        unavailableProducts.length > 0 ||
        quantityIssues.length > 0;

      if (!basketProductsPresent) {
        this.el.addCart.textContent = this.units.length
          ? 'Add JD Greta products to use basket'
          : 'Add complete kitchen to basket';
      } else if (unavailableProducts.length) {
        this.el.addCart.textContent = `Remove ${unavailableProducts.length} unavailable product${unavailableProducts.length === 1 ? '' : 's'}`;
      } else if (quantityIssues.length) {
        const issue = quantityIssues[0];
        this.el.addCart.textContent = quantityIssues.length === 1
          ? `${issue.product.sku}: max ${issue.maximum}`
          : `Resolve ${quantityIssues.length} quantity limits`;
      } else if (hasBlockingLayoutIssue) {
        const issueCount = cabinetOverlaps + roomConflicts;
        this.el.addCart.textContent = `Resolve ${issueCount} layout overlap${issueCount === 1 ? '' : 's'}`;
      } else if (requiresDesignConfirm && !confirmed) {
        this.el.addCart.textContent = requiresHeightConfirm && requiresColourConfirm
          ? 'Confirm height / colour differences above'
          : requiresColourConfirm
            ? 'Confirm door colour difference above'
            : 'Confirm height difference above';
      } else {
        this.el.addCart.textContent = 'Add complete kitchen to basket';
      }
    }

    updateSummaryScrollHint() {
      if (!this.el.summaryScrollHint || !this.el.summary) return;
      const canScroll = this.el.summary.scrollHeight > this.el.summary.clientHeight + 6;
      const expanded = this.el.summary.classList.contains('is-expanded');
      const atBottom = this.el.summary.scrollTop + this.el.summary.clientHeight >= this.el.summary.scrollHeight - 8;

      this.el.summaryScrollHint.hidden = !canScroll || expanded || atBottom;
      if (this.el.summaryToggle) {
        this.el.summaryToggle.hidden = !canScroll && !expanded;
        this.el.summaryToggle.textContent = expanded ? 'Collapse' : 'View all';
      }

      const label = this.el.summaryScrollHint.querySelector('span');
      if (label) label.textContent = 'More items below — swipe up';
    }

    changeSummaryQuantity(productId, delta) {
      const product = this.productById.get(String(productId));
      if (!product || !delta) return;

      if (product.placeable) {
        if (delta > 0) {
          this.addUnit(product.id);
          return;
        }

        const index = this.units.map(unit => String(unit.productId)).lastIndexOf(String(product.id));
        if (index >= 0) {
          const [removed] = this.units.splice(index, 1);
          if (removed?.uid) this.justAddedIds.delete(removed.uid);
          if (removed?.uid && this.lastAddedId === removed.uid) this.lastAddedId = null;
          if (removed?.uid === this.selectedId) this.selectedId = null;
          this.renderAll();
          this.save();
        }
        return;
      }

      const key = String(product.id);
      const current = Number(this.extras[key]) || 0;
      const minimum = product.category === 'plinth'
        ? this.requiredPlinthSticks(key)
        : 0;
      const next = Math.max(minimum, current + delta);
      if (next === 0) delete this.extras[key];
      else this.extras[key] = next;
      if (product.category === 'plinth' && delta < 0 && current + delta < minimum) {
        this.setStatus(`${minimum} × 2700 mm plinth piece${minimum === 1 ? '' : 's'} required for the applied cut lengths. Remove or shorten an applied plinth first.`);
      }
      if (this.endPanelSpec(product.sku)) {
        this.trimEndPanelAssignmentsForProduct(key);
        this.renderUnits();
        this.renderSelection();
        this.sync3D();
      }
      this.renderSummary();
      this.save();
    }

    removeSummaryLine(productId) {
      const product = this.productById.get(String(productId));
      if (!product) return;
      const plannedQty = product.placeable
        ? this.units.filter(unit => String(unit.productId) === String(product.id)).length
        : (Number(this.extras[String(product.id)]) || 0);
      const quantityText = plannedQty > 1 ? ` all ${plannedQty}` : '';
      if (!window.confirm(`Remove${quantityText} ${product.sku || 'items'} from your kitchen?`)) return;

      if (product.placeable) {
        const removedIds = new Set(
          this.units
            .filter(unit => String(unit.productId) === String(product.id))
            .map(unit => unit.uid)
        );
        this.units = this.units.filter(unit => String(unit.productId) !== String(product.id));
        if (product.custom) {
          this.customProducts.delete(String(product.id));
          this.productById.delete(String(product.id));
        }
        removedIds.forEach(uid => {
          this.justAddedIds.delete(uid);
          this.releaseEndPanelsForUnit(uid);
        });
        if (this.lastAddedId && removedIds.has(this.lastAddedId)) this.lastAddedId = null;
        if (this.selectedId && removedIds.has(this.selectedId)) this.selectedId = null;
        this.renderAll();
      } else {
        if (product.category === 'plinth') {
          const applied = (this.plinthApplications || []).filter(item =>
            String(item.productId || '') === String(product.id)
          );
          if (applied.length) {
            this.plinthApplications = (this.plinthApplications || []).filter(item =>
              String(item.productId || '') !== String(product.id)
            );
            this.setStatus('Plinth removed from basket and all planned coverage lengths for this colour were cleared.');
          }
        }
        delete this.extras[String(product.id)];
        if (this.endPanelSpec(product.sku)) {
          if (String(this.activeEndPanelProductId || '') === String(product.id)) {
            this.activeEndPanelProductId = null;
          }
          this.endPanelAssignments = (this.endPanelAssignments || []).filter(item =>
            String(item.productId || '') !== String(product.id)
          );
          this.renderUnits();
          this.renderSelection();
          this.sync3D();
        }
        this.renderSummary();
      }

      this.save();
    }

    unitDimensions(unit) {
      const product = this.product(unit);
      if (!product) return { width: 0, depth: 0 };

      const planningDepth = this.fittedRunDepth(product) || Number(product.depth) || 0;
      return unit.rotation === 90
        ? { width: planningDepth, depth: product.width }
        : { width: product.width, depth: planningDepth };
    }

    product(unit) {
      return this.productById.get(String(unit.productId));
    }

    cabinetCollisionPairs() {
      const pairs = [];

      for (let i = 0; i < this.units.length; i += 1) {
        const a = this.units[i];
        const pa = this.product(a);
        if (!pa || pa.planningKind) continue;
        const ar = this.rect(a);

        for (let j = i + 1; j < this.units.length; j += 1) {
          const b = this.units[j];
          const pb = this.product(b);
          if (!pb || pb.planningKind) continue;
          if (!this.sameCollisionLayer(a, b)) continue;
          if (!this.rectsOverlap(ar, this.rect(b))) continue;
          pairs.push({ a, b, productA: pa, productB: pb });
        }
      }

      return pairs;
    }

    hasCollision(testUnit) {
      const a = this.rect(testUnit);
      const testProduct = this.product(testUnit);
      if (!this.rectFitsRoomShape(a)) return true;
      return this.units.some(other => {
        if (other.uid === testUnit.uid) return false;
        const otherProduct = this.product(other);
        if (!testProduct || !otherProduct) return false;
        const b = this.rect(other);
        if (!this.rectsOverlap(a, b)) return false;

        if (testProduct.planningKind || otherProduct.planningKind) {
          if (testProduct.planningKind && otherProduct.planningKind) return false;
          const testY = this.productVerticalRange(testProduct);
          const otherY = this.productVerticalRange(otherProduct);
          return Math.min(testY.top, otherY.top) - Math.max(testY.bottom, otherY.bottom) > 20;
        }

        return this.sameCollisionLayer(testUnit, other);
      });
    }

    sameCollisionLayer(a, b) {
      const pa = this.product(a);
      const pb = this.product(b);
      if (!pa || !pb) return true;
      // Doors, windows, radiators and obstacle zones are fixed-room constraints.
      // They may overlap cabinet geometry so the planner can show the real conflict
      // instead of silently moving the room feature elsewhere.
      if (pa.planningKind || pb.planningKind) return false;

      const aWall = pa.category === 'wall';
      const bWall = pb.category === 'wall';
      const aTall = pa.category === 'tall';
      const bTall = pb.category === 'tall';
      if (aTall || bTall) return true;
      return aWall === bWall;
    }

    isFloorHeightUnit(product) {
      return product &&
        !product.planningKind &&
        ['base', 'drawer', 'sink', 'oven', 'corner', 'tall'].includes(product.category);
    }

    heightWarningsFor(unit) {
      const product = this.product(unit);
      if (!this.isFloorHeightUnit(product) || product?.standalone) return [];

      const a = this.rect(unit);
      const tolerance = Math.max(5, this.grid / 2);
      const warnings = [];

      for (const other of this.units) {
        if (other.uid === unit.uid) continue;
        const otherProduct = this.product(other);
        if (!this.isFloorHeightUnit(otherProduct) || otherProduct?.standalone) continue;

        const currentHeight = Number(product.height) || 0;
        const otherHeight = Number(otherProduct.height) || 0;
        const delta = Math.abs(currentHeight - otherHeight);
        if (delta < 10) continue;

        const b = this.rect(other);
        const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
        const overlapY = Math.min(a.y + a.depth, b.y + b.depth) - Math.max(a.y, b.y);

        // Render every shared seam only once: on the cabinet immediately
        // to the left, or immediately above. This avoids duplicate badges.
        let side = null;
        let direction = null;
        let neighbourPosition = null;

        if (Math.abs((a.x + a.width) - b.x) <= tolerance && overlapY > tolerance) {
          side = 'right';
          direction = '→';
          neighbourPosition = 'right';
        } else if (Math.abs((a.y + a.depth) - b.y) <= tolerance && overlapX > tolerance) {
          side = 'bottom';
          direction = '↓';
          neighbourPosition = 'below';
        } else {
          continue;
        }

        const taller = otherHeight > currentHeight;
        const heightDirection = taller ? '↑' : '↓';
        const relation = taller ? 'taller' : 'lower';

        warnings.push({
          side,
          direction,
          heightDirection,
          delta,
          other,
          title: `Cabinet to the ${neighbourPosition} (${otherProduct.sku}) is ${delta} mm ${relation}`
        });
      }

      return warnings;
    }

    heightMismatchFor(unit) {
      const product = this.product(unit);
      if (!this.isFloorHeightUnit(product) || product?.standalone) return null;
      const a = this.rect(unit);
      const tolerance = Math.max(5, this.grid / 2);

      return this.units.find(other => {
        if (other.uid === unit.uid) return false;
        const otherProduct = this.product(other);
        if (!this.isFloorHeightUnit(otherProduct) || otherProduct?.standalone) return false;
        if (Math.abs(Number(product.height) - Number(otherProduct.height)) < 10) return false;

        const b = this.rect(other);
        const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
        const overlapY = Math.min(a.y + a.depth, b.y + b.depth) - Math.max(a.y, b.y);
        const touchX = Math.abs((a.x + a.width) - b.x) <= tolerance ||
          Math.abs((b.x + b.width) - a.x) <= tolerance;
        const touchY = Math.abs((a.y + a.depth) - b.y) <= tolerance ||
          Math.abs((b.y + b.depth) - a.y) <= tolerance;
        return (touchX && overlapY > tolerance) || (touchY && overlapX > tolerance);
      }) || null;
    }

    floorUnitsBelowWall(unit) {
      const product = this.product(unit);
      if (!product || product.category !== 'wall') return [];
      const a = this.rect(unit);
      return this.units.filter(other => {
        if (other.uid === unit.uid) return false;
        const otherProduct = this.product(other);
        if (!otherProduct || otherProduct.planningKind ||
            otherProduct.category === 'wall' || otherProduct.category === 'tall') return false;
        return this.rectsOverlap(a, this.rect(other));
      });
    }

    wallOverlapsFloor(unit) {
      return this.floorUnitsBelowWall(unit).length > 0;
    }

    rect(unit) {
      const d = this.unitDimensions(unit);
      return { x: unit.x, y: unit.y, width: d.width, depth: d.depth };
    }

    clear() {
      if (this.units.length && !window.confirm('Clear this kitchen design?')) return;
      this.units = [];
      this.extras = {};
      this.endPanelAssignments = [];
      this.plinthApplications = [];
      this.activeEndPanelProductId = null;
      this.customProducts.clear();
      this.productById = new Map(this.catalog.map(item => [String(item.id), item]));
      this.selectedId = null;
      this.justAddedIds.clear();
      this.lastAddedId = null;
      this.designId = this.createDesignId();
      this.renderAll();
      this.save();
      this.setStatus('Design cleared.');
    }

    projectProductSnapshots() {
      const usedIds = new Set(
        this.units
          .map(unit => String(unit.productId))
          .concat(
            Object.entries(this.extras || {})
              .filter(([, qty]) => (Number(qty) || 0) > 0)
              .map(([productId]) => String(productId))
          )
      );

      return [...usedIds].flatMap(id => {
        const product = this.productById.get(id);
        if (!product || product.custom) return [];
        return [{
          id: String(product.id),
          sku: String(product.sku || 'PRODUCT'),
          productTitle: String(product.productTitle || product.sku || 'Kitchen product').slice(0, 160),
          price: Number(product.price) || 0,
          category: String(product.category || 'base'),
          familyLabel: String(product.familyLabel || 'Kitchen unit').slice(0, 80),
          width: Number(product.width) || null,
          depth: Number(product.depth) || null,
          height: Number(product.height) || null,
          placeable: Boolean(product.placeable),
          standalone: Boolean(product.standalone),
          colour: String(product.colour || 'Other').slice(0, 40),
          cornerShape: Boolean(product.cornerShape),
          cornerArmDepth: Number(product.cornerArmDepth) || null
        }];
      });
    }

    designState() {
      return {
        version: 2,
        designId: this.designId,
        projectName: this.projectName || 'My kitchen',
        roomWidth: this.roomWidth,
        roomDepth: this.roomDepth,
        roomShape: { ...this.normaliseRoomShape(this.roomShape) },
        grid: this.grid,
        wallColour: this.wallColour,
        units: this.units.map(unit => ({
          uid: unit.uid,
          productId: String(unit.productId),
          x: Number(unit.x) || 0,
          y: Number(unit.y) || 0,
          rotation: Number(unit.rotation) === 90 ? 90 : 0,
          cornerTurn: ((Number(unit.cornerTurn) || 0) % 4 + 4) % 4,
          doorOpen: Boolean(unit.doorOpen),
          hingeSide: unit.hingeSide === 'right' ? 'right' : 'left',
          locked: Boolean(unit.locked),
          wallLinked: unit.wallLinked !== false,
          frontEdgeOverride: unit.wallLinked === false && ['top', 'right', 'bottom', 'left'].includes(String(unit.frontEdgeOverride || ''))
            ? String(unit.frontEdgeOverride)
            : null,
          serviceGapOverride:
            unit.serviceGapOverride !== null &&
            unit.serviceGapOverride !== undefined &&
            unit.serviceGapOverride !== '' &&
            Number.isFinite(Number(unit.serviceGapOverride))
              ? Number(unit.serviceGapOverride)
              : null
        })),
        extras: { ...this.extras },
        endPanelAssignments: (this.endPanelAssignments || []).map(item => ({
          productId: String(item.productId || ''),
          unitId: String(item.unitId || ''),
          localSide: item.localSide === 'right' ? 'right' : 'left'
        })),
        plinthApplications: (this.plinthApplications || []).map(item => ({
          productId: String(item.productId || ''),
          unitId: String(item.unitId || ''),
          unitIds: Array.isArray(item.unitIds) ? item.unitIds.map(String) : [],
          length: Math.max(100, Math.round(Number(item.length) || 0))
        })),
        // Live Shopify collections can stop returning a sold-out product.
        // Keep a compact product snapshot so saved plans never lose cabinets.
        productSnapshots: this.projectProductSnapshots(),
        customProducts: [...this.customProducts.values()].map(product => ({
          id: String(product.id),
          sku: String(product.sku || 'CUSTOM'),
          productTitle: String(product.productTitle || 'Custom cabinet'),
          variantTitle: '',
          price: 0,
          available: true,
          url: '',
          image: null,
          category: ['base', 'wall', 'tall'].includes(product.category) ? product.category : 'base',
          familyLabel: String(product.familyLabel || 'Custom item'),
          width: Number(product.width) || 600,
          depth: Number(product.depth) || 560,
          height: Number(product.height) || 870,
          placeable: true,
          standalone: false,
          colour: String(product.colour || 'White'),
          custom: true,
          planningKind: ['door', 'window', 'radiator', 'fridge', 'obstacle'].includes(product.planningKind)
            ? product.planningKind
            : '',
          elevation: this.clamp(Number(product.elevation) || 0, 0, 3000)
        })),
        savedAt: new Date().toISOString()
      };
    }

    readProjectLibrary() {
      try {
        const raw = localStorage.getItem(PROJECTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed)
          ? parsed.filter(entry => entry && entry.designId && entry.state && Array.isArray(entry.state.units))
          : [];
      } catch (error) {
        console.warn('JD Kitchen Builder: could not read saved projects', error);
        return [];
      }
    }

    writeProjectLibrary(projects) {
      try {
        localStorage.setItem(PROJECTS_KEY, JSON.stringify((projects || []).slice(0, PROJECT_LIMIT)));
        return true;
      } catch (error) {
        console.warn('JD Kitchen Builder: could not save project library', error);
        return false;
      }
    }

    persistProjectToLibrary(state = null) {
      const snapshot = state || this.designState();
      const designId = String(snapshot.designId || this.designId || '');
      if (!designId) return;

      const savedAt = snapshot.savedAt || new Date().toISOString();
      const entry = {
        designId,
        name: String(snapshot.projectName || this.projectName || 'My kitchen').slice(0, 60),
        savedAt,
        state: snapshot
      };

      const projects = this.readProjectLibrary()
        .filter(item => String(item.designId) !== designId);
      projects.unshift(entry);
      projects.sort((a, b) => String(b.savedAt || '').localeCompare(String(a.savedAt || '')));
      return this.writeProjectLibrary(projects);
    }

    persistState() {
      const state = this.designState();
      let currentSaved = false;

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        currentSaved = true;
      } catch (error) {
        console.warn('JD Kitchen Builder: could not save current design', error);
      }

      const librarySaved = this.persistProjectToLibrary(state);
      return currentSaved && librarySaved;
    }

    snapshotString() {
      const state = this.designState();
      delete state.savedAt;
      return JSON.stringify(state);
    }

    resetHistory() {
      this.historyPast = [this.snapshotString()];
      this.historyFuture = [];
      this.historyReady = true;
      this.updateHistoryButtons();
    }

    recordHistory() {
      if (!this.historyReady || this.historyApplying) return;
      const snapshot = this.snapshotString();
      if (this.historyPast[this.historyPast.length - 1] === snapshot) {
        this.updateHistoryButtons();
        return;
      }

      this.historyPast.push(snapshot);
      if (this.historyPast.length > HISTORY_LIMIT) this.historyPast.shift();
      this.historyFuture = [];
      this.updateHistoryButtons();
    }

    updateHistoryButtons() {
      if (this.el.undo) this.el.undo.disabled = this.historyPast.length <= 1;
      if (this.el.redo) this.el.redo.disabled = this.historyFuture.length === 0;
    }

    undo() {
      if (this.historyPast.length <= 1) return;
      const current = this.historyPast.pop();
      this.historyFuture.push(current);
      const previous = this.historyPast[this.historyPast.length - 1];

      this.historyApplying = true;
      try {
        this.applyDesignState(JSON.parse(previous));
        this.persistState();
        this.renderAll();
      } finally {
        this.historyApplying = false;
      }

      this.updateHistoryButtons();
      this.setStatus('Undone.');
    }

    redo() {
      if (!this.historyFuture.length) return;
      const next = this.historyFuture.pop();
      this.historyPast.push(next);

      this.historyApplying = true;
      try {
        this.applyDesignState(JSON.parse(next));
        this.persistState();
        this.renderAll();
      } finally {
        this.historyApplying = false;
      }

      this.updateHistoryButtons();
      this.setStatus('Redone.');
    }

    applyDesignState(saved) {
      if (!saved || !Array.isArray(saved.units)) return false;

      this.productById = new Map(this.catalog.map(item => [String(item.id), item]));
      this.customProducts = new Map();

      const liveProductBySku = new Map(
        this.catalog.map(product => [String(product.sku || '').trim().toUpperCase(), product])
      );
      const savedProductIdMap = new Map(this.catalogProductIdAliases || []);
      this.stateProductIdRemapCount = 0;

      const productSnapshots = Array.isArray(saved.productSnapshots) ? saved.productSnapshots : [];
      productSnapshots.forEach(raw => {
        const id = String(raw?.id || '');
        if (!id) return;

        if (this.productById.has(id)) {
          savedProductIdMap.set(id, id);
          return;
        }

        const skuKey = String(raw?.sku || '').trim().toUpperCase();
        const liveProduct = skuKey ? liveProductBySku.get(skuKey) : null;
        if (liveProduct) {
          const liveId = String(liveProduct.id);
          savedProductIdMap.set(id, liveId);
          if (liveId !== id) this.stateProductIdRemapCount += 1;
          return;
        }

        const category = [
          'base', 'wall', 'tall', 'drawer', 'sink', 'oven', 'corner',
          'worktop', 'panel', 'plinth', 'front', 'accessory'
        ].includes(String(raw.category))
          ? String(raw.category)
          : 'base';
        const placeable = Boolean(raw.placeable);

        this.productById.set(id, {
          id,
          sku: String(raw.sku || 'SAVED-ITEM'),
          productTitle: String(raw.productTitle || raw.sku || 'Saved kitchen item').slice(0, 160),
          variantTitle: '',
          price: Number(raw.price) || 0,
          available: false,
          url: '',
          image: null,
          category,
          familyLabel: String(raw.familyLabel || 'Saved kitchen item').slice(0, 80),
          width: raw.width == null ? null : this.clamp(Number(raw.width), 100, 3000),
          depth: raw.depth == null ? null : this.clamp(Number(raw.depth), 50, 1500),
          height: raw.height == null ? null : this.clamp(Number(raw.height), 100, 3000),
          placeable,
          standalone: Boolean(raw.standalone),
          colour: String(raw.colour || 'Other').slice(0, 40),
          cornerShape: Boolean(raw.cornerShape),
          cornerArmDepth: Number(raw.cornerArmDepth) || null,
          custom: false,
          savedSnapshot: true
        });
        savedProductIdMap.set(id, id);
      });

      const resolveSavedProductId = rawId => {
        const id = String(rawId || '');
        return savedProductIdMap.get(id) || id;
      };

      const customList = Array.isArray(saved.customProducts) ? saved.customProducts : [];
      customList.forEach(raw => {
        const id = String(raw?.id || '');
        if (!id) return;
        const product = {
          id,
          sku: String(raw.sku || 'CUSTOM'),
          productTitle: String(raw.productTitle || 'Custom cabinet').slice(0, 50),
          variantTitle: '',
          price: 0,
          available: true,
          url: '',
          image: null,
          category: ['base', 'wall', 'tall'].includes(raw.category) ? raw.category : 'base',
          familyLabel: String(raw.familyLabel || 'Custom item'),
          width: this.clamp(Number(raw.width), 100, 3000),
          depth: this.clamp(Number(raw.depth), 100, 1500),
          height: this.clamp(Number(raw.height), 100, 3000),
          placeable: true,
          standalone: false,
          colour: String(raw.colour || 'White'),
          custom: true,
          planningKind: ['door', 'window', 'radiator', 'fridge', 'obstacle'].includes(raw.planningKind)
            ? raw.planningKind
            : '',
          elevation: this.clamp(Number(raw.elevation) || 0, 0, 3000)
        };
        this.customProducts.set(id, product);
        this.productById.set(id, product);
      });

      this.roomWidth = this.clamp(Number(saved.roomWidth) || this.roomWidth, 1000, 15000);
      this.roomDepth = this.clamp(Number(saved.roomDepth) || this.roomDepth, 1000, 15000);
      this.roomShape = this.normaliseRoomShape(saved.roomShape);
      this.grid = this.clamp(Number(saved.grid) || this.grid, 10, 500);
      this.wallColour = ['white', 'soft-grey', 'warm-grey', 'greige', 'sage'].includes(saved.wallColour)
        ? saved.wallColour
        : 'white';

      this.el.widthInput.value = this.roomWidth;
      this.el.depthInput.value = this.roomDepth;
      this.el.wallPicker3d?.querySelectorAll('[data-wall-colour]').forEach(button => {
        button.classList.toggle('is-active', button.dataset.wallColour === this.wallColour);
      });

      this.designId = String(saved.designId || this.createDesignId());
      this.projectName = String(saved.projectName || 'My kitchen').trim().slice(0, 60) || 'My kitchen';

      this.extras = {};
      if (saved.extras && typeof saved.extras === 'object') {
        Object.entries(saved.extras).forEach(([rawProductId, rawQty]) => {
          const productId = resolveSavedProductId(rawProductId);
          const qty = Number(rawQty) || 0;
          if (!productId || qty < 1 || !this.productById.has(productId)) return;
          this.extras[productId] = (Number(this.extras[productId]) || 0) + qty;
        });
      }

      this.units = saved.units
        .map(unit => ({
          raw: unit,
          productId: resolveSavedProductId(unit?.productId)
        }))
        .filter(item => this.productById.has(item.productId))
        .map(({ raw: unit, productId }) => ({
          uid: String(unit.uid || `u-${Math.random().toString(36).slice(2)}`),
          productId,
          x: Number(unit.x) || 0,
          y: Number(unit.y) || 0,
          rotation: Number(unit.rotation) === 90 ? 90 : 0,
          cornerTurn: ((Number(unit.cornerTurn) || 0) % 4 + 4) % 4,
          doorOpen: Boolean(unit.doorOpen),
          hingeSide: unit.hingeSide === 'right' ? 'right' : 'left',
          locked: Boolean(unit.locked),
          wallLinked: unit.wallLinked !== false,
          frontEdgeOverride: unit.wallLinked === false && ['top', 'right', 'bottom', 'left'].includes(String(unit.frontEdgeOverride || ''))
            ? String(unit.frontEdgeOverride)
            : null,
          serviceGapOverride:
            unit.serviceGapOverride !== null &&
            unit.serviceGapOverride !== undefined &&
            unit.serviceGapOverride !== '' &&
            Number.isFinite(Number(unit.serviceGapOverride))
              ? Number(unit.serviceGapOverride)
              : null
        }));

      this.units.forEach(unit => {
        const d = this.unitDimensions(unit);
        unit.x = this.clamp(unit.x, 0, Math.max(0, this.roomWidth - d.width));
        unit.y = this.clamp(unit.y, 0, Math.max(0, this.roomDepth - d.depth));
      });

      const validUnitIds = new Set(this.units.map(unit => String(unit.uid)));
      this.endPanelAssignments = (Array.isArray(saved.endPanelAssignments) ? saved.endPanelAssignments : [])
        .map(item => ({
          productId: resolveSavedProductId(item?.productId),
          unitId: String(item?.unitId || ''),
          localSide: item?.localSide === 'right' ? 'right' : 'left'
        }))
        .filter(item =>
          item.productId &&
          item.unitId &&
          validUnitIds.has(item.unitId) &&
          this.endPanelSpec(this.productById.get(item.productId)?.sku)
        );

      // Never restore more placements than the purchased quantity.
      [...new Set(this.endPanelAssignments.map(item => item.productId))]
        .forEach(productId => this.trimEndPanelAssignmentsForProduct(productId));

      this.plinthApplications = (Array.isArray(saved.plinthApplications) ? saved.plinthApplications : [])
        .map(item => ({
          productId: resolveSavedProductId(item?.productId),
          unitId: String(item?.unitId || ''),
          unitIds: (Array.isArray(item?.unitIds) ? item.unitIds : []).map(String).filter(id => validUnitIds.has(id)),
          length: this.clamp(Math.round(Number(item?.length) || 0), 100, 10000)
        }))
        .filter(item =>
          item.productId &&
          item.unitId &&
          validUnitIds.has(item.unitId) &&
          this.productById.get(item.productId)?.category === 'plinth'
        );

      this.selectedId = null;
      this.justAddedIds.clear();
      this.lastAddedId = null;
      this.heightWarningSignature = '';
      return true;
    }

    migratePlaceableExtras() {
      let migrated = false;

      for (const [productId, qtyRaw] of Object.entries({ ...this.extras })) {
        const product = this.productById.get(String(productId));
        let remaining = Number(qtyRaw) || 0;
        if (!product?.placeable || remaining < 1) continue;

        while (remaining > 0) {
          const unit = {
            uid: `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
            productId: String(product.id),
            x: 0,
            y: 0,
            rotation: 0,
            doorOpen: false,
            hingeSide: 'left',
            locked: false,
            wallLinked: true,
            frontEdgeOverride: null,
            serviceGapOverride: null
          };
          const open = this.findOpenPosition(unit);
          if (!open) break;
          Object.assign(unit, open);
          this.units.push(unit);
          remaining -= 1;
          migrated = true;
        }

        if (remaining > 0) this.extras[String(productId)] = remaining;
        else delete this.extras[String(productId)];
      }

      return migrated;
    }

    openProjects() {
      if (!this.el.projectsModal) return;
      this.persistState();
      if (this.el.projectsName) this.el.projectsName.value = this.projectName || 'My kitchen';
      this.renderProjectLibrary();
      this.el.projectsModal.hidden = false;
      this.el.projectsModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('jkb-projects-open');
      requestAnimationFrame(() => this.el.projectsName?.focus({ preventScroll: true }));
    }

    closeProjects() {
      if (!this.el.projectsModal) return;
      this.el.projectsModal.hidden = true;
      this.el.projectsModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('jkb-projects-open');
    }

    renderProjectLibrary() {
      if (!this.el.projectsList) return;
      const projects = this.readProjectLibrary();

      if (!projects.length) {
        this.el.projectsList.innerHTML = '<p class="jkb__projects-empty">No saved projects on this device yet.</p>';
        return;
      }

      this.el.projectsList.innerHTML = projects.map(entry => {
        const state = entry.state || {};
        const units = Array.isArray(state.units) ? state.units.length : 0;
        const extras = Object.values(state.extras || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
        const totalItems = units + extras;
        let when = '';
        try {
          when = entry.savedAt ? new Date(entry.savedAt).toLocaleString([], {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          }) : '';
        } catch (_) {}

        const current = String(entry.designId) === String(this.designId);
        const name = String(entry.name || state.projectName || 'My kitchen');
        return `
          <article class="jkb__project-card ${current ? 'is-current' : ''}">
            <div class="jkb__project-card-copy">
              <strong>${this.escape(name)}</strong>
              <span>${this.escape(entry.designId)}</span>
              <small>${Number(state.roomWidth) || 0} × ${Number(state.roomDepth) || 0} mm · ${totalItems} item${totalItems === 1 ? '' : 's'}${when ? ` · ${this.escape(when)}` : ''}</small>
            </div>
            <div class="jkb__project-card-actions">
              ${current
                ? '<span class="jkb__project-current">Open now</span>'
                : `<button type="button" data-project-open="${this.escape(entry.designId)}">Open</button>`}
              <button type="button" data-project-copy="${this.escape(entry.designId)}">Copy</button>
              <button type="button" data-project-delete="${this.escape(entry.designId)}" ${current ? 'disabled' : ''}>Delete</button>
            </div>
          </article>
        `;
      }).join('');
    }

    saveCurrentProject() {
      const name = String(this.el.projectsName?.value || this.projectName || 'My kitchen')
        .trim()
        .slice(0, 60);
      this.projectName = name || 'My kitchen';
      this.persistState();
      this.renderAll();
      this.renderProjectLibrary();
      if (this.el.projectsName) this.el.projectsName.value = this.projectName;
      this.setStatus('Project saved on this device.');
    }

    openProject(designId) {
      const entry = this.readProjectLibrary()
        .find(item => String(item.designId) === String(designId));
      if (!entry?.state) return;

      this.applyDesignState(entry.state);
      this.persistState();
      this.resetHistory();
      this.renderAll();
      this.updateShareNotice(false);

      const url = new URL(window.location.href);
      if (url.searchParams.has(SHARE_PARAM)) {
        url.searchParams.delete(SHARE_PARAM);
        window.history.replaceState({}, '', url.toString());
      }

      this.closeProjects();
      this.setStatus(`${this.projectName} opened.`);
    }

    duplicateProject(designId) {
      const entry = this.readProjectLibrary()
        .find(item => String(item.designId) === String(designId));
      if (!entry?.state) return;

      const copy = JSON.parse(JSON.stringify(entry.state));
      copy.designId = this.createDesignId();
      copy.projectName = `${String(entry.name || copy.projectName || 'My kitchen').slice(0, 50)} copy`;
      copy.savedAt = new Date().toISOString();

      this.applyDesignState(copy);
      this.persistState();
      this.resetHistory();
      this.renderAll();
      this.renderProjectLibrary();
      this.closeProjects();
      this.setStatus(`${this.projectName} created.`);
    }
    deleteProject(designId) {
      if (String(designId) === String(this.designId)) {
        this.setStatus('Open another project before deleting this one.');
        return;
      }

      const projects = this.readProjectLibrary();
      const entry = projects.find(item => String(item.designId) === String(designId));
      if (!entry) return;
      if (!window.confirm(`Delete "${entry.name || 'this project'}" from this device?`)) return;

      this.writeProjectLibrary(projects.filter(item => String(item.designId) !== String(designId)));
      this.renderProjectLibrary();
      this.setStatus('Saved project deleted.');
    }

    newProject() {
      this.persistState();

      this.designId = this.createDesignId();
      this.projectName = 'My kitchen';
      this.roomShape = { type: 'rectangle', corner: 'top-right', cutX: 500, cutY: 500 };
      this.units = [];
      this.extras = {};
      this.endPanelAssignments = [];
      this.plinthApplications = [];
      this.customProducts.clear();
      this.productById = new Map(this.catalog.map(item => [String(item.id), item]));
      this.selectedId = null;
      this.justAddedIds.clear();
      this.lastAddedId = null;
      this.heightWarningSignature = '';
      this.activeLayer = 'all';
      this.setLayer('all');
      this.persistState();
      this.resetHistory();
      this.renderAll();
      this.closeProjects();
      this.setStatus('New kitchen project created.');
    }

    save(showMessage = false) {
      const saved = this.persistState();
      this.recordHistory();
      if (showMessage) {
        this.setStatus(saved
          ? 'Project saved on this device.'
          : 'The browser could not save this project. Free some website storage or copy a share link.');
      }
      return saved;
    }

    restore() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const saved = JSON.parse(raw);
        if (!saved || ![1, 2].includes(Number(saved.version || 1)) || !Array.isArray(saved.units)) return false;

        this.applyDesignState(saved);
        const migratedExtras = this.migratePlaceableExtras();
        if (migratedExtras || this.stateProductIdRemapCount > 0) this.persistState();
        return true;
      } catch (error) {
        console.warn('JD Kitchen Builder: could not restore design', error);
        return false;
      }
    }

    compactShareState(state) {
      const wallCodes = ['white', 'soft-grey', 'warm-grey', 'greige', 'sage'];
      const categoryCodes = ['base', 'wall', 'tall'];
      const frontCodes = ['', 'top', 'right', 'bottom', 'left'];

      const shape = this.normaliseRoomShape(state.roomShape);
      const shapeTypes = ['rectangle', 'chamfer', 'notch'];
      const shapeCorners = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];

      return {
        v: 9,
        i: String(state.designId || ''),
        n: String(state.projectName || 'My kitchen').slice(0, 60),
        r: [
          Number(state.roomWidth) || 4000,
          Number(state.roomDepth) || 3000,
          Number(state.grid) || 50,
          Math.max(0, wallCodes.indexOf(state.wallColour))
        ],
        s: [
          Math.max(0, shapeTypes.indexOf(shape.type)),
          Math.max(0, shapeCorners.indexOf(shape.corner)),
          Math.round(Number(shape.cutX) || 0),
          Math.round(Number(shape.cutY) || 0)
        ],
        u: (state.units || []).map(unit => [
          String(unit.uid || ''),
          String(unit.productId || ''),
          Number(unit.x) || 0,
          Number(unit.y) || 0,
          Number(unit.rotation) === 90 ? 1 : 0,
          unit.doorOpen ? 1 : 0,
          unit.hingeSide === 'right' ? 1 : 0,
          unit.locked ? 1 : 0,
          ((Number(unit.cornerTurn) || 0) % 4 + 4) % 4,
          unit.wallLinked === false ? 0 : 1,
          Math.max(0, frontCodes.indexOf(String(unit.frontEdgeOverride || ''))),
          unit.serviceGapOverride !== null &&
          unit.serviceGapOverride !== undefined &&
          unit.serviceGapOverride !== '' &&
          Number.isFinite(Number(unit.serviceGapOverride))
            ? Number(unit.serviceGapOverride)
            : -1
        ]),
        e: Object.entries(state.extras || {}).filter(([, qty]) => Number(qty) > 0),
        a: (state.endPanelAssignments || []).map(item => [
          String(item.productId || ''),
          String(item.unitId || ''),
          item.localSide === 'right' ? 1 : 0
        ]),
        l: (state.plinthApplications || []).map(item => [
          String(item.productId || ''),
          String(item.unitId || ''),
          Math.max(100, Math.round(Number(item.length) || 0))
        ]),
        p: (state.productSnapshots || []).map(product => [
          String(product.id || ''),
          String(product.sku || 'PRODUCT'),
          String(product.productTitle || product.sku || 'Kitchen product').slice(0, 160),
          Number(product.price) || 0,
          String(product.category || 'base'),
          String(product.familyLabel || 'Kitchen unit').slice(0, 80),
          Number(product.width) || 0,
          Number(product.depth) || 0,
          Number(product.height) || 0,
          product.placeable ? 1 : 0,
          product.standalone ? 1 : 0,
          String(product.colour || 'Other').slice(0, 40),
          product.cornerShape ? 1 : 0,
          Number(product.cornerArmDepth) || 0
        ]),
        c: (state.customProducts || []).map(product => [
          String(product.id || ''),
          String(product.sku || 'CUSTOM'),
          String(product.productTitle || 'Custom cabinet'),
          Math.max(0, categoryCodes.indexOf(product.category)),
          String(product.familyLabel || 'Custom item'),
          Number(product.width) || 600,
          Number(product.depth) || 560,
          Number(product.height) || 870,
          String(product.colour || 'White'),
          String(product.planningKind || ''),
          Number(product.elevation) || 0
        ])
      };
    }

    expandShareState(payload) {
      if (!payload || ![3, 4, 5, 6, 7, 8, 9].includes(Number(payload.v)) || !Array.isArray(payload.u)) return payload;

      const wallCodes = ['white', 'soft-grey', 'warm-grey', 'greige', 'sage'];
      const categoryCodes = ['base', 'wall', 'tall'];
      const frontCodes = ['', 'top', 'right', 'bottom', 'left'];
      const shapeTypes = ['rectangle', 'chamfer', 'notch'];
      const shapeCorners = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];
      const room = Array.isArray(payload.r) ? payload.r : [];
      const extras = {};

      (Array.isArray(payload.e) ? payload.e : []).forEach(pair => {
        if (!Array.isArray(pair) || pair.length < 2) return;
        const qty = Number(pair[1]) || 0;
        if (qty > 0) extras[String(pair[0])] = qty;
      });

      return {
        version: 2,
        designId: String(payload.i || this.createDesignId()),
        projectName: String(payload.n || 'My kitchen').slice(0, 60),
        roomWidth: Number(room[0]) || 4000,
        roomDepth: Number(room[1]) || 3000,
        roomShape: Number(payload.v) >= 9 && Array.isArray(payload.s)
          ? {
              type: shapeTypes[Number(payload.s[0])] || 'rectangle',
              corner: shapeCorners[Number(payload.s[1])] || 'top-right',
              cutX: Number(payload.s[2]) || 500,
              cutY: Number(payload.s[3]) || 500
            }
          : { type: 'rectangle', corner: 'top-right', cutX: 500, cutY: 500 },
        grid: Number(room[2]) || 50,
        wallColour: wallCodes[Number(room[3])] || 'white',
        units: payload.u.map(row => ({
          uid: String(row?.[0] || ''),
          productId: String(row?.[1] || ''),
          x: Number(row?.[2]) || 0,
          y: Number(row?.[3]) || 0,
          rotation: Number(row?.[4]) === 1 ? 90 : 0,
          doorOpen: Number(row?.[5]) === 1,
          hingeSide: Number(row?.[6]) === 1 ? 'right' : 'left',
          locked: Number(row?.[7]) === 1,
          cornerTurn: ((Number(row?.[8]) || 0) % 4 + 4) % 4,
          wallLinked: Number(payload.v) < 5 || Number(row?.[9]) !== 0,
          frontEdgeOverride: Number(payload.v) >= 5 ? (frontCodes[Number(row?.[10])] || null) : null,
          serviceGapOverride: Number(payload.v) >= 6 && Number(row?.[11]) >= 0
            ? Number(row?.[11])
            : null
        })),
        extras,
        endPanelAssignments: Number(payload.v) >= 7 && Array.isArray(payload.a)
          ? payload.a.map(row => ({
              productId: String(row?.[0] || ''),
              unitId: String(row?.[1] || ''),
              localSide: Number(row?.[2]) === 1 ? 'right' : 'left'
            }))
          : [],
        plinthApplications: Number(payload.v) >= 8 && Array.isArray(payload.l)
          ? payload.l.map(row => ({
              productId: String(row?.[0] || ''),
              unitId: String(row?.[1] || ''),
              length: Math.max(100, Math.round(Number(row?.[2]) || 0))
            }))
          : [],
        productSnapshots: (Array.isArray(payload.p) ? payload.p : []).map(row => ({
          id: String(row?.[0] || ''),
          sku: String(row?.[1] || 'PRODUCT'),
          productTitle: String(row?.[2] || row?.[1] || 'Kitchen product'),
          price: Number(row?.[3]) || 0,
          category: String(row?.[4] || 'base'),
          familyLabel: String(row?.[5] || 'Kitchen unit'),
          width: Number(row?.[6]) || null,
          depth: Number(row?.[7]) || null,
          height: Number(row?.[8]) || null,
          placeable: Number(row?.[9]) === 1,
          standalone: Number(row?.[10]) === 1,
          colour: String(row?.[11] || 'Other'),
          cornerShape: Number(row?.[12]) === 1,
          cornerArmDepth: Number(row?.[13]) || null
        })),
        customProducts: (Array.isArray(payload.c) ? payload.c : []).map(row => ({
          id: String(row?.[0] || ''),
          sku: String(row?.[1] || 'CUSTOM'),
          productTitle: String(row?.[2] || 'Custom cabinet'),
          variantTitle: '',
          price: 0,
          available: true,
          url: '',
          image: null,
          category: categoryCodes[Number(row?.[3])] || 'base',
          familyLabel: String(row?.[4] || 'Custom item'),
          width: Number(row?.[5]) || 600,
          depth: Number(row?.[6]) || 560,
          height: Number(row?.[7]) || 870,
          placeable: true,
          standalone: false,
          colour: String(row?.[8] || 'White'),
          custom: true,
          planningKind: ['door', 'window', 'radiator', 'fridge', 'obstacle'].includes(String(row?.[9] || ''))
            ? String(row[9])
            : '',
          elevation: Math.max(0, Number(row?.[10]) || 0)
        }))
      };
    }

    encodeShareState(state) {
      const compact = this.compactShareState(state);
      const json = JSON.stringify(compact);
      const bytes = new TextEncoder().encode(json);
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      return btoa(binary)
        .replaceAll('+', '-')
        .replaceAll('/', '_')
        .replace(/=+$/g, '');
    }

    decodeShareState(token) {
      const normalised = String(token || '')
        .replaceAll('-', '+')
        .replaceAll('_', '/');
      const padded = normalised + '='.repeat((4 - normalised.length % 4) % 4);
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      return this.expandShareState(JSON.parse(new TextDecoder().decode(bytes)));
    }

    updateShareNotice(show = false) {
      if (!this.el.shareNotice) return;
      let hasBackup = false;
      try {
        hasBackup = Boolean(localStorage.getItem(SHARE_BACKUP_KEY));
      } catch (error) {
        console.warn('JD Kitchen Builder: shared-project backup is unavailable', error);
      }
      this.el.shareNotice.hidden = !show;
      if (this.el.restoreLocal) this.el.restoreLocal.hidden = !hasBackup;
    }

    restoreSharedDesign() {
      try {
        const url = new URL(window.location.href);
        const token = url.searchParams.get(SHARE_PARAM);
        if (!token) return false;

        const saved = this.decodeShareState(token);
        if (!saved || !Array.isArray(saved.units)) return false;

        const currentRaw = localStorage.getItem(STORAGE_KEY);
        if (currentRaw) {
          let shouldBackup = true;
          try {
            const current = JSON.parse(currentRaw);
            shouldBackup = String(current?.designId || '') !== String(saved.designId || '');
          } catch (_) {}

          if (shouldBackup) {
            try {
              localStorage.setItem(SHARE_BACKUP_KEY, currentRaw);
            } catch (error) {
              // Opening the shared project is still useful even when Safari
              // refuses a backup because website storage is full or blocked.
              console.warn('JD Kitchen Builder: could not back up the local project before opening a share link', error);
            }
          }
        }

        this.applyDesignState(saved);
        return true;
      } catch (error) {
        console.warn('JD Kitchen Builder: could not open shared project', error);
        return false;
      }
    }

    restoreLocalAfterShare() {
      try {
        const raw = localStorage.getItem(SHARE_BACKUP_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.units)) return;

        this.applyDesignState(saved);
        this.persistState();
        localStorage.removeItem(SHARE_BACKUP_KEY);

        const url = new URL(window.location.href);
        url.searchParams.delete(SHARE_PARAM);
        window.history.replaceState({}, '', url.toString());

        this.updateShareNotice(false);
        this.resetHistory();
        this.renderAll();
        this.setStatus('Your saved project is back.');
      } catch (error) {
        console.warn('JD Kitchen Builder: could not restore the previous local project', error);
      }
    }

    shareUrl() {
      const state = this.designState();
      const url = new URL(window.location.href);
      url.searchParams.set(SHARE_PARAM, this.encodeShareState(state));
      url.hash = '';
      return url.toString();
    }

    copyTextFallback(value) {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.left = '-9999px';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.focus();
      field.select();
      field.setSelectionRange(0, field.value.length);
      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      field.remove();
      return copied;
    }

    flashShareButton(label) {
      const button = this.el.shareDesign;
      if (!button) return;
      const original = button.dataset.originalLabel || button.textContent;
      button.dataset.originalLabel = original;
      button.textContent = label;
      window.clearTimeout(this.shareFeedbackTimer);
      this.shareFeedbackTimer = window.setTimeout(() => {
        if (button.isConnected) button.textContent = original;
      }, 1600);
    }

    showShareLink(url) {
      let dialog = this.root.querySelector('[data-share-dialog]');
      if (!dialog) {
        dialog = document.createElement('div');
        dialog.className = 'jkb__share-dialog';
        dialog.setAttribute('data-share-dialog', '');
        dialog.hidden = true;
        dialog.innerHTML = `
          <button type="button" class="jkb__share-dialog-backdrop" data-share-dialog-close aria-label="Close share window"></button>
          <section class="jkb__share-dialog-panel" role="dialog" aria-modal="true" aria-labelledby="jkb-share-title">
            <div class="jkb__share-dialog-heading">
              <strong id="jkb-share-title">Share project</strong>
              <button type="button" data-share-dialog-close aria-label="Close share window">×</button>
            </div>
            <p>Copy this link to open the same kitchen project.</p>
            <div class="jkb__share-dialog-copy">
              <input type="text" readonly data-share-dialog-link aria-label="Project link">
              <button type="button" data-share-dialog-copy>Copy link</button>
            </div>
          </section>
        `;
        this.root.appendChild(dialog);
        dialog.querySelectorAll('[data-share-dialog-close]').forEach(button => {
          button.addEventListener('click', () => {
            dialog.hidden = true;
            dialog.setAttribute('aria-hidden', 'true');
          });
        });
        dialog.querySelector('[data-share-dialog-copy]')?.addEventListener('click', async () => {
          const input = dialog.querySelector('[data-share-dialog-link]');
          const value = input?.value || '';
          let copied = false;
          try {
            if (navigator.clipboard?.writeText) {
              await navigator.clipboard.writeText(value);
              copied = true;
            }
          } catch (error) {
            copied = false;
          }
          copied = copied || this.copyTextFallback(value);
          if (copied) {
            this.setStatus('Project link copied.');
            this.flashShareButton('Copied ✓');
            dialog.hidden = true;
          } else {
            input?.focus();
            input?.select();
          }
        });
      }

      const input = dialog.querySelector('[data-share-dialog-link]');
      if (input) input.value = url;
      dialog.hidden = false;
      dialog.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => {
        input?.focus();
        input?.select();
      });
    }

    async shareDesign() {
      this.save();
      const url = this.shareUrl();
      const shareData = {
        title: this.projectName || 'Kitchen project',
        text: 'Open this kitchen project on JD Greta.',
        url
      };

      if (navigator.share && this.isMobile()) {
        try {
          await navigator.share(shareData);
          this.setStatus('Project shared.');
          this.flashShareButton('Shared ✓');
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
          console.warn('JD Kitchen Builder: native share unavailable, using copy fallback', error);
        }
      }

      let copied = false;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      copied = copied || this.copyTextFallback(url);

      if (copied) {
        this.setStatus('Project link copied.');
        this.flashShareButton('Copied ✓');
        return;
      }

      this.showShareLink(url);
      this.setStatus('Copy the project link from the share window.');
    }

    async addToCart() {
      if (!this.units.length) return;

      const unavailable = this.unavailableBasketProducts();
      const quantityIssues = this.basketQuantityIssues();
      const blockingIssues = this.cabinetCollisionPairs().length + this.roomFeatureConflicts().length;
      if (unavailable.length || quantityIssues.length || blockingIssues) {
        this.updateCheckoutState();
        if (unavailable.length) {
          this.setStatus('Remove unavailable products before adding this kitchen to the basket.');
        } else if (quantityIssues.length) {
          const issue = quantityIssues[0];
          const more = quantityIssues.length > 1
            ? ` Another ${quantityIssues.length - 1} product${quantityIssues.length === 2 ? '' : 's'} also exceed the limit.`
            : '';
          this.setStatus(
            `${issue.product.sku}: the design needs ${issue.quantity}, but Shopify currently permits ${issue.maximum}.${more}`
          );
        } else {
          this.setStatus('Resolve the layout overlaps shown in Smart Fit before checkout.');
        }
        return;
      }

      this.el.addCart.disabled = true;
      this.el.addCart.textContent = 'Adding kitchen…';
      this.save();

      const grouped = new Map();
      for (const unit of this.units) {
        const product = this.product(unit);
        if (!product || product.custom) continue;
        const key = String(product.id);
        if (!grouped.has(key)) grouped.set(key, { id: product.id, quantity: 0 });
        grouped.get(key).quantity += 1;
      }

      for (const [productId, qtyRaw] of Object.entries(this.extras || {})) {
        const qty = Number(qtyRaw) || 0;
        if (qty < 1) continue;
        const product = this.productById.get(String(productId));
        if (!product || product.custom) continue;
        const key = String(product.id);
        if (!grouped.has(key)) grouped.set(key, { id: product.id, quantity: 0 });
        grouped.get(key).quantity += qty;
      }

      if (!grouped.size) {
        this.updateCheckoutState(false);
        this.setStatus('Custom planning items are not sold through the JD Greta basket.');
        return;
      }

      const items = [...grouped.values()].map(line => ({
        id: line.id,
        quantity: line.quantity,
        properties: {
          '_Kitchen design': this.designId
        }
      }));

      try {
        const root = window.Shopify?.routes?.root || '/';
        let pendingItems = items;

        /*
         * Re-opening checkout for the same saved design must be idempotent.
         * Shopify merges matching variant + line-property rows, so posting the
         * complete design twice can hit a variant's maximum-cart quantity.
         */
        const cartResponse = await fetch(root + 'cart.js', {
          headers: { 'Accept': 'application/json' }
        }).catch(() => null);

        if (cartResponse?.ok) {
          const cart = await cartResponse.json().catch(() => null);
          const existingForDesign = new Map();
          const existingInBasket = new Map();

          for (const line of cart?.items || []) {
            const key = String(line.variant_id || line.id || '');
            const lineQuantity = Number(line.quantity) || 0;
            existingInBasket.set(key, (existingInBasket.get(key) || 0) + lineQuantity);

            const lineDesign = line?.properties?.['_Kitchen design'];
            if (String(lineDesign || '') !== String(this.designId)) continue;
            existingForDesign.set(
              key,
              (existingForDesign.get(key) || 0) + lineQuantity
            );
          }

          pendingItems = items
            .map(item => ({
              ...item,
              quantity: Math.max(
                0,
                Number(item.quantity || 0) - (existingForDesign.get(String(item.id)) || 0)
              )
            }))
            .filter(item => item.quantity > 0);

          const cartQuantityIssue = pendingItems.map(item => {
            const product = this.productById.get(String(item.id));
            const maximum = this.productQuantityMaximum(product);
            const alreadyInBasket = existingInBasket.get(String(item.id)) || 0;
            return maximum != null && alreadyInBasket + Number(item.quantity || 0) > maximum
              ? { product, maximum, alreadyInBasket, required: Number(item.quantity || 0) }
              : null;
          }).find(Boolean);

          if (cartQuantityIssue) {
            const sku = cartQuantityIssue.product?.sku || 'This item';
            throw new Error(
              `${sku}: basket already contains ${cartQuantityIssue.alreadyInBasket}; this project needs ${cartQuantityIssue.required} more, but the maximum is ${cartQuantityIssue.maximum}. Remove old items from the basket and try again.`
            );
          }

          if (!pendingItems.length) {
            this.setStatus('This kitchen is already in your basket. Opening basket…');
            window.setTimeout(() => {
              window.location.href = root + 'cart';
            }, 250);
            return;
          }
        }

        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: pendingItems })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          const message = error.description || error.message || 'Could not add the kitchen to the basket.';
          const cartLimit = /maximum quantity|already in your cart|quantity limit/i.test(message);
          throw new Error(cartLimit
            ? 'An item is already at its basket or stock limit. Review existing basket quantities, then try again.'
            : message);
        }

        await fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              '_kitchen_design': this.designId,
              '_kitchen_room': `${this.roomWidth}x${this.roomDepth}mm`,
              '_kitchen_room_shape': this.roomShape.type === 'rectangle'
                ? 'Rectangle'
                : `${this.roomShape.type} ${this.roomShape.corner} ${Math.round(this.roomShape.cutX)}x${Math.round(this.roomShape.cutY)}mm`
            }
          })
        }).catch(() => null);

        window.location.href = root + 'cart';
      } catch (error) {
        console.error(error);
        this.updateCheckoutState();
        this.setStatus(error.message || 'Could not add the kitchen to the basket.');
      }
    }

    observeResize() {
      let frame = null;
      const render = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          this.updateResponsiveMode();
          this.fitRoom();
          this.renderUnits();
          if (this.viewMode === '3d') this.render3DSelection();
        });
      };
      window.addEventListener('resize', render, { passive: true });
      window.visualViewport?.addEventListener('resize', render, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(render);
        this.resizeObserver.observe(this.el.stage);
      }
    }

    money(cents) {
      const currency = window.Shopify?.currency?.active || 'GBP';
      try {
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
      } catch (_) {
        return `£${((Number(cents) || 0) / 100).toFixed(2)}`;
      }
    }

    createDesignId() {
      const d = new Date();
      const stamp = [
        String(d.getFullYear()).slice(-2),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0')
      ].join('');
      return `JD-KP-${stamp}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    }

    roundGrid(value) {
      return Math.round(Number(value || 0) / this.grid) * this.grid;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, Number(value) || 0));
    }

    escape(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    setStatus(message) {
      this.el.status.textContent = message;
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        if (this.el.status.textContent === message) this.el.status.textContent = '';
      }, 4000);
    }
  }

  const boot = scope => {
    (scope || document).querySelectorAll('[data-kitchen-builder]').forEach(root => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      new KitchenBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
